import NonTranslatable from './non-translatable.ts';
import npmApiTranslations from './sk.api.npm.ts';
import pt9800ApiTranslations from './sk.api.pt9800.ts';

import {
  generalTerm,
} from '@/constants/locales/sk.shared.ts';
import {
  TValidateExpressionRouteResponse,
} from '@/generated/qmulus/axios/signal-builder.ts';
import {
  components,
} from '@/generated/qmulus/openapi.ts';
import {
  EAppGroupSemanticDepartment, EAppGroupSemanticTopic, EAppId,
} from '@/types/app-type.ts';
import {
  EAssetProfileCombinator,
} from '@/types/asset-profile-combinator.ts';
import {
  EAssetProfileStatus,
} from '@/types/asset-profile-status.ts';
import {
  EHierarchy,
} from '@/types/asset-tree.ts';
import {
  EAuditResultType,
} from '@/types/audit-result.ts';
import {
  ECalendarEventType,
  ECalendarPeriod,
  EMaintenancePriority,
  EMaintenanceRecurrenceEndCondition,
  EMaintenanceRecurrenceMonthDayDimension,
  EMaintenanceRecurrenceMonthDayValue,
  EMaintenanceState,
} from '@/types/calendar.ts';
import {
  EChamberType, EHeatTreatmentProcessType,
} from '@/types/chamber.ts';
import {
  TFunctionCategory,
  TFunctionDefinition,
  TOperatorDefinition,
} from '@/types/function.ts';
import {
  TJobStatus,
} from '@/types/job-status.ts';
import {
  EBlobMeaning,
  EConsumptionMeaningMap, EJobFeatureMeaningMap,
  EPhysicalPropertyMeaningMap,
} from '@/types/meaning-type.ts';
import {
  EDataFormat,
} from '@/types/metadata.ts';
import {
  EModelLifecycleState,
} from '@/types/model-builder.ts';
import {
  ENotificationChannelType,
} from '@/types/notification-channel-type.ts';
import {
  ESubscriptionTreeLabel,
} from '@/types/notification.ts';
import {
  EObservationType,
} from '@/types/observation-type.ts';
import {
  ETimeseriesMeaning,
  TComponentKeys,
  TComponentValue,
  TComponentsSchema,
} from '@/types/openapi-type.ts';
import {
  TOrderStatus,
} from '@/types/order-status.ts';
import {
  EPhysicalDimension,
} from '@/types/physical-dimension.ts';
import {
  TQmulusDashboardWidgetSharedProps,
  TQmulusDashboardWidgetType,
} from '@/types/qmulus-dashboard.ts';
import {
  TReportBlockId,
} from '@/types/report-block.ts';
import {
  EScaleOption,
} from '@/types/scale.ts';
import {
  ESimulatorAMS275910CompoundLayerClass,
  ESimulatorAMS275910SteelClass,
  ESimulatorAMS275912PorosityClass,
  ESimulatorAMS275912SteelClass,
  ESimulatorPreTreatmentType,
  ESimulatorRecipeSimulationValuesType,
  ESimulatorRecipeType,
  ESimulatorSimulationPartShape,
} from '@/types/simulator.ts';
import {
  EUnitOfMeasurement,
} from '@/types/unit-of-measurement.ts';
import {
  sinkExpression, sinkVariable,
} from '@/utils/expression.ts';

const SparePart: Partial<TComponentKeys<'SparePart'>> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  manufacturerId: generalTerm.manufacturer,
  manufacturerDisplayName: generalTerm.manufacturer,
};

const HeatTreatmentCapability: Partial<TComponentKeys<'HeatTreatmentCapability'>> = {
  heatTreatmentProcess: generalTerm.heatTreatmentProcess,
  preference: generalTerm.preference,
};

const UnitOfMeasurementDisplayName: Record<EUnitOfMeasurement, string> = {
  [EUnitOfMeasurement.AMPERE]: 'Ampér',
  [EUnitOfMeasurement.ATMOSPHERE]: 'Atmosféra',
  [EUnitOfMeasurement.BAR]: 'Bar',
  [EUnitOfMeasurement.BAR_PER_MINUTE]: 'Bar za minútu',
  [EUnitOfMeasurement.BRINELL_HARDNESS]: 'Tvrdosť Brinell',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_1500_10]: 'Tvrdosť Brinell 10/1500/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_1500_15]: 'Tvrdosť Brinell 10/1500/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_3000_10]: 'Tvrdosť Brinell 10/3000/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_3000_15]: 'Tvrdosť Brinell 10/3000/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_500_10]: 'Tvrdosť Brinell 10/500/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_500_15]: 'Tvrdosť Brinell 10/500/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_1500_10]: 'Tvrdosť Brinell 6/1500/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_1500_15]: 'Tvrdosť Brinell 6/1500/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_3000_10]: 'Tvrdosť Brinell 6/3000/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_3000_15]: 'Tvrdosť Brinell 6/3000/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_500_10]: 'Tvrdosť Brinell 6/500/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_500_15]: 'Tvrdosť Brinell 6/500/15',
  [EUnitOfMeasurement.BTU]: 'Britská tepelná jednotka (BTU)',
  [EUnitOfMeasurement.BTU_PER_HOUR]: 'BTU za hodinu',
  [EUnitOfMeasurement.BTU_PER_MINUTE]: 'BTU za minútu',
  [EUnitOfMeasurement.CENTIMETRE_PER_MINUTE]: 'Centimetre za minútu',
  [EUnitOfMeasurement.CENTIMETRE_PER_SECOND]: 'Centimetre za sekundu',
  [EUnitOfMeasurement.CUBIC_CENTIMETRE_PER_MINUTE]: 'Kubický centimeter za minútu',
  [EUnitOfMeasurement.CUBIC_FEET]: 'Kubické stopy',
  [EUnitOfMeasurement.CUBIC_FEET_PER_HOUR]: 'Kubické stopy za hodinu',
  [EUnitOfMeasurement.CUBIC_FEET_PER_MINUTE]: 'Kubické stopy za minútu',
  [EUnitOfMeasurement.CUBIC_METRES]: 'Kubické metre',
  [EUnitOfMeasurement.CUBIC_METRE_PER_HOUR]: 'Kubické metre za hodinu',
  [EUnitOfMeasurement.CUBIC_METRE_PER_MINUTE]: 'Kubické metre za minútu',
  [EUnitOfMeasurement.CUBIC_METRE_PER_SECOND]: 'Kubické metre za sekundu',
  [EUnitOfMeasurement.DAY]: 'Deň',
  [EUnitOfMeasurement.DEGREES_CELSIUS]: 'Stupne Celzia',
  [EUnitOfMeasurement.DEGREES_CELSIUS_PER_MINUTE]: 'Stupne Celzia za minútu',
  [EUnitOfMeasurement.DEGREES_FAHRENHEIT]: 'Stupne Fahrenheita',
  [EUnitOfMeasurement.DEGREES_FAHRENHEIT_PER_MINUTE]: 'Stupne Fahrenheita za minútu',
  [EUnitOfMeasurement.DEGREES_KELVIN]: 'Stupne Kelvina',
  [EUnitOfMeasurement.DELTA_DEGREES_CELSIUS]: 'Delta stupne Celzia',
  [EUnitOfMeasurement.DELTA_DEGREES_FAHRENHEIT]: 'Delta stupne Fahrenheita',
  [EUnitOfMeasurement.DELTA_DEGREES_KELVIN]: 'Delta stupne Kelvina',
  [EUnitOfMeasurement.FEET_OF_WATER_COLUMN]: 'Stopy vodného stĺpca',
  [EUnitOfMeasurement.FEET_PER_MINUTE]: 'Stopy za minútu',
  [EUnitOfMeasurement.FEET_PER_SECOND]: 'Stopy za sekundu',
  [EUnitOfMeasurement.GALLONS_PER_HOUR]: 'Galóny za hodinu',
  [EUnitOfMeasurement.GALLONS_PER_MINUTE]: 'Galóny za minútu',
  [EUnitOfMeasurement.GRAM]: 'Gram',
  [EUnitOfMeasurement.GRAM_PER_SECOND]: 'Gram za sekundu',
  [EUnitOfMeasurement.HECTO_PASCAL]: 'Hektopascal',
  [EUnitOfMeasurement.HERTZ]: 'Hertz',
  [EUnitOfMeasurement.HORSEPOWER]: 'Konská sila',
  [EUnitOfMeasurement.HOUR]: 'Hodina',
  [EUnitOfMeasurement.INCH]: 'Palec',
  [EUnitOfMeasurement.INCH_OF_MERCURY]: 'Palce ortuťového stĺpca',
  [EUnitOfMeasurement.INCH_OF_WATER_COLUMN]: 'Palce vodného stĺpca',
  [EUnitOfMeasurement.INCH_PER_MINUTE]: 'Palce za minútu',
  [EUnitOfMeasurement.INCH_PER_SECOND]: 'Palce za sekundu',
  [EUnitOfMeasurement.KILOGRAM]: 'Kilogram',
  [EUnitOfMeasurement.KILOGRAM_PER_HOUR]: 'Kilogram za hodinu',
  [EUnitOfMeasurement.KILOGRAM_PER_MINUTE]: 'Kilogram za minútu',
  [EUnitOfMeasurement.KILOGRAM_PER_SECOND]: 'Kilogram za sekundu',
  [EUnitOfMeasurement.KILOMETER]: 'Kilometer',
  [EUnitOfMeasurement.KILOVOLT]: 'Kilovolt',
  [EUnitOfMeasurement.KILOVOLT_AMPERE]: 'Kilovoltampér',
  [EUnitOfMeasurement.KILOWATT]: 'Kilowatt',
  [EUnitOfMeasurement.KILOWATT_HOUR]: 'Kilowatthodina',
  [EUnitOfMeasurement.KILO_PASCAL]: 'Kilopascal',
  [EUnitOfMeasurement.KNOOP_HARDNESS]: 'Tvrdosť Knoop',
  [EUnitOfMeasurement.KNOOP_HARDNESS_100]: 'Tvrdosť Knoop 100',
  [EUnitOfMeasurement.KNOOP_HARDNESS_200]: 'Tvrdosť Knoop 200',
  [EUnitOfMeasurement.KNOOP_HARDNESS_500]: 'Tvrdosť Knoop 500',
  [EUnitOfMeasurement.KNOOP_HARDNESS_50]: 'Tvrdosť Knoop 50',
  [EUnitOfMeasurement.LITRE]: 'Liter',
  [EUnitOfMeasurement.LITRE_PER_HOUR]: 'Liter za hodinu',
  [EUnitOfMeasurement.LITRE_PER_MINUTE]: 'Liter za minútu',
  [EUnitOfMeasurement.MEGAWATT]: 'Megawatt',
  [EUnitOfMeasurement.METRES_PER_MINUTE]: 'Metre za minútu',
  [EUnitOfMeasurement.METRES_PER_SECOND]: 'Metre za sekundu',
  [EUnitOfMeasurement.METRE]: 'Meter',
  [EUnitOfMeasurement.MICROBAR]: 'Mikrobar',
  [EUnitOfMeasurement.MICROBAR_PER_MINUTE]: 'Mikrobar za minútu',
  [EUnitOfMeasurement.MICRON]: 'Mikrón',
  [EUnitOfMeasurement.MICRON_HG_PER_HOUR]: 'Mikrón Hg za hodinu',
  [EUnitOfMeasurement.MICRON_HG_PER_MINUTE]: 'Mikrón Hg za minútu',
  [EUnitOfMeasurement.MICRON_OF_MERCURY]: 'Mikrón ortuťového stĺpca',
  [EUnitOfMeasurement.MILLIBAR]: 'Milibar',
  [EUnitOfMeasurement.MILLIBAR_PER_MINUTE]: 'Milibar za minútu',
  [EUnitOfMeasurement.MILLIGRAM]: 'Miligram',
  [EUnitOfMeasurement.MILLILITRE]: 'Mililiter',
  [EUnitOfMeasurement.MILLILITRE_PER_MINUTE]: 'Mililiter za minútu',
  [EUnitOfMeasurement.MILLIMETRE]: 'Milimeter',
  [EUnitOfMeasurement.MILLIMETRE_OF_MERCURY]: 'Milimeter ortuťového stĺpca',
  [EUnitOfMeasurement.MILLIMETRE_OF_WATER_COLUMN]: 'Milimeter vodného stĺpca',
  [EUnitOfMeasurement.MILLIMETRE_PER_SECOND]: 'Milimeter za sekundu',
  [EUnitOfMeasurement.MILLITORR_PER_HOUR]: 'Millitorr za hodinu',
  [EUnitOfMeasurement.MILLIVOLT]: 'Milivolt',
  [EUnitOfMeasurement.MILLI_AMPERE]: 'Miliampér',
  [EUnitOfMeasurement.MINUTE]: 'Minúta',
  [EUnitOfMeasurement.MONTH]: 'Mesiac',
  [EUnitOfMeasurement.NEWTON_PER_SQUARE_MILLIMETRE]: 'Newton na štvorcový milimeter',
  [EUnitOfMeasurement.NORMAL_LITRE_PER_SQUARE_METERS_PER_HOUR]: 'Normálny liter na štvorcový meter za hodinu',
  [EUnitOfMeasurement.NUMBER]: 'Počet/Číslo',
  [EUnitOfMeasurement.PARTS_PER_MILLION]: 'Dielov na milión',
  [EUnitOfMeasurement.PASCAL]: 'Pascal',
  [EUnitOfMeasurement.PERCENT]: 'Percento',
  [EUnitOfMeasurement.PERCENT_CARBON]: 'Percento uhlíka',
  [EUnitOfMeasurement.PERCENT_HYDROGEN]: 'Percento vodíka',
  [EUnitOfMeasurement.PERCENT_NITROGEN]: 'Percento dusíka',
  [EUnitOfMeasurement.POUND]: 'Libra',
  [EUnitOfMeasurement.PSI]: 'PSI',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS]: 'Tvrdosť Rockwell',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_N]: 'Tvrdosť Rockwell 15N',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_T]: 'Tvrdosť Rockwell 15T',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_W]: 'Tvrdosť Rockwell 15W',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_X]: 'Tvrdosť Rockwell 15X',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_Y]: 'Tvrdosť Rockwell 15Y',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_N]: 'Tvrdosť Rockwell 30N',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_T]: 'Tvrdosť Rockwell 30T',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_W]: 'Tvrdosť Rockwell 30W',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_X]: 'Tvrdosť Rockwell 30X',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_Y]: 'Tvrdosť Rockwell 30Y',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_N]: 'Tvrdosť Rockwell 45N',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_T]: 'Tvrdosť Rockwell 45T',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_W]: 'Tvrdosť Rockwell 45W',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_X]: 'Tvrdosť Rockwell 45X',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_Y]: 'Tvrdosť Rockwell 45Y',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_A]: 'Tvrdosť Rockwell A',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_B]: 'Tvrdosť Rockwell B',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_C]: 'Tvrdosť Rockwell C',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_D]: 'Tvrdosť Rockwell D',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_E]: 'Tvrdosť Rockwell E',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_F]: 'Tvrdosť Rockwell F',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_G]: 'Tvrdosť Rockwell G',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_H]: 'Tvrdosť Rockwell H',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_K]: 'Tvrdosť Rockwell K',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_L]: 'Tvrdosť Rockwell L',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_M]: 'Tvrdosť Rockwell M',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_P]: 'Tvrdosť Rockwell P',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_R]: 'Tvrdosť Rockwell R',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_S]: 'Tvrdosť Rockwell S',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_V]: 'Tvrdosť Rockwell V',
  [EUnitOfMeasurement.RPM]: 'Otáčky za minútu (RPM)',
  [EUnitOfMeasurement.SECOND]: 'Sekunda',
  [EUnitOfMeasurement.TONNE]: 'Tona',
  [EUnitOfMeasurement.TORR]: 'Torr',
  [EUnitOfMeasurement.TORR_PER_MINUTE]: 'Torr za minútu',
  [EUnitOfMeasurement.UNIT_LESS]: 'Bez jednotky',
  [EUnitOfMeasurement.THOUSANDTHS_OF_AN_INCH]: 'Tisícina palca',
  [EUnitOfMeasurement.THOUSANDTHS_OF_WATER_COLUMN]: 'Tisícina palca vodného stĺpca',
  [EUnitOfMeasurement.THOUSANDTHS_OF_MERCURY_COLUMN]: 'Tisícina palca ortuťového stĺpca',
  [EUnitOfMeasurement.VICKERS_HARDNESS]: 'Tvrdosť Vickers',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_1]: 'Tvrdosť Vickers 0.1',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_2]: 'Tvrdosť Vickers 0.2',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_3]: 'Tvrdosť Vickers 0.3',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_4]: 'Tvrdosť Vickers 0.4',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_5]: 'Tvrdosť Vickers 0.5',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_6]: 'Tvrdosť Vickers 0.6',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_7]: 'Tvrdosť Vickers 0.7',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_8]: 'Tvrdosť Vickers 0.8',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_9]: 'Tvrdosť Vickers 0.9',
  [EUnitOfMeasurement.VICKERS_HARDNESS_100]: 'Tvrdosť Vickers 100',
  [EUnitOfMeasurement.VICKERS_HARDNESS_10]: 'Tvrdosť Vickers 10',
  [EUnitOfMeasurement.VICKERS_HARDNESS_1]: 'Tvrdosť Vickers 1',
  [EUnitOfMeasurement.VICKERS_HARDNESS_20]: 'Tvrdosť Vickers 20',
  [EUnitOfMeasurement.VICKERS_HARDNESS_2]: 'Tvrdosť Vickers 2',
  [EUnitOfMeasurement.VICKERS_HARDNESS_30]: 'Tvrdosť Vickers 30',
  [EUnitOfMeasurement.VICKERS_HARDNESS_40]: 'Tvrdosť Vickers 40',
  [EUnitOfMeasurement.VICKERS_HARDNESS_50]: 'Tvrdosť Vickers 50',
  [EUnitOfMeasurement.VICKERS_HARDNESS_5]: 'Tvrdosť Vickers 5',
  [EUnitOfMeasurement.VICKERS_HARDNESS_60]: 'Tvrdosť Vickers 60',
  [EUnitOfMeasurement.VICKERS_HARDNESS_70]: 'Tvrdosť Vickers 70',
  [EUnitOfMeasurement.VICKERS_HARDNESS_80]: 'Tvrdosť Vickers 80',
  [EUnitOfMeasurement.VICKERS_HARDNESS_90]: 'Tvrdosť Vickers 90',
  [EUnitOfMeasurement.VOLT]: 'Volt',
  [EUnitOfMeasurement.VOLT_AMPERE]: 'Voltampér',
  [EUnitOfMeasurement.WATT]: 'Watt',
  [EUnitOfMeasurement.WEEK]: 'Týždeň',
  [EUnitOfMeasurement.WEIGHT_PERCENT]: 'Hmotnostné percento',
  [EUnitOfMeasurement.YEAR]: 'Rok',
};

const UnitOfMeasurementSymbol: Record<EUnitOfMeasurement, string> = {
  ...NonTranslatable.UnitOfMeasurementSymbol,
  [EUnitOfMeasurement.MINUTE]: 'Minúta | Minúty',
  [EUnitOfMeasurement.HOUR]: 'Hodina | Hodiny',
  [EUnitOfMeasurement.DAY]: 'Deň | Dni',
  [EUnitOfMeasurement.WEEK]: 'Týždeň | Týždne',
  [EUnitOfMeasurement.MONTH]: 'Mesiac | Mesiace',
  [EUnitOfMeasurement.YEAR]: 'Rok | Roky',
};

const AuditResultType: Record<EAuditResultType, string> = {
  [EAuditResultType.FAILED]: 'Nevyhovujúci',
  [EAuditResultType.INCONCLUSIVE]: 'Nerozhodný',
  [EAuditResultType.PASSED]: 'Vyhovujúci',
};

const PhysicalPropertyMeaning: Record<EPhysicalPropertyMeaningMap, string> = {
  [EPhysicalPropertyMeaningMap.LOAD_MASS]: 'Hmotnosť záťaže',
};

const ConsumptionMeaning: Record<EConsumptionMeaningMap, string> = {
  [EConsumptionMeaningMap.DEPRECATED_AMMONIA_CONSUMPTION]: 'Spotreba amoniaku',
  [EConsumptionMeaningMap.DEPRECATED_AMMONIA_COST]: 'Náklady na amoniak',
  [EConsumptionMeaningMap.DEPRECATED_CARBON_DIOXIDE_CONSUMPTION]: 'Spotreba oxidu uhličitého',
  [EConsumptionMeaningMap.DEPRECATED_CARBON_DIOXIDE_COST]: 'Náklady na oxid uhličitý',
  [EConsumptionMeaningMap.DEPRECATED_CO_GAS_CONSUMPTION]: 'Spotreba plynu CO',
  [EConsumptionMeaningMap.DEPRECATED_CO_GAS_COST]: 'Náklady na plyn CO',
  [EConsumptionMeaningMap.DEPRECATED_DISSOCIATED_AMMONIA_CONSUMPTION]: 'Spotreba disociovaného amoniaku',
  [EConsumptionMeaningMap.DEPRECATED_DISSOCIATED_AMMONIA_COST]: 'Náklady na disociovaný amoniak',
  [EConsumptionMeaningMap.DEPRECATED_ELECTRICITY_CONSUMPTION]: 'Spotreba elektriny',
  [EConsumptionMeaningMap.DEPRECATED_ELECTRICITY_COST]: 'Náklady na elektrinu',
  [EConsumptionMeaningMap.DEPRECATED_ENDO_GAS_CONSUMPTION]: 'Spotreba endo plynu',
  [EConsumptionMeaningMap.DEPRECATED_ENDO_GAS_COST]: 'Náklady na endo plyn',
  [EConsumptionMeaningMap.DEPRECATED_HYDROGEN_CONSUMPTION]: 'Spotreba vodíka',
  [EConsumptionMeaningMap.DEPRECATED_HYDROGEN_COST]: 'Náklady na vodík',
  [EConsumptionMeaningMap.DEPRECATED_NITROGEN_CONSUMPTION]: 'Spotreba dusíka',
  [EConsumptionMeaningMap.DEPRECATED_NITROGEN_COST]: 'Náklady na dusík',
  [EConsumptionMeaningMap.ACETYLENE_C2H2_CONSUMPTION]: 'Spotreba acetylénu (C2H2)',
  [EConsumptionMeaningMap.AIR_CONSUMPTION]: 'Spotreba vzduchu',
  [EConsumptionMeaningMap.AMMONIA_NH3_CONSUMPTION]: 'Spotreba amoniaku (NH3)',
  [EConsumptionMeaningMap.ARGON_CONSUMPTION]: 'Spotreba argónu',
  [EConsumptionMeaningMap.CARBON_DIOXIDE_CO2_CONSUMPTION]: 'Spotreba oxidu uhličitého (CO2)',
  [EConsumptionMeaningMap.CARBON_MONOXIDE_CO_CONSUMPTION]: 'Spotreba oxidu uhoľnatého (CO)',
  [EConsumptionMeaningMap.DISSOCIATED_AMMONIA_NH3_CONSUMPTION]: 'Spotreba disociovaného amoniaku (NH3)',
  [EConsumptionMeaningMap.ENDO_GAS_NATURAL_CONSUMPTION]: 'Spotreba endo plynu (prírodného)',
  [EConsumptionMeaningMap.ENDO_GAS_PROPANE_CONSUMPTION]: 'Spotreba endo plynu (propánového)',
  [EConsumptionMeaningMap.ETHENE_C2H4_CONSUMPTION]: 'Spotreba eténu (C2H4)',
  [EConsumptionMeaningMap.HELIUM_CONSUMPTION]: 'Spotreba hélia',
  [EConsumptionMeaningMap.HYDROGEN_H2_CONSUMPTION]: 'Spotreba vodíka (H2)',
  [EConsumptionMeaningMap.METHANOL_CH3OH_CONSUMPTION]: 'Spotreba metanolu (CH3OH)',
  [EConsumptionMeaningMap.NATURAL_GAS_CONSUMPTION]: 'Spotreba zemného plynu',
  [EConsumptionMeaningMap.NITROGEN_CONSUMPTION]: 'Spotreba dusíka',
  [EConsumptionMeaningMap.NITROUS_OXIDE_N2O_CONSUMPTION]: 'Spotreba oxidu dusného (N2O)',
  [EConsumptionMeaningMap.PROPANE_C3H8_CONSUMPTION]: 'Spotreba propánu (C3H8)',
  [EConsumptionMeaningMap.WATER_CONSUMPTION]: 'Spotreba vody',
  [EConsumptionMeaningMap.ETHANE_C2H6_CONSUMPTION]: 'Spotreba etánu (C2H6)',
  [EConsumptionMeaningMap.METHANE_CH4_CONSUMPTION]: 'Spotreba metánu (CH4)',
  [EConsumptionMeaningMap.ELECTRICAL_POWER_CONSUMPTION]: 'Spotreba elektrickej energie',
  [EConsumptionMeaningMap.USER_DEFINED_001]: 'Spotreba definovaná používateľom 001',
  [EConsumptionMeaningMap.USER_DEFINED_002]: 'Spotreba definovaná používateľom 002',
  [EConsumptionMeaningMap.USER_DEFINED_003]: 'Spotreba definovaná používateľom 003',
  [EConsumptionMeaningMap.USER_DEFINED_004]: 'Spotreba definovaná používateľom 004',
  [EConsumptionMeaningMap.USER_DEFINED_005]: 'Spotreba definovaná používateľom 005',
  [EConsumptionMeaningMap.USER_DEFINED_006]: 'Spotreba definovaná používateľom 006',
  [EConsumptionMeaningMap.USER_DEFINED_007]: 'Spotreba definovaná používateľom 007',
  [EConsumptionMeaningMap.USER_DEFINED_008]: 'Spotreba definovaná používateľom 008',
  [EConsumptionMeaningMap.USER_DEFINED_009]: 'Spotreba definovaná používateľom 009',
  [EConsumptionMeaningMap.USER_DEFINED_010]: 'Spotreba definovaná používateľom 010',
  [EConsumptionMeaningMap.USER_DEFINED_011]: 'Spotreba definovaná používateľom 011',
  [EConsumptionMeaningMap.USER_DEFINED_012]: 'Spotreba definovaná používateľom 012',
  [EConsumptionMeaningMap.USER_DEFINED_013]: 'Spotreba definovaná používateľom 013',
  [EConsumptionMeaningMap.USER_DEFINED_014]: 'Spotreba definovaná používateľom 014',
  [EConsumptionMeaningMap.USER_DEFINED_015]: 'Spotreba definovaná používateľom 015',
  [EConsumptionMeaningMap.USER_DEFINED_016]: 'Spotreba definovaná používateľom 016',
  [EConsumptionMeaningMap.USER_DEFINED_017]: 'Spotreba definovaná používateľom 017',
  [EConsumptionMeaningMap.USER_DEFINED_018]: 'Spotreba definovaná používateľom 018',
  [EConsumptionMeaningMap.USER_DEFINED_019]: 'Spotreba definovaná používateľom 019',
  [EConsumptionMeaningMap.USER_DEFINED_020]: 'Spotreba definovaná používateľom 020',
};

const JobFeatureMeaning: Record<EJobFeatureMeaningMap, string> = {
  [EJobFeatureMeaningMap.HEAT_LOSS_MIN_POWER]: '(ZASTARANÉ) Minimálny výkon tepelných strát',
  [EJobFeatureMeaningMap.HEAT_LOSS_MAX_TEMPERATURE]: '(ZASTARANÉ) Maximálna teplota tepelných strát',
  [EJobFeatureMeaningMap.TEMPERATURE_CONTROL_ACCURACY]: 'Presnosť regulácie teploty',
  [EJobFeatureMeaningMap.TEMPERATURE_CONTROL_PRECISION]: 'Precíznosť regulácie teploty',
  [EJobFeatureMeaningMap.HEATING_POWER_OUTPUT_CONTROL_ACCURACY]: 'Presnosť riadenia výkonu ohrevu',
  [EJobFeatureMeaningMap.HEATING_POWER_OUTPUT_CONTROL_PRECISION]: 'Precíznosť riadenia výkonu ohrevu',
  [EJobFeatureMeaningMap.ELECTRICAL_POWER_CONTROL_ACCURACY]: 'Presnosť riadenia elektrickej energie',
  [EJobFeatureMeaningMap.ELECTRICAL_POWER_CONTROL_PRECISION]: 'Precíznosť riadenia elektrickej energie',
  [EJobFeatureMeaningMap.COOLING_POWER_OUTPUT_CONTROL_ACCURACY]: 'Presnosť riadenia výkonu chladenia',
  [EJobFeatureMeaningMap.COOLING_POWER_OUTPUT_CONTROL_PRECISION]: 'Precíznosť riadenia výkonu chladenia',
  [EJobFeatureMeaningMap.RELATIVE_PRESSURE_CONTROL_ACCURACY]: 'Presnosť riadenia relatívneho tlaku',
  [EJobFeatureMeaningMap.RELATIVE_PRESSURE_CONTROL_PRECISION]: 'Precíznosť riadenia relatívneho tlaku',
  [EJobFeatureMeaningMap.CHAMBER_TEMPERATURE_CONTROL_ACCURACY]: 'Presnosť regulácie teploty komory',
  [EJobFeatureMeaningMap.CHAMBER_TEMPERATURE_CONTROL_PRECISION]: 'Precíznosť regulácie teploty komory',
  [EJobFeatureMeaningMap.ABSOLUTE_PRESSURE_CONTROL_ACCURACY]: 'Presnosť riadenia absolútneho tlaku',
  [EJobFeatureMeaningMap.ABSOLUTE_PRESSURE_CONTROL_PRECISION]: 'Precíznosť riadenia absolútneho tlaku',
  [EJobFeatureMeaningMap.VACUUM_PRESSURE_CONTROL_ACCURACY]: 'Presnosť riadenia podtlaku',
  [EJobFeatureMeaningMap.VACUUM_PRESSURE_CONTROL_PRECISION]: 'Precíznosť riadenia podtlaku',
  [EJobFeatureMeaningMap.ATMOSPHERIC_PRESSURE_CONTROL_ACCURACY]: 'Presnosť riadenia atmosferického tlaku',
  [EJobFeatureMeaningMap.ATMOSPHERIC_PRESSURE_CONTROL_PRECISION]: 'Precíznosť riadenia atmosferického tlaku',
  [EJobFeatureMeaningMap.ROOM_TEMPERATURE_CONTROL_ACCURACY]: 'Presnosť regulácie izbovej teploty',
  [EJobFeatureMeaningMap.ROOM_TEMPERATURE_CONTROL_PRECISION]: 'Precíznosť regulácie izbovej teploty',
  [EJobFeatureMeaningMap.ACTIVITY_AH_CONTROL_ACCURACY]: 'Presnosť riadenia aktivity aH',
  [EJobFeatureMeaningMap.ACTIVITY_AH_CONTROL_PRECISION]: 'Precíznosť riadenia aktivity aH',
  [EJobFeatureMeaningMap.POTENTIAL_HP_CONTROL_ACCURACY]: 'Presnosť riadenia potenciálu HP',
  [EJobFeatureMeaningMap.POTENTIAL_HP_CONTROL_PRECISION]: 'Precíznosť riadenia potenciálu HP',
  [EJobFeatureMeaningMap.POTENTIAL_KH_CONTROL_ACCURACY]: 'Presnosť riadenia potenciálu kH',
  [EJobFeatureMeaningMap.POTENTIAL_KH_CONTROL_PRECISION]: 'Precíznosť riadenia potenciálu kH',
  [EJobFeatureMeaningMap.ACTIVITY_AO_CONTROL_ACCURACY]: 'Presnosť riadenia aktivity aO',
  [EJobFeatureMeaningMap.ACTIVITY_AO_CONTROL_PRECISION]: 'Precíznosť riadenia aktivity aO',
  [EJobFeatureMeaningMap.POTENTIAL_OP_CONTROL_ACCURACY]: 'Presnosť riadenia potenciálu OP',
  [EJobFeatureMeaningMap.POTENTIAL_OP_CONTROL_PRECISION]: 'Precíznosť riadenia potenciálu OP',
  [EJobFeatureMeaningMap.POTENTIAL_KO_CONTROL_ACCURACY]: 'Presnosť riadenia potenciálu kO',
  [EJobFeatureMeaningMap.POTENTIAL_KO_CONTROL_PRECISION]: 'Precíznosť riadenia potenciálu kO',
  [EJobFeatureMeaningMap.ACTIVITY_AC_CONTROL_ACCURACY]: 'Presnosť riadenia aktivity aC',
  [EJobFeatureMeaningMap.ACTIVITY_AC_CONTROL_PRECISION]: 'Precíznosť riadenia aktivity aC',
  [EJobFeatureMeaningMap.POTENTIAL_CP_CONTROL_ACCURACY]: 'Presnosť riadenia potenciálu CP',
  [EJobFeatureMeaningMap.POTENTIAL_CP_CONTROL_PRECISION]: 'Precíznosť riadenia potenciálu CP',
  [EJobFeatureMeaningMap.POTENTIAL_KC_CONTROL_ACCURACY]: 'Presnosť riadenia potenciálu kC',
  [EJobFeatureMeaningMap.POTENTIAL_KC_CONTROL_PRECISION]: 'Precíznosť riadenia potenciálu kC',
  [EJobFeatureMeaningMap.ACTIVITY_AN_CONTROL_ACCURACY]: 'Presnosť riadenia aktivity aN',
  [EJobFeatureMeaningMap.ACTIVITY_AN_CONTROL_PRECISION]: 'Precíznosť riadenia aktivity aN',
  [EJobFeatureMeaningMap.POTENTIAL_NP_CONTROL_ACCURACY]: 'Presnosť riadenia potenciálu NP',
  [EJobFeatureMeaningMap.POTENTIAL_NP_CONTROL_PRECISION]: 'Precíznosť riadenia potenciálu NP',
  [EJobFeatureMeaningMap.POTENTIAL_KN_CONTROL_ACCURACY]: 'Presnosť riadenia potenciálu kN',
  [EJobFeatureMeaningMap.POTENTIAL_KN_CONTROL_PRECISION]: 'Precíznosť riadenia potenciálu kN',
  [EJobFeatureMeaningMap.ACTIVITY_AB_CONTROL_ACCURACY]: 'Presnosť riadenia aktivity aB',
  [EJobFeatureMeaningMap.ACTIVITY_AB_CONTROL_PRECISION]: 'Precíznosť riadenia aktivity aB',
  [EJobFeatureMeaningMap.POTENTIAL_BP_CONTROL_ACCURACY]: 'Presnosť riadenia potenciálu BP',
  [EJobFeatureMeaningMap.POTENTIAL_BP_CONTROL_PRECISION]: 'Precíznosť riadenia potenciálu BP',
  [EJobFeatureMeaningMap.POTENTIAL_KB_CONTROL_ACCURACY]: 'Presnosť riadenia potenciálu kB',
  [EJobFeatureMeaningMap.POTENTIAL_KB_CONTROL_PRECISION]: 'Precíznosť riadenia potenciálu kB',
  [EJobFeatureMeaningMap.ACTIVITY_AS_CONTROL_ACCURACY]: 'Presnosť riadenia aktivity aS',
  [EJobFeatureMeaningMap.ACTIVITY_AS_CONTROL_PRECISION]: 'Precíznosť riadenia aktivity aS',
  [EJobFeatureMeaningMap.POTENTIAL_SP_CONTROL_ACCURACY]: 'Presnosť riadenia potenciálu SP',
  [EJobFeatureMeaningMap.POTENTIAL_SP_CONTROL_PRECISION]: 'Precíznosť riadenia potenciálu SP',
  [EJobFeatureMeaningMap.POTENTIAL_KS_CONTROL_ACCURACY]: 'Presnosť riadenia potenciálu kS',
  [EJobFeatureMeaningMap.POTENTIAL_KS_CONTROL_PRECISION]: 'Precíznosť riadenia potenciálu kS',
  [EJobFeatureMeaningMap.ACETYLENE_C2H2_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku acetylénu C2H2',
  [EJobFeatureMeaningMap.ACETYLENE_C2H2_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku acetylénu C2H2',
  [EJobFeatureMeaningMap.AIR_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku vzduchu',
  [EJobFeatureMeaningMap.AIR_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku vzduchu',
  [EJobFeatureMeaningMap.AMMONIA_NH3_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku amoniaku NH3',
  [EJobFeatureMeaningMap.AMMONIA_NH3_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku amoniaku NH3',
  [EJobFeatureMeaningMap.ARGON_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku argónu',
  [EJobFeatureMeaningMap.ARGON_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku argónu',
  [EJobFeatureMeaningMap.CARBON_DIOXIDE_CO2_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku oxidu uhličitého CO2',
  [EJobFeatureMeaningMap.CARBON_DIOXIDE_CO2_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku oxidu uhličitého CO2',
  [EJobFeatureMeaningMap.CARBON_MONOXIDE_CO_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku oxidu uhoľnatého CO',
  [EJobFeatureMeaningMap.CARBON_MONOXIDE_CO_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku oxidu uhoľnatého CO',
  [EJobFeatureMeaningMap.DISSOCIATED_AMMONIA_NH3_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku disociovaného amoniaku NH3',
  [EJobFeatureMeaningMap.DISSOCIATED_AMMONIA_NH3_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku disociovaného amoniaku NH3',
  [EJobFeatureMeaningMap.ENDO_GAS_NATURAL_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku endo plynu (prírodného)',
  [EJobFeatureMeaningMap.ENDO_GAS_NATURAL_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku endo plynu (prírodného)',
  [EJobFeatureMeaningMap.ENDO_GAS_PROPANE_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku endo plynu (propánového)',
  [EJobFeatureMeaningMap.ENDO_GAS_PROPANE_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku endo plynu (propánového)',
  [EJobFeatureMeaningMap.ETHENE_C2H4_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku eténu C2H4',
  [EJobFeatureMeaningMap.ETHENE_C2H4_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku eténu C2H4',
  [EJobFeatureMeaningMap.HELIUM_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku hélia',
  [EJobFeatureMeaningMap.HELIUM_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku hélia',
  [EJobFeatureMeaningMap.HYDROGEN_H2_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku vodíka H2',
  [EJobFeatureMeaningMap.HYDROGEN_H2_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku vodíka H2',
  [EJobFeatureMeaningMap.METHANOL_CH3OH_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku metanolu CH3OH',
  [EJobFeatureMeaningMap.METHANOL_CH3OH_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku metanolu CH3OH',
  [EJobFeatureMeaningMap.NATURAL_GAS_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku zemného plynu',
  [EJobFeatureMeaningMap.NATURAL_GAS_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku zemného plynu',
  [EJobFeatureMeaningMap.NITROGEN_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku dusíka',
  [EJobFeatureMeaningMap.NITROGEN_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku dusíka',
  [EJobFeatureMeaningMap.PROPANE_C3H8_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku propánu C3H8',
  [EJobFeatureMeaningMap.PROPANE_C3H8_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku propánu C3H8',
  [EJobFeatureMeaningMap.WATER_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku vody',
  [EJobFeatureMeaningMap.WATER_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku vody',
  [EJobFeatureMeaningMap.NITROUS_OXIDE_N2O_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku oxidu dusného N2O',
  [EJobFeatureMeaningMap.NITROUS_OXIDE_N2O_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku oxidu dusného N2O',
  [EJobFeatureMeaningMap.TEMPERATURE_CONTROL_AGILITY]: 'Agilita regulácie teploty',
  [EJobFeatureMeaningMap.HEATING_POWER_OUTPUT_CONTROL_AGILITY]: 'Agilita riadenia výkonu ohrevu',
  [EJobFeatureMeaningMap.ELECTRICAL_POWER_CONTROL_AGILITY]: 'Agilita riadenia elektrickej energie',
  [EJobFeatureMeaningMap.COOLING_POWER_OUTPUT_CONTROL_AGILITY]: 'Agilita riadenia výkonu chladenia',
  [EJobFeatureMeaningMap.RELATIVE_PRESSURE_CONTROL_AGILITY]: 'Agilita riadenia relatívneho tlaku',
  [EJobFeatureMeaningMap.CHAMBER_TEMPERATURE_CONTROL_AGILITY]: 'Agilita regulácie teploty komory',
  [EJobFeatureMeaningMap.ABSOLUTE_PRESSURE_CONTROL_AGILITY]: 'Agilita riadenia absolútneho tlaku',
  [EJobFeatureMeaningMap.VACUUM_PRESSURE_CONTROL_AGILITY]: 'Agilita riadenia podtlaku',
  [EJobFeatureMeaningMap.ATMOSPHERIC_PRESSURE_CONTROL_AGILITY]: 'Agilita riadenia atmosferického tlaku',
  [EJobFeatureMeaningMap.ROOM_TEMPERATURE_CONTROL_AGILITY]: 'Agilita regulácie izbovej teploty',
  [EJobFeatureMeaningMap.ACTIVITY_AH_CONTROL_AGILITY]: 'Agilita riadenia aktivity aH',
  [EJobFeatureMeaningMap.POTENTIAL_HP_CONTROL_AGILITY]: 'Agilita riadenia potenciálu HP',
  [EJobFeatureMeaningMap.POTENTIAL_KH_CONTROL_AGILITY]: 'Agilita riadenia potenciálu kH',
  [EJobFeatureMeaningMap.ACTIVITY_AO_CONTROL_AGILITY]: 'Agilita riadenia aktivity aO',
  [EJobFeatureMeaningMap.POTENTIAL_OP_CONTROL_AGILITY]: 'Agilita riadenia potenciálu OP',
  [EJobFeatureMeaningMap.POTENTIAL_KO_CONTROL_AGILITY]: 'Agilita riadenia potenciálu kO',
  [EJobFeatureMeaningMap.ACTIVITY_AC_CONTROL_AGILITY]: 'Agilita riadenia aktivity aC',
  [EJobFeatureMeaningMap.POTENTIAL_CP_CONTROL_AGILITY]: 'Agilita riadenia potenciálu CP',
  [EJobFeatureMeaningMap.POTENTIAL_KC_CONTROL_AGILITY]: 'Agilita riadenia potenciálu kC',
  [EJobFeatureMeaningMap.ACTIVITY_AN_CONTROL_AGILITY]: 'Agilita riadenia aktivity aN',
  [EJobFeatureMeaningMap.POTENTIAL_NP_CONTROL_AGILITY]: 'Agilita riadenia potenciálu NP',
  [EJobFeatureMeaningMap.POTENTIAL_KN_CONTROL_AGILITY]: 'Agilita riadenia potenciálu kN',
  [EJobFeatureMeaningMap.ACTIVITY_AB_CONTROL_AGILITY]: 'Agilita riadenia aktivity aB',
  [EJobFeatureMeaningMap.POTENTIAL_BP_CONTROL_AGILITY]: 'Agilita riadenia potenciálu BP',
  [EJobFeatureMeaningMap.POTENTIAL_KB_CONTROL_AGILITY]: 'Agilita riadenia potenciálu kB',
  [EJobFeatureMeaningMap.ACTIVITY_AS_CONTROL_AGILITY]: 'Agilita riadenia aktivity aS',
  [EJobFeatureMeaningMap.POTENTIAL_SP_CONTROL_AGILITY]: 'Agilita riadenia potenciálu SP',
  [EJobFeatureMeaningMap.POTENTIAL_KS_CONTROL_AGILITY]: 'Agilita riadenia potenciálu kS',
  [EJobFeatureMeaningMap.ACETYLENE_C2H2_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku acetylénu C2H2',
  [EJobFeatureMeaningMap.AIR_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku vzduchu',
  [EJobFeatureMeaningMap.AMMONIA_NH3_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku amoniaku NH3',
  [EJobFeatureMeaningMap.ARGON_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku argónu',
  [EJobFeatureMeaningMap.CARBON_DIOXIDE_CO2_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku oxidu uhličitého CO2',
  [EJobFeatureMeaningMap.CARBON_MONOXIDE_CO_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku oxidu uhoľnatého CO',
  [EJobFeatureMeaningMap.DISSOCIATED_AMMONIA_NH3_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku disociovaného amoniaku NH3',
  [EJobFeatureMeaningMap.ENDO_GAS_NATURAL_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku endo plynu (prírodného)',
  [EJobFeatureMeaningMap.ENDO_GAS_PROPANE_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku endo plynu (propánového)',
  [EJobFeatureMeaningMap.ETHENE_C2H4_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku eténu C2H4',
  [EJobFeatureMeaningMap.HELIUM_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku hélia',
  [EJobFeatureMeaningMap.HYDROGEN_H2_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku vodíka H2',
  [EJobFeatureMeaningMap.METHANOL_CH3OH_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku metanolu CH3OH',
  [EJobFeatureMeaningMap.NATURAL_GAS_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku zemného plynu',
  [EJobFeatureMeaningMap.NITROGEN_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku dusíka',
  [EJobFeatureMeaningMap.PROPANE_C3H8_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku propánu C3H8',
  [EJobFeatureMeaningMap.WATER_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku vody',
  [EJobFeatureMeaningMap.NITROUS_OXIDE_N2O_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku oxidu dusného N2O',
  [EJobFeatureMeaningMap.OXYGEN_O2_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku kyslíka O2',
  [EJobFeatureMeaningMap.OXYGEN_O2_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku kyslíka O2',
  [EJobFeatureMeaningMap.OXYGEN_O2_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku kyslíka O2',
  [EJobFeatureMeaningMap.METHANE_CH4_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku metánu CH4',
  [EJobFeatureMeaningMap.METHANE_CH4_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku metánu CH4',
  [EJobFeatureMeaningMap.METHANE_CH4_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku metánu CH4',
  [EJobFeatureMeaningMap.DISSOCIATION_CONTROL_ACCURACY]: 'Presnosť riadenia disociácie',
  [EJobFeatureMeaningMap.DISSOCIATION_CONTROL_PRECISION]: 'Precíznosť riadenia disociácie',
  [EJobFeatureMeaningMap.DISSOCIATION_CONTROL_AGILITY]: 'Agilita riadenia disociácie',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku acetylénu C2H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku acetylénu C2H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku acetylénu C2H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku vzduchu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku vzduchu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku vzduchu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku argónu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku argónu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku argónu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku oxidu uhličitého CO2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku oxidu uhličitého CO2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku oxidu uhličitého CO2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku oxidu uhoľnatého CO',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku oxidu uhoľnatého CO',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku oxidu uhoľnatého CO',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku disociovaného amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku disociovaného amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku disociovaného amoniaku NH3',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku endo plynu (prírodného)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku endo plynu (prírodného)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku endo plynu (prírodného)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku endo plynu (propánového)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku endo plynu (propánového)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku endo plynu (propánového)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku eténu C2H4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku eténu C2H4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku eténu C2H4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku hélia',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku hélia',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku hélia',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku vodíka H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku vodíka H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku vodíka H2',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku metanolu CH3OH',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku metanolu CH3OH',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku metanolu CH3OH',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku zemného plynu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku zemného plynu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku zemného plynu',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku dusíka',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku dusíka',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku dusíka',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku oxidu dusného N2O',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku oxidu dusného N2O',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku oxidu dusného N2O',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku propánu C3H8',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku propánu C3H8',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku propánu C3H8',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku vody',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku vody',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku vody',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku acetylénu C2H2 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku acetylénu C2H2 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku acetylénu C2H2 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku vzduchu (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku vzduchu (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku vzduchu (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku amoniaku NH3 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku amoniaku NH3 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku amoniaku NH3 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku argónu (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku argónu (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku argónu (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku oxidu uhličitého CO2 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku oxidu uhličitého CO2 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku oxidu uhličitého CO2 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku oxidu uhoľnatého CO (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku oxidu uhoľnatého CO (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku oxidu uhoľnatého CO (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku disociovaného amoniaku NH3 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku disociovaného amoniaku NH3 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku disociovaného amoniaku NH3 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku endo plynu (prírodného) (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku endo plynu (prírodného) (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku endo plynu (prírodného) (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku endo plynu (propánového) (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku endo plynu (propánového) (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku endo plynu (propánového) (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku eténu C2H4 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku eténu C2H4 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku eténu C2H4 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku hélia (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku hélia (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku hélia (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku vodíka H2 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku vodíka H2 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku vodíka H2 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku metanolu CH3OH (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku metanolu CH3OH (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku metanolu CH3OH (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku zemného plynu (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku zemného plynu (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku zemného plynu (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku dusíka (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku dusíka (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku dusíka (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku oxidu dusného N2O (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku oxidu dusného N2O (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku oxidu dusného N2O (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku propánu C3H8 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku propánu C3H8 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku propánu C3H8 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku vody (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku vody (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku vody (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku kyslíka',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku kyslíka',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku kyslíka',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku uhlíka',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku uhlíka',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku uhlíka',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku bóru',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku bóru',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku bóru',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku síry',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku síry',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku síry',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku kyslíka (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku kyslíka (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku kyslíka (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku uhlíka (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku uhlíka (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku uhlíka (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku bóru (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku bóru (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku bóru (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku síry (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku síry (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku síry (percentuálne)',
  [EJobFeatureMeaningMap.ETHANE_C2H6_FLOW_CONTROL_ACCURACY]: 'Presnosť riadenia prietoku etánu C2H6',
  [EJobFeatureMeaningMap.ETHANE_C2H6_FLOW_CONTROL_AGILITY]: 'Agilita riadenia prietoku etánu C2H6',
  [EJobFeatureMeaningMap.ETHANE_C2H6_FLOW_CONTROL_PRECISION]: 'Precíznosť riadenia prietoku etánu C2H6',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku etánu C2H6',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku etánu C2H6',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku etánu C2H6',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku etánu C2H6 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku etánu C2H6 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku etánu C2H6 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku metánu CH4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku metánu CH4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku metánu CH4',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_CONTROL_ACCURACY]: 'Presnosť riadenia parciálneho tlaku metánu CH4 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_CONTROL_AGILITY]: 'Agilita riadenia parciálneho tlaku metánu CH4 (percentuálne)',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_CONTROL_PRECISION]: 'Precíznosť riadenia parciálneho tlaku metánu CH4 (percentuálne)',
  [EJobFeatureMeaningMap.HEAT_LOSS_AT_500C]: 'Tepelné straty',
  [EJobFeatureMeaningMap.ASSET_DWELLING_TIME]: 'Čas pobytu zariadenia na job',
  [EJobFeatureMeaningMap.HIGH_VACUUM_PRESSURE_CONTROL_ACCURACY]: 'Presnosť riadenia vysokého vákua',
  [EJobFeatureMeaningMap.HIGH_VACUUM_PRESSURE_CONTROL_AGILITY]: 'Agilita riadenia vysokého vákua',
  [EJobFeatureMeaningMap.HIGH_VACUUM_PRESSURE_CONTROL_PRECISION]: 'Precíznosť riadenia vysokého vákua',
  [EJobFeatureMeaningMap.LOW_VACUUM_PRESSURE_CONTROL_ACCURACY]: 'Presnosť riadenia nízkeho vákua',
  [EJobFeatureMeaningMap.LOW_VACUUM_PRESSURE_CONTROL_AGILITY]: 'Agilita riadenia nízkeho vákua',
  [EJobFeatureMeaningMap.LOW_VACUUM_PRESSURE_CONTROL_PRECISION]: 'Precíznosť riadenia nízkeho vákua',
  [EJobFeatureMeaningMap.VACUUM_TIME_TO_CROSS_OVER]: 'Čas vákua na prechod',
  [EJobFeatureMeaningMap.VACUUM_MAX_LEAK_UP_RATE]: 'Maximálna rýchlosť úniku',
  [EJobFeatureMeaningMap.LEAD_TIME_CONTROL_ACCURACY]: 'Presnosť riadenia dodacej doby',
  [EJobFeatureMeaningMap.LEAD_TIME_CONTROL_AGILITY]: 'Agilita riadenia dodacej doby',
  [EJobFeatureMeaningMap.LEAD_TIME_CONTROL_PRECISION]: 'Precíznosť riadenia dodacej doby',
  [EJobFeatureMeaningMap.BELT_SPEED_CONTROL_ACCURACY]: 'Presnosť riadenia rýchlosti pásu',
  [EJobFeatureMeaningMap.BELT_SPEED_CONTROL_AGILITY]: 'Agilita riadenia rýchlosti pásu',
  [EJobFeatureMeaningMap.BELT_SPEED_CONTROL_PRECISION]: 'Precíznosť riadenia rýchlosti pásu',
  [EJobFeatureMeaningMap.LOAD_TEMPERATURE_CONTROL_ACCURACY]: 'Presnosť riadenia teploty záťaže',
  [EJobFeatureMeaningMap.LOAD_TEMPERATURE_CONTROL_AGILITY]: 'Agilita riadenia teploty záťaže',
  [EJobFeatureMeaningMap.LOAD_TEMPERATURE_CONTROL_PRECISION]: 'Precíznosť riadenia teploty záťaže',
  [EJobFeatureMeaningMap.PEARSON_CORRELATION]: 'Pearsonova korelácia',
  [EJobFeatureMeaningMap.SPEARMAN_CORRELATION]: 'Spearmanova korelácia',
  [EJobFeatureMeaningMap.USER_DEFINED_001]: 'Hodnota funkcie jobu definovaná používateľom 001',
  [EJobFeatureMeaningMap.USER_DEFINED_002]: 'Hodnota funkcie jobu definovaná používateľom 002',
  [EJobFeatureMeaningMap.USER_DEFINED_003]: 'Hodnota funkcie jobu definovaná používateľom 003',
  [EJobFeatureMeaningMap.USER_DEFINED_004]: 'Hodnota funkcie jobu definovaná používateľom 004',
  [EJobFeatureMeaningMap.USER_DEFINED_005]: 'Hodnota funkcie jobu definovaná používateľom 005',
  [EJobFeatureMeaningMap.USER_DEFINED_006]: 'Hodnota funkcie jobu definovaná používateľom 006',
  [EJobFeatureMeaningMap.USER_DEFINED_007]: 'Hodnota funkcie jobu definovaná používateľom 007',
  [EJobFeatureMeaningMap.USER_DEFINED_008]: 'Hodnota funkcie jobu definovaná používateľom 008',
  [EJobFeatureMeaningMap.USER_DEFINED_009]: 'Hodnota funkcie jobu definovaná používateľom 009',
  [EJobFeatureMeaningMap.USER_DEFINED_010]: 'Hodnota funkcie jobu definovaná používateľom 010',
  [EJobFeatureMeaningMap.USER_DEFINED_011]: 'Hodnota funkcie jobu definovaná používateľom 011',
  [EJobFeatureMeaningMap.USER_DEFINED_012]: 'Hodnota funkcie jobu definovaná používateľom 012',
  [EJobFeatureMeaningMap.USER_DEFINED_013]: 'Hodnota funkcie jobu definovaná používateľom 013',
  [EJobFeatureMeaningMap.USER_DEFINED_014]: 'Hodnota funkcie jobu definovaná používateľom 014',
  [EJobFeatureMeaningMap.USER_DEFINED_015]: 'Hodnota funkcie jobu definovaná používateľom 015',
  [EJobFeatureMeaningMap.USER_DEFINED_016]: 'Hodnota funkcie jobu definovaná používateľom 016',
  [EJobFeatureMeaningMap.USER_DEFINED_017]: 'Hodnota funkcie jobu definovaná používateľom 017',
  [EJobFeatureMeaningMap.USER_DEFINED_018]: 'Hodnota funkcie jobu definovaná používateľom 018',
  [EJobFeatureMeaningMap.USER_DEFINED_019]: 'Hodnota funkcie jobu definovaná používateľom 019',
  [EJobFeatureMeaningMap.USER_DEFINED_020]: 'Hodnota funkcie jobu definovaná používateľom 020',
  [EJobFeatureMeaningMap.USER_DEFINED_021]: 'Hodnota funkcie jobu definovaná používateľom 021',
  [EJobFeatureMeaningMap.USER_DEFINED_022]: 'Hodnota funkcie jobu definovaná používateľom 022',
  [EJobFeatureMeaningMap.USER_DEFINED_023]: 'Hodnota funkcie jobu definovaná používateľom 023',
  [EJobFeatureMeaningMap.USER_DEFINED_024]: 'Hodnota funkcie jobu definovaná používateľom 024',
  [EJobFeatureMeaningMap.USER_DEFINED_025]: 'Hodnota funkcie jobu definovaná používateľom 025',
  [EJobFeatureMeaningMap.USER_DEFINED_026]: 'Hodnota funkcie jobu definovaná používateľom 026',
  [EJobFeatureMeaningMap.USER_DEFINED_027]: 'Hodnota funkcie jobu definovaná používateľom 027',
  [EJobFeatureMeaningMap.USER_DEFINED_028]: 'Hodnota funkcie jobu definovaná používateľom 028',
  [EJobFeatureMeaningMap.USER_DEFINED_029]: 'Hodnota funkcie jobu definovaná používateľom 029',
  [EJobFeatureMeaningMap.USER_DEFINED_030]: 'Hodnota funkcie jobu definovaná používateľom 030',
  [EJobFeatureMeaningMap.USER_DEFINED_031]: 'Hodnota funkcie jobu definovaná používateľom 031',
  [EJobFeatureMeaningMap.USER_DEFINED_032]: 'Hodnota funkcie jobu definovaná používateľom 032',
  [EJobFeatureMeaningMap.USER_DEFINED_033]: 'Hodnota funkcie jobu definovaná používateľom 033',
  [EJobFeatureMeaningMap.USER_DEFINED_034]: 'Hodnota funkcie jobu definovaná používateľom 034',
  [EJobFeatureMeaningMap.USER_DEFINED_035]: 'Hodnota funkcie jobu definovaná používateľom 035',
  [EJobFeatureMeaningMap.USER_DEFINED_036]: 'Hodnota funkcie jobu definovaná používateľom 036',
  [EJobFeatureMeaningMap.USER_DEFINED_037]: 'Hodnota funkcie jobu definovaná používateľom 037',
  [EJobFeatureMeaningMap.USER_DEFINED_038]: 'Hodnota funkcie jobu definovaná používateľom 038',
  [EJobFeatureMeaningMap.USER_DEFINED_039]: 'Hodnota funkcie jobu definovaná používateľom 039',
  [EJobFeatureMeaningMap.USER_DEFINED_040]: 'Hodnota funkcie jobu definovaná používateľom 040',
  [EJobFeatureMeaningMap.USER_DEFINED_041]: 'Hodnota funkcie jobu definovaná používateľom 041',
  [EJobFeatureMeaningMap.USER_DEFINED_042]: 'Hodnota funkcie jobu definovaná používateľom 042',
  [EJobFeatureMeaningMap.USER_DEFINED_043]: 'Hodnota funkcie jobu definovaná používateľom 043',
  [EJobFeatureMeaningMap.USER_DEFINED_044]: 'Hodnota funkcie jobu definovaná používateľom 044',
  [EJobFeatureMeaningMap.USER_DEFINED_045]: 'Hodnota funkcie jobu definovaná používateľom 045',
  [EJobFeatureMeaningMap.USER_DEFINED_046]: 'Hodnota funkcie jobu definovaná používateľom 046',
  [EJobFeatureMeaningMap.USER_DEFINED_047]: 'Hodnota funkcie jobu definovaná používateľom 047',
  [EJobFeatureMeaningMap.USER_DEFINED_048]: 'Hodnota funkcie jobu definovaná používateľom 048',
  [EJobFeatureMeaningMap.USER_DEFINED_049]: 'Hodnota funkcie jobu definovaná používateľom 049',
  [EJobFeatureMeaningMap.USER_DEFINED_050]: 'Hodnota funkcie jobu definovaná používateľom 050',
  [EJobFeatureMeaningMap.USER_DEFINED_051]: 'Hodnota funkcie jobu definovaná používateľom 051',
  [EJobFeatureMeaningMap.USER_DEFINED_052]: 'Hodnota funkcie jobu definovaná používateľom 052',
  [EJobFeatureMeaningMap.USER_DEFINED_053]: 'Hodnota funkcie jobu definovaná používateľom 053',
  [EJobFeatureMeaningMap.USER_DEFINED_054]: 'Hodnota funkcie jobu definovaná používateľom 054',
  [EJobFeatureMeaningMap.USER_DEFINED_055]: 'Hodnota funkcie jobu definovaná používateľom 055',
  [EJobFeatureMeaningMap.USER_DEFINED_056]: 'Hodnota funkcie jobu definovaná používateľom 056',
  [EJobFeatureMeaningMap.USER_DEFINED_057]: 'Hodnota funkcie jobu definovaná používateľom 057',
  [EJobFeatureMeaningMap.USER_DEFINED_058]: 'Hodnota funkcie jobu definovaná používateľom 058',
  [EJobFeatureMeaningMap.USER_DEFINED_059]: 'Hodnota funkcie jobu definovaná používateľom 059',
  [EJobFeatureMeaningMap.USER_DEFINED_060]: 'Hodnota funkcie jobu definovaná používateľom 060',
  [EJobFeatureMeaningMap.USER_DEFINED_061]: 'Hodnota funkcie jobu definovaná používateľom 061',
  [EJobFeatureMeaningMap.USER_DEFINED_062]: 'Hodnota funkcie jobu definovaná používateľom 062',
  [EJobFeatureMeaningMap.USER_DEFINED_063]: 'Hodnota funkcie jobu definovaná používateľom 063',
  [EJobFeatureMeaningMap.USER_DEFINED_064]: 'Hodnota funkcie jobu definovaná používateľom 064',
  [EJobFeatureMeaningMap.USER_DEFINED_065]: 'Hodnota funkcie jobu definovaná používateľom 065',
  [EJobFeatureMeaningMap.USER_DEFINED_066]: 'Hodnota funkcie jobu definovaná používateľom 066',
  [EJobFeatureMeaningMap.USER_DEFINED_067]: 'Hodnota funkcie jobu definovaná používateľom 067',
  [EJobFeatureMeaningMap.USER_DEFINED_068]: 'Hodnota funkcie jobu definovaná používateľom 068',
  [EJobFeatureMeaningMap.USER_DEFINED_069]: 'Hodnota funkcie jobu definovaná používateľom 069',
  [EJobFeatureMeaningMap.USER_DEFINED_070]: 'Hodnota funkcie jobu definovaná používateľom 070',
  [EJobFeatureMeaningMap.USER_DEFINED_071]: 'Hodnota funkcie jobu definovaná používateľom 071',
  [EJobFeatureMeaningMap.USER_DEFINED_072]: 'Hodnota funkcie jobu definovaná používateľom 072',
  [EJobFeatureMeaningMap.USER_DEFINED_073]: 'Hodnota funkcie jobu definovaná používateľom 073',
  [EJobFeatureMeaningMap.USER_DEFINED_074]: 'Hodnota funkcie jobu definovaná používateľom 074',
  [EJobFeatureMeaningMap.USER_DEFINED_075]: 'Hodnota funkcie jobu definovaná používateľom 075',
  [EJobFeatureMeaningMap.USER_DEFINED_076]: 'Hodnota funkcie jobu definovaná používateľom 076',
  [EJobFeatureMeaningMap.USER_DEFINED_077]: 'Hodnota funkcie jobu definovaná používateľom 077',
  [EJobFeatureMeaningMap.USER_DEFINED_078]: 'Hodnota funkcie jobu definovaná používateľom 078',
  [EJobFeatureMeaningMap.USER_DEFINED_079]: 'Hodnota funkcie jobu definovaná používateľom 079',
  [EJobFeatureMeaningMap.USER_DEFINED_080]: 'Hodnota funkcie jobu definovaná používateľom 080',
};

const TimeseriesMeaning: Record<ETimeseriesMeaning, string> = {
  [ETimeseriesMeaning.TEMPERATURE_SETPOINT]: 'Nastavená hodnota teploty',
  [ETimeseriesMeaning.TEMPERATURE_READING]: 'Odčítanie teploty',
  [ETimeseriesMeaning.HEATING_POWER_OUTPUT_SETPOINT]: 'Nastavená hodnota výkonu ohrevu',
  [ETimeseriesMeaning.HEATING_POWER_OUTPUT_READING]: 'Odčítanie výkonu ohrevu',
  [ETimeseriesMeaning.HEATING_POWER_ENABLED]: 'Výkon ohrevu zapnutý',
  [ETimeseriesMeaning.ELECTRICAL_POWER_SETPOINT]: 'Nastavená hodnota elektrickej energie',
  [ETimeseriesMeaning.ELECTRICAL_POWER_READING]: 'Odčítanie elektrickej energie',
  [ETimeseriesMeaning.TOTAL_HEATING_GAS_SUPPLY_FLOW_READING]: 'Odčítanie celkového prietoku ohrevného plynu',
  [ETimeseriesMeaning.COOLING_POWER_OUTPUT_SETPOINT]: 'Nastavená hodnota výkonu chladenia',
  [ETimeseriesMeaning.COOLING_POWER_OUTPUT_READING]: 'Odčítanie výkonu chladenia',
  [ETimeseriesMeaning.COOLING_POWER_ENABLED]: 'Výkon chladenia zapnutý',
  [ETimeseriesMeaning.TOTAL_COOLING_GAS_SUPPLY_FLOW_READING]: 'Odčítanie celkového prietoku chladiaceho plynu',
  [ETimeseriesMeaning.RELATIVE_PRESSURE_SETPOINT]: 'Nastavená hodnota relatívneho tlaku',
  [ETimeseriesMeaning.RELATIVE_PRESSURE_READING]: 'Odčítanie relatívneho tlaku',
  [ETimeseriesMeaning.CHAMBER_TEMPERATURE_SETPOINT]: 'Nastavená hodnota teploty komory',
  [ETimeseriesMeaning.CHAMBER_TEMPERATURE_READING]: 'Odčítanie teploty komory',
  [ETimeseriesMeaning.ABSOLUTE_PRESSURE_SETPOINT]: 'Nastavená hodnota absolútneho tlaku',
  [ETimeseriesMeaning.ABSOLUTE_PRESSURE_READING]: 'Odčítanie absolútneho tlaku',
  [ETimeseriesMeaning.VACUUM_PRESSURE_SETPOINT]: 'Nastavená hodnota podtlaku',
  [ETimeseriesMeaning.VACUUM_PRESSURE_READING]: 'Odčítanie podtlaku',
  [ETimeseriesMeaning.ATMOSPHERIC_PRESSURE_SETPOINT]: 'Nastavená hodnota atmosferického tlaku',
  [ETimeseriesMeaning.ATMOSPHERIC_PRESSURE_READING]: 'Odčítanie atmosferického tlaku',
  [ETimeseriesMeaning.ROOM_TEMPERATURE_SETPOINT]: 'Nastavená hodnota izbovej teploty',
  [ETimeseriesMeaning.ROOM_TEMPERATURE_READING]: 'Odčítanie izbovej teploty',
  [ETimeseriesMeaning.ACTIVITY_AH_SETPOINT]: 'Nastavená hodnota aktivity aH',
  [ETimeseriesMeaning.ACTIVITY_AH_CALCULATION]: 'Výpočet aktivity aH',
  [ETimeseriesMeaning.POTENTIAL_HP_SETPOINT]: 'Nastavená hodnota potenciálu HP',
  [ETimeseriesMeaning.POTENTIAL_HP_CALCULATION]: 'Výpočet potenciálu HP',
  [ETimeseriesMeaning.POTENTIAL_KH_SETPOINT]: 'Nastavená hodnota potenciálu kH',
  [ETimeseriesMeaning.POTENTIAL_KH_CALCULATION]: 'Výpočet potenciálu kH',
  [ETimeseriesMeaning.ACTIVITY_AO_SETPOINT]: 'Nastavená hodnota aktivity aO',
  [ETimeseriesMeaning.ACTIVITY_AO_CALCULATION]: 'Výpočet aktivity aO',
  [ETimeseriesMeaning.POTENTIAL_OP_SETPOINT]: 'Nastavená hodnota potenciálu OP',
  [ETimeseriesMeaning.POTENTIAL_OP_CALCULATION]: 'Výpočet potenciálu OP',
  [ETimeseriesMeaning.POTENTIAL_KO_SETPOINT]: 'Nastavená hodnota potenciálu kO',
  [ETimeseriesMeaning.POTENTIAL_KO_CALCULATION]: 'Výpočet potenciálu kO',
  [ETimeseriesMeaning.ACTIVITY_AC_SETPOINT]: 'Nastavená hodnota aktivity aC',
  [ETimeseriesMeaning.ACTIVITY_AC_CALCULATION]: 'Výpočet aktivity aC',
  [ETimeseriesMeaning.POTENTIAL_CP_SETPOINT]: 'Nastavená hodnota potenciálu CP',
  [ETimeseriesMeaning.POTENTIAL_CP_CALCULATION]: 'Výpočet potenciálu CP',
  [ETimeseriesMeaning.POTENTIAL_KC_SETPOINT]: 'Nastavená hodnota potenciálu kC',
  [ETimeseriesMeaning.POTENTIAL_KC_CALCULATION]: 'Výpočet potenciálu kC',
  [ETimeseriesMeaning.ACTIVITY_AN_SETPOINT]: 'Nastavená hodnota aktivity aN',
  [ETimeseriesMeaning.ACTIVITY_AN_CALCULATION]: 'Výpočet aktivity aN',
  [ETimeseriesMeaning.POTENTIAL_NP_SETPOINT]: 'Nastavená hodnota potenciálu NP',
  [ETimeseriesMeaning.POTENTIAL_NP_CALCULATION]: 'Výpočet potenciálu NP',
  [ETimeseriesMeaning.POTENTIAL_KN_SETPOINT]: 'Nastavená hodnota potenciálu kN',
  [ETimeseriesMeaning.POTENTIAL_KN_CALCULATION]: 'Výpočet potenciálu kN',
  [ETimeseriesMeaning.ACTIVITY_AB_SETPOINT]: 'Nastavená hodnota aktivity aB',
  [ETimeseriesMeaning.ACTIVITY_AB_CALCULATION]: 'Výpočet aktivity aB',
  [ETimeseriesMeaning.POTENTIAL_BP_SETPOINT]: 'Nastavená hodnota potenciálu BP',
  [ETimeseriesMeaning.POTENTIAL_BP_CALCULATION]: 'Výpočet potenciálu BP',
  [ETimeseriesMeaning.POTENTIAL_KB_SETPOINT]: 'Nastavená hodnota potenciálu kB',
  [ETimeseriesMeaning.POTENTIAL_KB_CALCULATION]: 'Výpočet potenciálu kB',
  [ETimeseriesMeaning.ACTIVITY_AS_SETPOINT]: 'Nastavená hodnota aktivity aS',
  [ETimeseriesMeaning.ACTIVITY_AS_CALCULATION]: 'Výpočet aktivity aS',
  [ETimeseriesMeaning.POTENTIAL_SP_SETPOINT]: 'Nastavená hodnota potenciálu SP',
  [ETimeseriesMeaning.POTENTIAL_SP_CALCULATION]: 'Výpočet potenciálu SP',
  [ETimeseriesMeaning.POTENTIAL_KS_SETPOINT]: 'Nastavená hodnota potenciálu kS',
  [ETimeseriesMeaning.POTENTIAL_KS_CALCULATION]: 'Výpočet potenciálu kS',
  [ETimeseriesMeaning.ACETYLENE_C2H2_FLOW_SETPOINT]: 'Nastavená hodnota prietoku acetylénu C2H2',
  [ETimeseriesMeaning.ACETYLENE_C2H2_FLOW_READING]: 'Odčítanie prietoku acetylénu C2H2',
  [ETimeseriesMeaning.AIR_FLOW_SETPOINT]: 'Nastavená hodnota prietoku vzduchu',
  [ETimeseriesMeaning.AIR_FLOW_READING]: 'Odčítanie prietoku vzduchu',
  [ETimeseriesMeaning.AMMONIA_NH3_FLOW_SETPOINT]: 'Nastavená hodnota prietoku amoniaku NH3',
  [ETimeseriesMeaning.AMMONIA_NH3_FLOW_READING]: 'Odčítanie prietoku amoniaku NH3',
  [ETimeseriesMeaning.ARGON_FLOW_SETPOINT]: 'Nastavená hodnota prietoku argónu',
  [ETimeseriesMeaning.ARGON_FLOW_READING]: 'Odčítanie prietoku argónu',
  [ETimeseriesMeaning.CARBON_DIOXIDE_CO2_FLOW_SETPOINT]: 'Nastavená hodnota prietoku oxidu uhličitého CO2',
  [ETimeseriesMeaning.CARBON_DIOXIDE_CO2_FLOW_READING]: 'Odčítanie prietoku oxidu uhličitého CO2',
  [ETimeseriesMeaning.CARBON_MONOXIDE_CO_FLOW_SETPOINT]: 'Nastavená hodnota prietoku oxidu uhoľnatého CO',
  [ETimeseriesMeaning.CARBON_MONOXIDE_CO_FLOW_READING]: 'Odčítanie prietoku oxidu uhoľnatého CO',
  [ETimeseriesMeaning.DISSOCIATED_AMMONIA_NH3_FLOW_SETPOINT]: 'Nastavená hodnota prietoku disociovaného amoniaku NH3',
  [ETimeseriesMeaning.DISSOCIATED_AMMONIA_NH3_FLOW_READING]: 'Odčítanie prietoku disociovaného amoniaku NH3',
  [ETimeseriesMeaning.ENDO_GAS_NATURAL_FLOW_SETPOINT]: 'Nastavená hodnota prietoku endo plynu (prírodného)',
  [ETimeseriesMeaning.ENDO_GAS_NATURAL_FLOW_READING]: 'Odčítanie prietoku endo plynu (prírodného)',
  [ETimeseriesMeaning.ENDO_GAS_PROPANE_FLOW_SETPOINT]: 'Nastavená hodnota prietoku endo plynu (propánového)',
  [ETimeseriesMeaning.ENDO_GAS_PROPANE_FLOW_READING]: 'Odčítanie prietoku endo plynu (propánového)',
  [ETimeseriesMeaning.ETHENE_C2H4_FLOW_SETPOINT]: 'Nastavená hodnota prietoku eténu C2H4',
  [ETimeseriesMeaning.ETHENE_C2H4_FLOW_READING]: 'Odčítanie prietoku eténu C2H4',
  [ETimeseriesMeaning.HELIUM_FLOW_SETPOINT]: 'Nastavená hodnota prietoku hélia',
  [ETimeseriesMeaning.HELIUM_FLOW_READING]: 'Odčítanie prietoku hélia',
  [ETimeseriesMeaning.HYDROGEN_H2_FLOW_SETPOINT]: 'Nastavená hodnota prietoku vodíka H2',
  [ETimeseriesMeaning.HYDROGEN_H2_FLOW_READING]: 'Odčítanie prietoku vodíka H2',
  [ETimeseriesMeaning.METHANOL_CH3OH_FLOW_SETPOINT]: 'Nastavená hodnota prietoku metanolu CH3OH',
  [ETimeseriesMeaning.METHANOL_CH3OH_FLOW_READING]: 'Odčítanie prietoku metanolu CH3OH',
  [ETimeseriesMeaning.NATURAL_GAS_FLOW_SETPOINT]: 'Nastavená hodnota prietoku zemného plynu',
  [ETimeseriesMeaning.NATURAL_GAS_FLOW_READING]: 'Odčítanie prietoku zemného plynu',
  [ETimeseriesMeaning.NITROGEN_FLOW_SETPOINT]: 'Nastavená hodnota prietoku dusíka',
  [ETimeseriesMeaning.NITROGEN_FLOW_READING]: 'Odčítanie prietoku dusíka',
  [ETimeseriesMeaning.PROPANE_C3H8_FLOW_SETPOINT]: 'Nastavená hodnota prietoku propánu C3H8',
  [ETimeseriesMeaning.PROPANE_C3H8_FLOW_READING]: 'Odčítanie prietoku propánu C3H8',
  [ETimeseriesMeaning.WATER_FLOW_SETPOINT]: 'Nastavená hodnota prietoku vody',
  [ETimeseriesMeaning.WATER_FLOW_READING]: 'Odčítanie prietoku vody',
  [ETimeseriesMeaning.NITROUS_OXIDE_N2O_FLOW_SETPOINT]: 'Nastavená hodnota prietoku oxidu dusného N2O',
  [ETimeseriesMeaning.NITROUS_OXIDE_N2O_FLOW_READING]: 'Odčítanie prietoku oxidu dusného N2O',
  [ETimeseriesMeaning.METHANE_CH4_FLOW_SETPOINT]: 'Nastavená hodnota prietoku metánu CH4',
  [ETimeseriesMeaning.METHANE_CH4_FLOW_READING]: 'Odčítanie prietoku metánu CH4',
  [ETimeseriesMeaning.OXYGEN_O2_FLOW_SETPOINT]: 'Nastavená hodnota prietoku kyslíka O2',
  [ETimeseriesMeaning.OXYGEN_O2_FLOW_READING]: 'Odčítanie prietoku kyslíka O2',
  [ETimeseriesMeaning.DISSOCIATION_SETPOINT]: 'Nastavená hodnota disociácie',
  [ETimeseriesMeaning.DISSOCIATION_READING]: 'Odčítanie disociácie',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ACETYLENE_C2H2_SETPOINT]: 'Nastavená hodnota parciálneho tlaku acetylénu C2H2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ACETYLENE_C2H2_READING]: 'Odčítanie parciálneho tlaku acetylénu C2H2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AIR_SETPOINT]: 'Nastavená hodnota parciálneho tlaku vzduchu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AIR_READING]: 'Odčítanie parciálneho tlaku vzduchu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AMMONIA_NH3_SETPOINT]: 'Nastavená hodnota parciálneho tlaku amoniaku NH3',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AMMONIA_NH3_READING]: 'Odčítanie parciálneho tlaku amoniaku NH3',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ARGON_SETPOINT]: 'Nastavená hodnota parciálneho tlaku argónu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ARGON_READING]: 'Odčítanie parciálneho tlaku argónu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_SETPOINT]: 'Nastavená hodnota parciálneho tlaku oxidu uhličitého CO2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_READING]: 'Odčítanie parciálneho tlaku oxidu uhličitého CO2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_SETPOINT]: 'Nastavená hodnota parciálneho tlaku oxidu uhoľnatého CO',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_READING]: 'Odčítanie parciálneho tlaku oxidu uhoľnatého CO',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_SETPOINT]: 'Nastavená hodnota parciálneho tlaku disociovaného amoniaku NH3',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_READING]: 'Odčítanie parciálneho tlaku disociovaného amoniaku NH3',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_SETPOINT]: 'Nastavená hodnota parciálneho tlaku endo plynu (prírodného)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_READING]: 'Odčítanie parciálneho tlaku endo plynu (prírodného)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_SETPOINT]: 'Nastavená hodnota parciálneho tlaku endo plynu (propánového)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_READING]: 'Odčítanie parciálneho tlaku endo plynu (propánového)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHENE_C2H4_SETPOINT]: 'Nastavená hodnota parciálneho tlaku eténu C2H4',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHENE_C2H4_READING]: 'Odčítanie parciálneho tlaku eténu C2H4',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HELIUM_SETPOINT]: 'Nastavená hodnota parciálneho tlaku hélia',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HELIUM_READING]: 'Odčítanie parciálneho tlaku hélia',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HYDROGEN_H2_SETPOINT]: 'Nastavená hodnota parciálneho tlaku vodíka H2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HYDROGEN_H2_READING]: 'Odčítanie parciálneho tlaku vodíka H2',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANOL_CH3OH_SETPOINT]: 'Nastavená hodnota parciálneho tlaku metanolu CH3OH',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANOL_CH3OH_READING]: 'Odčítanie parciálneho tlaku metanolu CH3OH',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NATURAL_GAS_SETPOINT]: 'Nastavená hodnota parciálneho tlaku zemného plynu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NATURAL_GAS_READING]: 'Odčítanie parciálneho tlaku zemného plynu',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROGEN_SETPOINT]: 'Nastavená hodnota parciálneho tlaku dusíka',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROGEN_READING]: 'Odčítanie parciálneho tlaku dusíka',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_SETPOINT]: 'Nastavená hodnota parciálneho tlaku oxidu dusného N2O',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_READING]: 'Odčítanie parciálneho tlaku oxidu dusného N2O',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_PROPANE_C3H8_SETPOINT]: 'Nastavená hodnota parciálneho tlaku propánu C3H8',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_PROPANE_C3H8_READING]: 'Odčítanie parciálneho tlaku propánu C3H8',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_WATER_SETPOINT]: 'Nastavená hodnota parciálneho tlaku vody',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_WATER_READING]: 'Odčítanie parciálneho tlaku vody',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku acetylénu C2H2 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_READING]: 'Odčítanie parciálneho tlaku acetylénu C2H2 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AIR_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku vzduchu (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AIR_PERCENT_READING]: 'Odčítanie parciálneho tlaku vzduchu (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku amoniaku NH3 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_READING]: 'Odčítanie parciálneho tlaku amoniaku NH3 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ARGON_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku argónu (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ARGON_PERCENT_READING]: 'Odčítanie parciálneho tlaku argónu (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku oxidu uhličitého CO2 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_READING]: 'Odčítanie parciálneho tlaku oxidu uhličitého CO2 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku oxidu uhoľnatého CO (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_READING]: 'Odčítanie parciálneho tlaku oxidu uhoľnatého CO (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku disociovaného amoniaku NH3 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_READING]: 'Odčítanie parciálneho tlaku disociovaného amoniaku NH3 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku endo plynu (prírodného, percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_READING]: 'Odčítanie parciálneho tlaku endo plynu (prírodného, percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku endo plynu (propánového, percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_READING]: 'Odčítanie parciálneho tlaku endo plynu (propánového, percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku eténu C2H4 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_READING]: 'Odčítanie parciálneho tlaku eténu C2H4 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HELIUM_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku hélia (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HELIUM_PERCENT_READING]: 'Odčítanie parciálneho tlaku hélia (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku vodíka H2 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_READING]: 'Odčítanie parciálneho tlaku vodíka H2 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku metanolu CH3OH (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_READING]: 'Odčítanie parciálneho tlaku metanolu CH3OH (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku zemného plynu (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_READING]: 'Odčítanie parciálneho tlaku zemného plynu (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROGEN_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku dusíka (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROGEN_PERCENT_READING]: 'Odčítanie parciálneho tlaku dusíka (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku oxidu dusného N2O (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_READING]: 'Odčítanie parciálneho tlaku oxidu dusného N2O (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku propánu C3H8 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_READING]: 'Odčítanie parciálneho tlaku propánu C3H8 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_WATER_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku vody (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_WATER_PERCENT_READING]: 'Odčítanie parciálneho tlaku vody (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_OXYGEN_SETPOINT]: 'Nastavená hodnota parciálneho tlaku kyslíka',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_OXYGEN_READING]: 'Odčítanie parciálneho tlaku kyslíka',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_SETPOINT]: 'Nastavená hodnota parciálneho tlaku uhlíka',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_READING]: 'Odčítanie parciálneho tlaku uhlíka',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_BORON_SETPOINT]: 'Nastavená hodnota parciálneho tlaku bóru',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_BORON_READING]: 'Odčítanie parciálneho tlaku bóru',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_SULFUR_SETPOINT]: 'Nastavená hodnota parciálneho tlaku síry',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_SULFUR_READING]: 'Odčítanie parciálneho tlaku síry',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_OXYGEN_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku kyslíka (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_OXYGEN_PERCENT_READING]: 'Odčítanie parciálneho tlaku kyslíka (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku uhlíka (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_PERCENT_READING]: 'Odčítanie parciálneho tlaku uhlíka (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_BORON_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku bóru (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_BORON_PERCENT_READING]: 'Odčítanie parciálneho tlaku bóru (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_SULFUR_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku síry (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_SULFUR_PERCENT_READING]: 'Odčítanie parciálneho tlaku síry (percentuálne)',
  [ETimeseriesMeaning.ETHANE_C2H6_FLOW_SETPOINT]: 'Nastavená hodnota prietoku etánu C2H6',
  [ETimeseriesMeaning.ETHANE_C2H6_FLOW_READING]: 'Odčítanie prietoku etánu C2H6',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHANE_C2H6_SETPOINT]: 'Nastavená hodnota parciálneho tlaku etánu C2H6',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHANE_C2H6_READING]: 'Odčítanie parciálneho tlaku etánu C2H6',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku etánu C2H6 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_READING]: 'Odčítanie parciálneho tlaku etánu C2H6 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANE_CH4_SETPOINT]: 'Nastavená hodnota parciálneho tlaku metánu CH4',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANE_CH4_READING]: 'Odčítanie parciálneho tlaku metánu CH4',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_SETPOINT]: 'Nastavená hodnota parciálneho tlaku metánu CH4 (percentuálne)',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_READING]: 'Odčítanie parciálneho tlaku metánu CH4 (percentuálne)',
  [ETimeseriesMeaning.HIGH_VACUUM_PRESSURE_SETPOINT]: 'Nastavená hodnota vysokého vákua',
  [ETimeseriesMeaning.HIGH_VACUUM_PRESSURE_READING]: 'Odčítanie vysokého vákua',
  [ETimeseriesMeaning.LOW_VACUUM_PRESSURE_SETPOINT]: 'Nastavená hodnota nízkeho vákua',
  [ETimeseriesMeaning.LOW_VACUUM_PRESSURE_READING]: 'Odčítanie nízkeho vákua',
  [ETimeseriesMeaning.LEAD_TIME_SETPOINT]: 'Nastavená hodnota dodacej doby',
  [ETimeseriesMeaning.LEAD_TIME_READING]: 'Odčítanie dodacej doby',
  [ETimeseriesMeaning.BELT_SPEED_SETPOINT]: 'Nastavená hodnota rýchlosti pásu',
  [ETimeseriesMeaning.BELT_SPEED_READING]: 'Odčítanie rýchlosti pásu',
  [ETimeseriesMeaning.LOAD_TEMPERATURE_SETPOINT]: 'Nastavená hodnota teploty záťaže',
  [ETimeseriesMeaning.LOAD_TEMPERATURE_READING]: 'Odčítanie teploty záťaže',
  [ETimeseriesMeaning.USER_DEFINED_001]: 'Časový rad definovaný používateľom 001',
  [ETimeseriesMeaning.USER_DEFINED_002]: 'Časový rad definovaný používateľom 002',
  [ETimeseriesMeaning.USER_DEFINED_003]: 'Časový rad definovaný používateľom 003',
  [ETimeseriesMeaning.USER_DEFINED_004]: 'Časový rad definovaný používateľom 004',
  [ETimeseriesMeaning.USER_DEFINED_005]: 'Časový rad definovaný používateľom 005',
  [ETimeseriesMeaning.USER_DEFINED_006]: 'Časový rad definovaný používateľom 006',
  [ETimeseriesMeaning.USER_DEFINED_007]: 'Časový rad definovaný používateľom 007',
  [ETimeseriesMeaning.USER_DEFINED_008]: 'Časový rad definovaný používateľom 008',
  [ETimeseriesMeaning.USER_DEFINED_009]: 'Časový rad definovaný používateľom 009',
  [ETimeseriesMeaning.USER_DEFINED_010]: 'Časový rad definovaný používateľom 010',
  [ETimeseriesMeaning.USER_DEFINED_011]: 'Časový rad definovaný používateľom 011',
  [ETimeseriesMeaning.USER_DEFINED_012]: 'Časový rad definovaný používateľom 012',
  [ETimeseriesMeaning.USER_DEFINED_013]: 'Časový rad definovaný používateľom 013',
  [ETimeseriesMeaning.USER_DEFINED_014]: 'Časový rad definovaný používateľom 014',
  [ETimeseriesMeaning.USER_DEFINED_015]: 'Časový rad definovaný používateľom 015',
  [ETimeseriesMeaning.USER_DEFINED_016]: 'Časový rad definovaný používateľom 016',
  [ETimeseriesMeaning.USER_DEFINED_017]: 'Časový rad definovaný používateľom 017',
  [ETimeseriesMeaning.USER_DEFINED_018]: 'Časový rad definovaný používateľom 018',
  [ETimeseriesMeaning.USER_DEFINED_019]: 'Časový rad definovaný používateľom 019',
  [ETimeseriesMeaning.USER_DEFINED_020]: 'Časový rad definovaný používateľom 020',
  [ETimeseriesMeaning.USER_DEFINED_021]: 'Časový rad definovaný používateľom 021',
  [ETimeseriesMeaning.USER_DEFINED_022]: 'Časový rad definovaný používateľom 022',
  [ETimeseriesMeaning.USER_DEFINED_023]: 'Časový rad definovaný používateľom 023',
  [ETimeseriesMeaning.USER_DEFINED_024]: 'Časový rad definovaný používateľom 024',
  [ETimeseriesMeaning.USER_DEFINED_025]: 'Časový rad definovaný používateľom 025',
  [ETimeseriesMeaning.USER_DEFINED_026]: 'Časový rad definovaný používateľom 026',
  [ETimeseriesMeaning.USER_DEFINED_027]: 'Časový rad definovaný používateľom 027',
  [ETimeseriesMeaning.USER_DEFINED_028]: 'Časový rad definovaný používateľom 028',
  [ETimeseriesMeaning.USER_DEFINED_029]: 'Časový rad definovaný používateľom 029',
  [ETimeseriesMeaning.USER_DEFINED_030]: 'Časový rad definovaný používateľom 030',
  [ETimeseriesMeaning.USER_DEFINED_031]: 'Časový rad definovaný používateľom 031',
  [ETimeseriesMeaning.USER_DEFINED_032]: 'Časový rad definovaný používateľom 032',
  [ETimeseriesMeaning.USER_DEFINED_033]: 'Časový rad definovaný používateľom 033',
  [ETimeseriesMeaning.USER_DEFINED_034]: 'Časový rad definovaný používateľom 034',
  [ETimeseriesMeaning.USER_DEFINED_035]: 'Časový rad definovaný používateľom 035',
  [ETimeseriesMeaning.USER_DEFINED_036]: 'Časový rad definovaný používateľom 036',
  [ETimeseriesMeaning.USER_DEFINED_037]: 'Časový rad definovaný používateľom 037',
  [ETimeseriesMeaning.USER_DEFINED_038]: 'Časový rad definovaný používateľom 038',
  [ETimeseriesMeaning.USER_DEFINED_039]: 'Časový rad definovaný používateľom 039',
  [ETimeseriesMeaning.USER_DEFINED_040]: 'Časový rad definovaný používateľom 040',
  [ETimeseriesMeaning.USER_DEFINED_041]: 'Časový rad definovaný používateľom 041',
  [ETimeseriesMeaning.USER_DEFINED_042]: 'Časový rad definovaný používateľom 042',
  [ETimeseriesMeaning.USER_DEFINED_043]: 'Časový rad definovaný používateľom 043',
  [ETimeseriesMeaning.USER_DEFINED_044]: 'Časový rad definovaný používateľom 044',
  [ETimeseriesMeaning.USER_DEFINED_045]: 'Časový rad definovaný používateľom 045',
  [ETimeseriesMeaning.USER_DEFINED_046]: 'Časový rad definovaný používateľom 046',
  [ETimeseriesMeaning.USER_DEFINED_047]: 'Časový rad definovaný používateľom 047',
  [ETimeseriesMeaning.USER_DEFINED_048]: 'Časový rad definovaný používateľom 048',
  [ETimeseriesMeaning.USER_DEFINED_049]: 'Časový rad definovaný používateľom 049',
  [ETimeseriesMeaning.USER_DEFINED_050]: 'Časový rad definovaný používateľom 050',
  [ETimeseriesMeaning.USER_DEFINED_051]: 'Časový rad definovaný používateľom 051',
  [ETimeseriesMeaning.USER_DEFINED_052]: 'Časový rad definovaný používateľom 052',
  [ETimeseriesMeaning.USER_DEFINED_053]: 'Časový rad definovaný používateľom 053',
  [ETimeseriesMeaning.USER_DEFINED_054]: 'Časový rad definovaný používateľom 054',
  [ETimeseriesMeaning.USER_DEFINED_055]: 'Časový rad definovaný používateľom 055',
  [ETimeseriesMeaning.USER_DEFINED_056]: 'Časový rad definovaný používateľom 056',
  [ETimeseriesMeaning.USER_DEFINED_057]: 'Časový rad definovaný používateľom 057',
  [ETimeseriesMeaning.USER_DEFINED_058]: 'Časový rad definovaný používateľom 058',
  [ETimeseriesMeaning.USER_DEFINED_059]: 'Časový rad definovaný používateľom 059',
  [ETimeseriesMeaning.USER_DEFINED_060]: 'Časový rad definovaný používateľom 060',
  [ETimeseriesMeaning.USER_DEFINED_061]: 'Časový rad definovaný používateľom 061',
  [ETimeseriesMeaning.USER_DEFINED_062]: 'Časový rad definovaný používateľom 062',
  [ETimeseriesMeaning.USER_DEFINED_063]: 'Časový rad definovaný používateľom 063',
  [ETimeseriesMeaning.USER_DEFINED_064]: 'Časový rad definovaný používateľom 064',
  [ETimeseriesMeaning.USER_DEFINED_065]: 'Časový rad definovaný používateľom 065',
  [ETimeseriesMeaning.USER_DEFINED_066]: 'Časový rad definovaný používateľom 066',
  [ETimeseriesMeaning.USER_DEFINED_067]: 'Časový rad definovaný používateľom 067',
  [ETimeseriesMeaning.USER_DEFINED_068]: 'Časový rad definovaný používateľom 068',
  [ETimeseriesMeaning.USER_DEFINED_069]: 'Časový rad definovaný používateľom 069',
  [ETimeseriesMeaning.USER_DEFINED_070]: 'Časový rad definovaný používateľom 070',
  [ETimeseriesMeaning.USER_DEFINED_071]: 'Časový rad definovaný používateľom 071',
  [ETimeseriesMeaning.USER_DEFINED_072]: 'Časový rad definovaný používateľom 072',
  [ETimeseriesMeaning.USER_DEFINED_073]: 'Časový rad definovaný používateľom 073',
  [ETimeseriesMeaning.USER_DEFINED_074]: 'Časový rad definovaný používateľom 074',
  [ETimeseriesMeaning.USER_DEFINED_075]: 'Časový rad definovaný používateľom 075',
  [ETimeseriesMeaning.USER_DEFINED_076]: 'Časový rad definovaný používateľom 076',
  [ETimeseriesMeaning.USER_DEFINED_077]: 'Časový rad definovaný používateľom 077',
  [ETimeseriesMeaning.USER_DEFINED_078]: 'Časový rad definovaný používateľom 078',
  [ETimeseriesMeaning.USER_DEFINED_079]: 'Časový rad definovaný používateľom 079',
  [ETimeseriesMeaning.USER_DEFINED_080]: 'Časový rad definovaný používateľom 080',
  [ETimeseriesMeaning.USER_DEFINED_081]: 'Časový rad definovaný používateľom 081',
  [ETimeseriesMeaning.USER_DEFINED_082]: 'Časový rad definovaný používateľom 082',
  [ETimeseriesMeaning.USER_DEFINED_083]: 'Časový rad definovaný používateľom 083',
  [ETimeseriesMeaning.USER_DEFINED_084]: 'Časový rad definovaný používateľom 084',
  [ETimeseriesMeaning.USER_DEFINED_085]: 'Časový rad definovaný používateľom 085',
  [ETimeseriesMeaning.USER_DEFINED_086]: 'Časový rad definovaný používateľom 086',
  [ETimeseriesMeaning.USER_DEFINED_087]: 'Časový rad definovaný používateľom 087',
  [ETimeseriesMeaning.USER_DEFINED_088]: 'Časový rad definovaný používateľom 088',
  [ETimeseriesMeaning.USER_DEFINED_089]: 'Časový rad definovaný používateľom 089',
  [ETimeseriesMeaning.USER_DEFINED_090]: 'Časový rad definovaný používateľom 090',
  [ETimeseriesMeaning.USER_DEFINED_091]: 'Časový rad definovaný používateľom 091',
  [ETimeseriesMeaning.USER_DEFINED_092]: 'Časový rad definovaný používateľom 092',
  [ETimeseriesMeaning.USER_DEFINED_093]: 'Časový rad definovaný používateľom 093',
  [ETimeseriesMeaning.USER_DEFINED_094]: 'Časový rad definovaný používateľom 094',
  [ETimeseriesMeaning.USER_DEFINED_095]: 'Časový rad definovaný používateľom 095',
  [ETimeseriesMeaning.USER_DEFINED_096]: 'Časový rad definovaný používateľom 096',
  [ETimeseriesMeaning.USER_DEFINED_097]: 'Časový rad definovaný používateľom 097',
  [ETimeseriesMeaning.USER_DEFINED_098]: 'Časový rad definovaný používateľom 098',
  [ETimeseriesMeaning.USER_DEFINED_099]: 'Časový rad definovaný používateľom 099',
  [ETimeseriesMeaning.USER_DEFINED_100]: 'Časový rad definovaný používateľom 100',
};

const PhysicalDimension: Record<EPhysicalDimension, string> = {
  [EPhysicalDimension.ACCELERATION]: 'Zrýchlenie',
  [EPhysicalDimension.ADMITTANCE]: 'Admitancia',
  [EPhysicalDimension.AMOUNT]: 'Množstvo',
  [EPhysicalDimension.ANGLE]: 'Uhol',
  [EPhysicalDimension.ANGULAR_ACCELERATION]: 'Uhlové zrýchlenie',
  [EPhysicalDimension.ANGULAR_MOMENTUM]: 'Uhlový moment hybnosti',
  [EPhysicalDimension.ANGULAR_VELOCITY]: 'Uhlová rýchlosť',
  [EPhysicalDimension.APPARENT_ELECTRICAL_POWER]: 'Zdanlivý elektrický výkon',
  [EPhysicalDimension.AREA]: 'Plocha',
  [EPhysicalDimension.CAPACITANCE]: 'Elektrická kapacita',
  [EPhysicalDimension.COOLING_RATE]: 'Rýchlosť chladenia',
  [EPhysicalDimension.CURRENCY]: 'Mena',
  [EPhysicalDimension.DATETIME]: 'Dátum a čas',
  [EPhysicalDimension.DATE]: 'Dátum',
  [EPhysicalDimension.DENSITY]: 'Hustota',
  [EPhysicalDimension.DURATION]: 'Trvanie',
  [EPhysicalDimension.ELECTRICAL_REACTIVE_POWER]: 'Jalový elektrický výkon',
  [EPhysicalDimension.ELECTRICAL_RESISTANCE]: 'Elektrický odpor',
  [EPhysicalDimension.ELECTRIC_CHARGE]: 'Elektrický náboj',
  [EPhysicalDimension.ELECTRIC_CURRENT]: 'Elektrický prúd',
  [EPhysicalDimension.ENERGY]: 'Energia',
  [EPhysicalDimension.FLOW]: 'Prietok',
  [EPhysicalDimension.FLOW_PER_AREA]: 'Prietok na plochu',
  [EPhysicalDimension.FORCE]: 'Sila',
  [EPhysicalDimension.FREQUENCY]: 'Frekvencia',
  [EPhysicalDimension.HARDNESS]: 'Tvrdosť',
  [EPhysicalDimension.HEAT_FLUX]: 'Tepelný tok',
  [EPhysicalDimension.IDENTITY]: 'Identifikátor',
  [EPhysicalDimension.INDUCTIVITY]: 'Induktivita',
  [EPhysicalDimension.INTERVAL]: 'Interval',
  [EPhysicalDimension.LENGTH]: 'Dĺžka',
  [EPhysicalDimension.MAGNETIC_FLUX]: 'Magnetický tok',
  [EPhysicalDimension.MASS]: 'Hmotnosť',
  [EPhysicalDimension.MASS_FLOW_RATE]: 'Hmotnostný prietok',
  [EPhysicalDimension.MASS_FRACTION]: 'Hmotnostný zlomok',
  [EPhysicalDimension.MODULUS_OF_ELASTICITY]: 'Modul pružnosti',
  [EPhysicalDimension.MOMENTUM]: 'Hybnosť',
  [EPhysicalDimension.MOMENT_OF_INERTIA]: 'Moment zotrvačnosti',
  [EPhysicalDimension.POWER]: 'Výkon',
  [EPhysicalDimension.POWER_DENSITY]: 'Hustota výkonu',
  [EPhysicalDimension.PRESSURE]: 'Tlak',
  [EPhysicalDimension.PRESSURE_RATE]: 'Tlak v čase',
  [EPhysicalDimension.QUANTITY]: 'Množstvo',
  [EPhysicalDimension.RATIO]: 'Pomer',
  [EPhysicalDimension.ROTATIONAL_VELOCITY]: 'Rýchlosť otáčania',
  [EPhysicalDimension.SOUND_PRESSURE]: 'Zvukový tlak',
  [EPhysicalDimension.STRESS]: 'Napätie',
  [EPhysicalDimension.SURFACE_TENSION]: 'Povrchové napätie',
  [EPhysicalDimension.TEMPERATURE]: 'Teplota',
  [EPhysicalDimension.THERMAL_EXPANSION]: 'Tepelná rozťažnosť',
  [EPhysicalDimension.THERMAL_RESISTANCE]: 'Tepelný odpor',
  [EPhysicalDimension.TIME]: 'Čas',
  [EPhysicalDimension.TORQUE]: 'Krútiaci moment',
  [EPhysicalDimension.VELOCITY]: 'Rýchlosť',
  [EPhysicalDimension.VISCOSITY]: 'Viskozita',
  [EPhysicalDimension.VOLTAGE]: 'Napätie',
  [EPhysicalDimension.VOLUME]: 'Objem',
  [EPhysicalDimension.WORK]: 'Práca',
};

const CalendarEventType: Record<ECalendarEventType, string> = {
  [ECalendarEventType.ACTUAL_UNIT_DOWNTIME]: 'Skutočný prestoj (neplánovaný)',
  [ECalendarEventType.PLANNED_UNIT_DOWNTIME]: 'Plánovaný prestoj',
  [ECalendarEventType.OPERATION_HOLD]: 'Pozastavenie výroby',
};

const MaintenanceEventType: Record<components['schemas']['MaintenanceEventType'], string> = {
  MAINTENANCE: 'Plánovaná údržba (opakovaná)',
};

const MaintenanceStateType: Record<components['schemas']['MaintenanceEventStateType'], string> = {
  ASSIGNED: 'Priradený',
  COMPLETED: 'Dokončený',
  IN_PROGRESS: 'Prebieha',
  IN_REVIEW: 'V kontrole',
  PENDING: 'Čaká na vybavenie',
  REJECTED: 'Odmietnutý',
};

export const Weekday: Record<TComponentValue<'Weekday'>, string> = {
  MONDAY: 'Pondelok',
  TUESDAY: 'Utorok',
  WEDNESDAY: 'Streda',
  THURSDAY: 'Štvrtok',
  FRIDAY: 'Piatok',
  SATURDAY: 'Sobota',
  SUNDAY: 'Nedeľa',
};

const WeekdayShort: Record<TComponentValue<'Weekday'>, string> = {
  MONDAY: 'po',
  TUESDAY: 'ut',
  WEDNESDAY: 'st',
  THURSDAY: 'št',
  FRIDAY: 'pi',
  SATURDAY: 'so',
  SUNDAY: 'ne',
};

const AssetHierarchyLevel: Record<EHierarchy, string> = {
  [EHierarchy.CHAMBER]: generalTerm.chamber,
  [EHierarchy.COMPANY]: generalTerm.company,
  [EHierarchy.COMPONENT]: generalTerm.component,
  [EHierarchy.EQUIPMENT]: generalTerm.equipment,
  [EHierarchy.EQUIPMENT_GROUP]: generalTerm.equipmentGroup,
  [EHierarchy.AUTOMAT]: generalTerm.automat,
  [EHierarchy.AREA]: generalTerm.area,
  [EHierarchy.LOCATION]: generalTerm.location,
  [EHierarchy.ROOT]: generalTerm.root,
};

export const NotificationChannelType: Record<ENotificationChannelType, string> = {
  [ENotificationChannelType.EMAIL]: generalTerm.email,
  [ENotificationChannelType.IN_APP]: 'V aplikácii',
  [ENotificationChannelType.SMS]: generalTerm.sms,
};

export const SimulationStep: TComponentKeys<'SimulationStep'> & {
  surfaceHardnessSetpoint: string,
  shortCaseDepth: string,
} = {
  runtimeSeconds: 'Čas behu v sekundách',
  stageIndex: 'Index fázy',
  stageRuntimeSeconds: 'Čas behu fázy v sekundách',
  temperature: 'Teplota',
  carbonPotential: 'Potenciál uhlíka',
  nitrogenPotential: 'Potenciál dusíka',
  massFlowHydroCarbon: 'Hmotnostný prietok',
  carburizingDepth: 'HĻbka cementácie',
  carburizingDepthSetPoint: 'Nastavená hodnota hĻbky cementácie',
  carburizingDepthCarbonContent: 'Obsah uhlíka v hĻbke cementácie',
  surfaceCarbon: 'Povrchový uhlík',
  surfaceNitrogen: 'Povrchový dusík',
  surfaceCarbide: 'Povrchový karbid',
  effectiveCaseHardness: 'Efektívna tvrdosť povrchovej vrstvy',
  carbideLimit: 'Limit karbidov',
  surfaceHardnessSetpoint: 'Nastavená hodnota povrchovéj tvrdosti',
  sootLimit: 'Limit sadzí',
  caseDepth: 'HĻbka povrchovej vrstvy',
  shortCaseDepth: 'ECD',
  retainedAustenite: 'Zvyškový austenit',
  compoundLayerThickness: 'Hrúbka zlučeniny',
  diffusionLayerThickness: 'Hrúbka difúznej vrstvy',
  nitridedHardnessDepth: 'NHD + 50HV',
  totalDiffusionDepth: 'Celková hĻbka nitridácie',
  carbonProfile: 'Profil uhlíka do hĻbky',
  carbonProfileMin: 'Minimálna hodnota profilu uhlíka',
  carbonProfileMax: 'Maximálna hodnota profilu uhlíka',
  nitrogenProfile: 'Profil dusíka do hĻbky',
  nitrogenProfileMin: 'Minimálna hodnota profilu dusíka',
  nitrogenProfileMax: 'Maximálna hodnota profilu dusíka',
  carbideProfile: 'Profil karbidu do hĻbky',
  carbideProfileMin: 'Minimálna hodnota profilu karbidu',
  carbideProfileMax: 'Maximálna hodnota profilu karbidu',
  hardnessProfile: 'Profil tvrdosti do hĻbky',
  hardnessProfileMin: 'Minimálna hodnota profilu tvrdosti',
  hardnessProfileMax: 'Maximálna hodnota profilu tvrdosti',
  nSolubilityProfile: 'Skutočný limit nitridov',
  nitrideProfile: 'Profil nitridu do hĻbky',
  nitrideProfileMin: 'Minimálna hodnota profilu nitridu',
  nitrideProfileMax: 'Maximálna hodnota profilu nitridu',
  profileXValues: 'Hodnoty pozdĺž profilu zvonka dovnútra v mm',
};

const Asset: Partial<TComponentKeys<'Asset'>> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  hierarchyOrder: 'Pozícia v zozname',
  hierarchyLevel: 'Úroveň aktíva',
  parentId: 'Nadriadené aktívum',
};

const AssetCreate: Partial<TComponentKeys<'AssetCreate'>> = {
  ...Asset,
  permissionsRoleIds: 'Roly pre uděľovanie prístupu',
};

const CompanyCreate: Partial<TComponentKeys<'CompanyCreate'>> = {
  ...Asset,
  timezone: 'Časové pásmo',
};

const ShiftHoursWindow: Partial<TComponentKeys<'ShiftHoursWindow'>> = {
  startsAt: 'Začína o',
  endsAt: 'Končí o',
};

const LocationShift: Partial<TComponentKeys<'LocationShift'>> = {
  displayName: 'Zmena',
};

const LocationCreate: Partial<TComponentKeys<'LocationCreate'>> = {
  ...CompanyCreate,
};

const EquipmentCreate: Partial<TComponentKeys<'EquipmentCreate'>> = {
  ...Asset,
  equipmentPhysicalTypeId: 'Fyzický typ',
  serialNumber: 'Výrobné číslo',
  manufacturer: 'Výrobca',
  model: 'Model',
  ipAddress: 'IP adresa',
  chambers: 'Komory',
  department: 'Oddelenie',
};

const ChamberCreate: Partial<TComponentKeys<'ChamberCreate'>> = {
  ...Asset,
  chamberType: 'Typ komory',
  heatingZoneCount: 'Počet výrevých zón',
  concurrentJobLimit: 'Max. počet súbežných zákaziek',
  heatTreatmentCapabilities: 'Možnosti tepelného spracovania',
};

const Chamber: Partial<TComponentKeys<'ChamberCreate'>> = {
  ...ChamberCreate,
};

const CameraRegistryEntryCameraType: Record<components['schemas']['CameraRegistryEntry']['cameraType'], string> = {
  IP_CAMERA_SNAPSHOT: 'Snímka IP kamery',
  IP_CAMERA_STREAM: 'Stream IP kamery',
};

const CameraRegistryEntry: TComponentKeys<'CameraRegistryEntry'> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  assetId: generalTerm.asset,
  maxAgeThresholdSeconds: 'Čas uchovávania snímky',
  staleThresholdSeconds: 'Označiť snímku ako zastaranú po',
  cameraId: 'Kamera',
  cameraType: 'Typ kamery',
  cameraUrl: 'URL kamery',
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
};

const AssetProfileStatus: Record<EAssetProfileStatus, string> = {
  [EAssetProfileStatus.COMPLETE]: 'Kompletný',
  [EAssetProfileStatus.INCOMPLETE]: 'Nekompletný',
  [EAssetProfileStatus.INVALID]: 'Neplatný',
};

const AssetProfileCombinator: Record<EAssetProfileCombinator, string> = {
  [EAssetProfileCombinator.ANY_OF]: 'Ktorýkoľvek',
  [EAssetProfileCombinator.ALL_OF]: 'Všetko',
  [EAssetProfileCombinator.ONE_OF]: 'Jeden z',
};

const ObservationType: Record<EObservationType, string> = {
  [EObservationType.LOWER_CONTROL_LIMIT_EXCEEDED]: 'Bol prekročený dolný kontrolný limit',
  [EObservationType.UPPER_CONTROL_LIMIT_EXCEEDED]: 'Bol prekročený horný kontrolný limit',
  [EObservationType.NEGATIVE_TREND_DETECTED]: 'Detekováný negatívny trend',
  [EObservationType.POSITIVE_TREND_DETECTED]: 'Detekováný pozitívny trend',
  [EObservationType.LOWER_CUSTOM_LIMIT_EXCEEDED]: 'Bol prekročený dolný voliteľný limit',
  [EObservationType.UPPER_CUSTOM_LIMIT_EXCEEDED]: 'Bol prekročený horný voliteľný limit',
};

const ChamberType: Record<EChamberType, string> = {
  [EChamberType.HOT]: 'Horüa',
  [EChamberType.ISO_THERMAL]: 'Izotermická',
  [EChamberType.QUENCH]: 'Kaliaciště',
  [EChamberType.VACUM]: 'Vákuová',
  [EChamberType.LOADING]: 'Nakladanie',
  [EChamberType.UNLOADING]: 'Vykladanie',
  [EChamberType.LOADING_UNLOADING]: 'Nakladanie/Vykladanie',
};

const HeatTreatmentProcessType: Record<EHeatTreatmentProcessType, string> = {
  [EHeatTreatmentProcessType.ANNEALING]: 'Žihaenie',
  [EHeatTreatmentProcessType.QUENCHING]: 'Kalení',
  [EHeatTreatmentProcessType.NEUTRAL_HARDENING]: 'Neutrálne kalení',
  [EHeatTreatmentProcessType.CRYOGENIC_TREATMENT]: 'Kryogénne spracovanie',
  [EHeatTreatmentProcessType.TEMPERING]: 'Popúšťanie',
  [EHeatTreatmentProcessType.AGING]: 'Starnutie',
  [EHeatTreatmentProcessType.CARBURIZING]: 'Cementácia',
  [EHeatTreatmentProcessType.CARBONITRIDING]: 'Karbonitridačné spracovanie',
  [EHeatTreatmentProcessType.NITRIDING]: 'Nitridačné spracovanie',
  [EHeatTreatmentProcessType.NITROCARBURIZING]: 'Nitrokarbonizačné spracovanie',
  [EHeatTreatmentProcessType.PRE_OXIDATION]: 'Predbežná oxidácia',
  [EHeatTreatmentProcessType.POST_OXIDATION]: 'Následná oxidácia',
  [EHeatTreatmentProcessType.ACTIVATION]: 'Aktivácia',
};

const AppGroupSemanticDepartment: Record<EAppGroupSemanticDepartment, string> = {
  [EAppGroupSemanticDepartment.PRODUCTION_PLANNING]: 'Príprava výroby',
  [EAppGroupSemanticDepartment.OPERATIONS]: 'Výroba',
  [EAppGroupSemanticDepartment.QUALITY_ASSURANCE]: 'Zabezpečenie kvality',
  [EAppGroupSemanticDepartment.ANALYTICS]: 'Vyhodnotenie',
  [EAppGroupSemanticDepartment.MASTER_DATA]: 'Kmeňové údaje',
  [EAppGroupSemanticDepartment.ADMINISTRATION]: 'Administrácia',
};

const AppGroupSemanticTopic: Record<EAppGroupSemanticTopic, string> = {
  [EAppGroupSemanticTopic.OPERATION]: 'Prevádzka',
  [EAppGroupSemanticTopic.DATA]: 'Údaje / Prehľad',
  [EAppGroupSemanticTopic.MAINTENANCE]: 'Údržba',
  [EAppGroupSemanticTopic.PROCESS]: 'Proces',
  [EAppGroupSemanticTopic.CONFIGURATION]: 'Konfigurácia',
};

const AppId: Record<EAppId, string> = {
  [EAppId.AI_ASSISTANT]: 'AI asistent',
  [EAppId.ALARM_STATISTICS]: 'Štatistiky alarmu',
  [EAppId.ANOMALY_DETECTION]: 'Detekovanie anomálií',
  [EAppId.ASSET_MANAGEMENT]: 'Správa aktív',
  [EAppId.CERTIFICATION]: 'Audit a kvalita',
  [EAppId.CUSTOMER]: 'Zákazník',
  [EAppId.DEPRECATED_MODEL_PROCESS]: 'Tvorba modelových procesov',
  [EAppId.GRAFANA]: 'Grafana',
  [EAppId.HISTORY_AND_TRENDS]: 'História a trendy',
  [EAppId.LIVE]: 'Live aplikácia',
  [EAppId.MATERIALS]: 'Materiály',
  [EAppId.MODEL_BUILDER]: 'Tvorba modelov',
  [EAppId.OPERATION_CALENDAR]: 'Prevádzkový kalendár',
  [EAppId.ORDERS]: 'Objednávky',
  [EAppId.PARTS]: 'Diely',
  [EAppId.PERFORMANCE_DASHBOARD]: 'Nástenca výkonu',
  [EAppId.PRODUCTION_LOGS]: 'Výrobné záznamy',
  [EAppId.PRODUCTION_SCHEDULER]: 'Plánovač výroby',
  [EAppId.PRODUCTION_STATISTICS]: 'Výrobné štatistiky',
  [EAppId.PT9800_CONFIGURATION]: 'Konfigurácia Protherm',
  [EAppId.QMULUS_DASHBOARD]: 'Nástenky',
  [EAppId.RECIPE]: 'Recepty',
  [EAppId.RECIPE_ON_DEMAND]: 'Recept na požiadanie',
  [EAppId.REMOTE_CONTROL]: 'Vzdialené ovládanie',
  [EAppId.SHOP_LAYOUT]: 'Rozmiestnenie dielne',
  [EAppId.SIMULATOR]: 'Simulátory',
  [EAppId.TENANT_SETTINGS]: 'Nastavenia',
  [EAppId.UPC_PRO]: 'UPC Toolbox',
  [EAppId.USER_MANAGEMENT]: 'Správa používateľov',
};

const CarburizingSimulationPartShape: Record<ESimulatorSimulationPartShape, string> = {
  [ESimulatorSimulationPartShape.ROUND_STEEL]: 'Okrúhla oceľ',
  [ESimulatorSimulationPartShape.HEXAGON_OR_OCTAGON_STEEL]: 'Šesťhranná alebo osemhranná oceľ',
  [ESimulatorSimulationPartShape.SQUARE_OR_FLAT_STEEL]: 'Štvorcová alebo plochá oceľ',
  [ESimulatorSimulationPartShape.SOLID_STEEL]: 'Pevná oceľ (vnútorný priemer disku < 200 mm)',
  [ESimulatorSimulationPartShape.RING]: 'Prsteň (vnútorný priemer disku > 200 mm)',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_LESS_80]: 'Vnútorný priemer valcového telesa < 80 mm',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_LESS_200]: 'Vnútorný priemer valcového telesa < 200 mm',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_GREATER_200]: 'Vnútorný priemer valcového telesa > 200 mm',
  [ESimulatorSimulationPartShape.HOLLOW_BODY_CLOSED]: 'Duté telo uzatvorené z jednej alebo dvoch strán',
};

const CarburizingSimulationPartShapeSize: Record<ESimulatorSimulationPartShape, string> = {
  [ESimulatorSimulationPartShape.ROUND_STEEL]: 'Priemer',
  [ESimulatorSimulationPartShape.HEXAGON_OR_OCTAGON_STEEL]: 'Vzdialenosť medzi dvoma stranami',
  [ESimulatorSimulationPartShape.SQUARE_OR_FLAT_STEEL]: 'Dĺžka najmenšej strany',
  [ESimulatorSimulationPartShape.SOLID_STEEL]: 'Dĺžka najmenšej strany',
  [ESimulatorSimulationPartShape.RING]: 'Dĺžka najmenšej strany',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_LESS_80]: 'Hrúbka steny',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_LESS_200]: 'Hrúbka steny',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_GREATER_200]: 'Hrúbka steny',
  [ESimulatorSimulationPartShape.HOLLOW_BODY_CLOSED]: 'Hrúbka steny',
};

const CarburizingSimulatorRecipeType: Record<ESimulatorRecipeType, string> = {
  [ESimulatorRecipeType.CARBURIZING]: 'Cementácia [C]',
  [ESimulatorRecipeType.CARBONITRIDING]: 'Karbonitridačné spracovanie [CN]',
  [ESimulatorRecipeType.LOW_PRESSURE_CARBURIZING]: 'Nízkotlaková cementácia [LPC]',
  [ESimulatorRecipeType.NITRIDING]: 'Nitridačné spracovanie [N]',
  [ESimulatorRecipeType.NITROCARBURIZING]: 'Nitrokarbonizačné spracovanie [NC]',
};

const MaterialPropertySpectrum: Record<keyof components['schemas']['MaterialPropertySpectrum']['avg'], string> = {
  alloyFactorCAustenite: 'Zliatinový faktor [uhlík]',
  carbideLimit: 'Hranica karbidov [%aC=1]',
  alloyFactorNAustenite: 'Zliatinový faktor [dusík]',
  nitrideLimit: 'Hranica nitridu [%FexNy]',
  coreHardness: 'Tvrdosť jadra bez spracovania',
  maxHardness: 'Maximálna tvrdosť martenziitu',
  ac1: 'AC1',
  ac3: 'AC3',
  ms: 'Začiatok martenzitizovania',
  mf: 'Koniec martenzitizovania',
};

const PartMaterialProperties: TComponentKeys<'PartMaterialProperties'> = {
  ...MaterialPropertySpectrum,
  surfaceCarbon: 'Povrchový uhlík',
  referenceCarbon: 'Referencný uhlík',
};

const SimulatorRecipe: TComponentKeys<'SimulatorRecipe'> = {
  description: generalTerm.description,
  displayName: generalTerm.name,
  isGasCompositionConstant: 'Konštantné zloženie plynu',
  gasComposition: generalTerm.gasComposition,
  recipeType: 'Typ tepelného spracovania',
  simulationValuesType: 'Typ',
  simulatorRecipeId: '',
  simulatorPartId: '',
  materialCalculationModelId: 'Model výpočtu materiálu',
  stages: '',
  userDefinedAlloyFactor: generalTerm.alloyFactor,
  userDefinedCarbideLimit: generalTerm.carbideLimit,
  userDefinedCarbonContent: generalTerm.carbonContent,
  userDefinedCarburizingDepth: generalTerm.carburizingDepth,
  userDefinedReferenceCarbon: generalTerm.referenceCarbon,
};

const NotificationTable: Record<ESubscriptionTreeLabel, Record<'event' | 'description', string>> = {
  [ESubscriptionTreeLabel.ALARMS]: {
    event: 'Alarmy',
    description: 'Dostať upozornenie, keď nastane udalosť alarmu akejkoľvek závažnosti.',
  },
  [ESubscriptionTreeLabel.ALARM_OCCURRED]: {
    event: 'Alarm',
    description: 'Upozornenie na udalosti alarmu',
  },
  [ESubscriptionTreeLabel.JOBS]: {
    event: 'Spracovanie',
    description: 'Dostať upozornenie poľa zmiení statusu spracovania.',
  },
  [ESubscriptionTreeLabel.JOB_FINISHED]: {
    event: 'Spracovanie dokončené',
    description: 'Spracovanie bolo dokončené.',
  },
  [ESubscriptionTreeLabel.JOB_STARTED]: {
    event: 'Spracovanie spustené',
    description: 'Spracovanie bolo spustené.',
  },
  [ESubscriptionTreeLabel.MONITORING]: {
    event: 'Monitorovanie',
    description: 'Dostať upozornenia o udalostiach monitorovania Qmulus',
  },
  [ESubscriptionTreeLabel.TIMESERIES_LAG_DETECTED]: {
    event: 'Detekováné oneskorenie časového radu',
    description: 'Synchronizácia medzi zdrojmi dát zaostáva o viac ako 30 minút.',
  },
  [ESubscriptionTreeLabel.NEW_QMULUS_RELEASE]: {
    event: 'Nová verzia Qmulus',
    description: 'Bola vydaná nová verzia Qmulus.',
  },
  [ESubscriptionTreeLabel.ANOMALIES_AND_OBSERVATIONS]: {
    event: 'Anomálie a pozorovania',
    description: 'Dostať upozornenie poľa detekovania anomálie.',
  },
  [ESubscriptionTreeLabel.CONTROL_LEVEL_EXCEEDED]: {
    event: 'Presiahnutie kontrolnej úrovne',
    description: 'Kontrolná úroveň senzorov bola presiahnutá.',
  },
  [ESubscriptionTreeLabel.TREND_DETECTED]: {
    event: 'Detekováný trend',
    description: 'Detekováný nový trend v odmeraniach senzorov.',
  },
  [ESubscriptionTreeLabel.DRIFT_DETECTED]: {
    event: 'Detekováné odchylenie',
    description: 'Odmery senzorov sa odchyľujú od referenčných hodnôt.',
  },
  [ESubscriptionTreeLabel.OPERATION_MAINTENANCE]: {
    event: 'Prevádzka a údržba',
    description: 'Dostať upozornenia o udalostiach prevádzky a údržby.',
  },
  [ESubscriptionTreeLabel.MAINTENANCE_OCCURRENCE_STATE_CHANGED]: {
    event: 'Stav údržby sa zmenil',
    description: 'Aktuálny stav úlohy údržby sa zmenil.',
  },
};

const SubscriptionTreeNode: Partial<TComponentKeys<'SubscriptionTreeNode'>> = {
  notificationMetadataId: generalTerm.description,
  label: generalTerm.event,
  subscriptions: '',
};

const CarburizingRecipeSimulationValuesType: Record<ESimulatorRecipeSimulationValuesType, string> = {
  [ESimulatorRecipeSimulationValuesType.AVG_VAL]: 'Priemer',
  [ESimulatorRecipeSimulationValuesType.COMP_VAL]: 'Kompozit',
  [ESimulatorRecipeSimulationValuesType.USER_VAL]: 'Definované používateľom',
};

const DataFormat: Record<EDataFormat, string> = {
  [EDataFormat.TIME_SERIES]: 'Časový rad',
  [EDataFormat.JOB_AGGREGATE]: 'Hodnota spracovania',
  [EDataFormat.EVENT]: 'Udalosť',
  [EDataFormat.ALARM_EVENT]: 'Alarm',
  [EDataFormat.BLOB]: 'Binárny veľký objekt (BLOB)',
};

const ObservationTypeHeader: Record<string, string> = {
  name: 'Názov typu pozorovania',
};

const AssetUtilization: Partial<TComponentKeys<'AssetUtilization'>> = {
  assetDisplayName: generalTerm.asset,
  noProductionSeconds: 'Bez výroby',
  actualProductionTimeSeconds: 'Skutočný čas výroby',
  plannedProductionTimeSeconds: 'Plánovaný čas výroby',
  actualUnitDowntimeSeconds: CalendarEventType.ACTUAL_UNIT_DOWNTIME,
  plannedDowntimeSeconds: CalendarEventType.PLANNED_UNIT_DOWNTIME,
  operationHoldSeconds: CalendarEventType.OPERATION_HOLD,
  noProductionPercentage: 'Bez výroby',
  actualProductionTimePercentage: 'Skutočný čas výroby',
  plannedProductionTimePercentage: 'Plánovaný čas výroby',
  actualUnitDowntimePercentage: CalendarEventType.ACTUAL_UNIT_DOWNTIME,
  plannedDowntimePercentage: CalendarEventType.PLANNED_UNIT_DOWNTIME,
  operationHoldPercentage: CalendarEventType.OPERATION_HOLD,
  effectiveTargetProductivityPercentage: 'Cieľová produktivita',
};

const AuditSpecificationResultOverview: TComponentKeys<'AuditSpecificationResultOverview'> = {
  assetId: generalTerm.assetId,
  assetDisplayName: generalTerm.asset,
  auditResultId: 'Číslo správy',
  auditedAtUtc: generalTerm.date,
  auditedByUserName: 'Reportér',
  jobName: generalTerm.jobDisplayName,
  recipeDisplayName: generalTerm.recipe,
  jobId: '',
};

const AuditEnvelopeNonPassedReasons: Record<components['schemas']['AuditEnvelopeNonPassedReasons'], string> = {
  no_start_point_found: 'Nebol nájdený počiatočný bod',
  values_out_of_band: 'Hodnoty mimo pásma',
  max_duration_exceeded: 'Bol presiahnutý maximálny čas trvania',
  min_duration_not_reached: 'Minimálny čas trvania nebol dosiahnutý',
  job_is_running: 'Spracovanie prebieha',
  meanings_missing: 'Pre aktívum nie sú k dispozícii žiadne signály s priradenými významami.',
  no_data: 'Žiadne dáta',
  data_ended_unexpected: 'Dáta sa neočakávane skončili',
};

const Part: TComponentKeys<'Part'> = {
  hasAllRequiredAttributes: 'Všetky povinné atribúty vyplnené',
  displayName: generalTerm.part,
  description: 'Popis dielu',
  materialId: generalTerm.material,
  customerId: generalTerm.customer,
  materialDisplayName: generalTerm.material,
  treatmentRemarks: 'Poznámka k spracovaniu',
  customerDisplayName: generalTerm.customer,
  drawingReference: 'Výkres',
  isOnetime: 'Jednorazový diel',
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  images: 'Obrázky',
  partId: generalTerm.partId,
  attributes: 'Atribúty dielu',
  partRevisionId: generalTerm.revision,
  lastModifiedAtUtc: generalTerm.lastModifiedAt,
  lastModifiedByUserId: generalTerm.lastModifiedBy,
  lastModifiedByUserDisplayName: generalTerm.lastModifiedBy,
  createdAtUtc: generalTerm.createdAt,
  createdByUserId: generalTerm.createdBy,
  createdByUserDisplayName: generalTerm.createdBy,
  isDeleted: generalTerm.showDeleted,
};

const PartDetails: TComponentKeys<'PartDetails'> = {
  ...Part,
  assetQualifications: 'Kvalifikované zariadenia a receptúry',
};

const Customer: TComponentKeys<'Customer'> = {
  city: 'Mesto',
  contacts: 'Kontakty',
  country: 'Krajina',
  customerId: 'Spoločnosť',
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  lastModifiedAtUtc: generalTerm.lastModifiedAt,
  postalCode: 'PSČ',
  state: 'Štát',
  street: 'Ulica',
  displayName: 'Spoločnosť',
  email: 'E-mail',
  phone: 'Telefón',
  cellphone: 'Mobilný telefón',
  fax: 'Fax',
};

const Material: Partial<TComponentKeys<'Material'>> = {
  displayName: 'Názov materiálu',
  materialComposition: 'Zloženie materiálu',
  materialGroups: 'Vlastné zoskupenie',
  materialNames: 'Názvy materiálu',
};

const MaterialNamingSystem: Partial<TComponentKeys<'MaterialNamingSystem'>> = {
  displayName: 'Nazwa',
};

const ChartAssessmentTestRequirementType: Record<components['schemas']['ChartAssessmentTestRequirementType'], string> = {
  setpoints_greater_than_measures: 'Merania < Nastavené hodnoty',
  setpoints_greater_than_or_equal_to_measures: 'Merania ≤ Nastavené hodnoty',
  setpoints_less_than_measures: 'Merania > Nastavené hodnoty',
  setpoints_less_than_or_equal_to_measures: 'Merania ≥ Nastavené hodnoty',
};

const User: Partial<TComponentKeys<'User'>> = {
  displayName: generalTerm.displayName,
  firstName: 'Meno',
  lastName: 'Priezvisko',
  email: 'E-mail',
  rolesDisplayName: 'Roly',
};

const OpenAPIPrimitiveType: Record<components['schemas']['OpenAPIPrimitiveType'], string> = {
  boolean: 'Booleánska hodnota',
  integer: 'Celé číslo',
  number: 'Desátinné číslo',
  string: 'Text',
};

const ProductionStatistics: Partial<TComponentKeys<'ProductionStatistics'>> = {
  displayName: generalTerm.name,
  earliestJobStartedAtUtc: 'Najskorší čas spustenia spracovania',
  latestJobFinishedAtUtc: 'Najneskorsí čas ukončenia spracovania',
  jobCnt: 'Počet spracovania',
};

const CsvSeperator: Record<components['schemas']['CsvSeperator'], string> = {
  comma: 'Čiarka',
  semicolon: 'Bodkočiarka',
  tab: 'Tabulátor',
  pipe: 'Zvislá čiara',
  space: 'Medzera',
};

const CsvDatetimeFormat: Record<components['schemas']['CsvDatetimeFormat'], string> = {
  iso_8601: 'ISO 8601',
  unix_timestamp: 'UNIX',
  us: 'US',
};

const CsvQuoteStyle: Record<components['schemas']['CsvQuoteStyle'], string> = {
  necessary: 'Nevyhnutné',
  always: 'Vždy',
  non_numeric: 'Nenumeické',
  never: 'Nikdy',
};

const CsvQuoteStyleHint: Record<components['schemas']['CsvQuoteStyle'], string> = {
  necessary: 'Sú nevyhnutné, keď polia obsahujú úvodziovky, oddeľovač alebo koniec záznamu. Úvodziovky sú tiež nevyhnutné pri zápise prázdneho záznamu (ktorý je nerozoznateľný od záznamu s jedným prázdnym poľom). Toto je predvolené nastavenie.',
  always: 'Toto umiestni úvodziovky okolo každého poľa. Vždy.',
  non_numeric: 'Toto umiestni úvodziovky okolo všetkých polí, ktoré nie sú čísla. Konkrétne, pri zápise poľa, ktoré sa neanalyzuje ako platné desatinné alebo celé číslo, budú použité úvodziovky, aj keď nie sú absolútne nevyhnutné.',
  never: 'Polia nie sú nikdy zabalené do úvodzioviek, aj keď by to spôsobilo neplatné CSV dáta (napr.',
};

const CsvLineTerminator: Record<components['schemas']['CsvLineTerminator'], string> = {
  posix: 'POSIX',
  windows: 'Windows',
};

const CsvQuoteCharacter: Record<components['schemas']['CsvQuoteCharacter'], string> = {
  single_quote: 'Jednoduchá úvodná čiarka',
  double_quote: 'Dvojitá úvodná čiarka',
};

const PartAssetRecipeQualification: Partial<TComponentKeys<'PartAssetRecipeQualification'>> = {
  maxNumberOfPartsPerRost: 'Maximálny počet dielov na rost',
  assetIds: generalTerm.asset,
  recipeDisplayName: generalTerm.recipe,
  recipeId: generalTerm.recipe,
  loadingSchemeId: 'Schéma nakľadávania',
};

const AuditSpecification: TComponentKeys<'AuditSpecification'> = {
  auditSpecificationId: 'Identifikátor auditu',
  description: 'Popis',
  displayName: 'Názov špecifikácie',
  envelopes: 'Počet obálok',
  revision: 'Identifikátor revizíe',
  isOneTimeSpec: '',
};

const AlarmCollection: TComponentKeys<'AlarmCollection'> = {
  dataId: 'ID dát',
  assetId: generalTerm.asset,
  displayName: generalTerm.displayName,
  eventType: 'Typ alarmu',
  occurrenceData: generalTerm.date,
  severity: 'Typ alarmu',
};

const AlarmEvent: TComponentKeys<'AlarmEvent'> = {
  occurredAtUtc: 'Začiatok',
  clearedAtUtc: 'Koniec',
  acknowledgedAtUtc: 'Potvrdené',
  durationSeconds: 'Trvanie',
};

const CounterAction: Partial<TComponentKeys<'CounterAction'>> = {
  displayName: 'Protiopatrenie',
  description: generalTerm.description,
};

const DataSource: TComponentKeys<'DataSource'> = {
  dataSourceId: generalTerm.dataSourceId,
  displayName: generalTerm.displayName,
  dataSourceType: 'Typ zdroja dát',
  description: generalTerm.description,
  timezone: 'Časové pásmo',
  connectionDetails: 'Podrobnosti',
  assetAssociations: 'Prepätia aktív',
};

const DataSourceAssociation: TComponentKeys<'DataSourceAssociation'> = {
  assetId: generalTerm.assetId,
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  dataSourceType: DataSource.dataSourceType,
};

const Metadata: Partial<TComponentKeys<'Metadata'>> = {
  dataId: 'dataId',
  assetId: generalTerm.asset,
  dataSourceId: generalTerm.dataSourceId,
  color: generalTerm.color,
  dataFormat: 'Formát dát',
  dataSourceReference: generalTerm.dataSourceReference,
  dataSourceType: DataSource.dataSourceType,
  description: generalTerm.description,
  displayName: generalTerm.name,
  guaranteedColor: generalTerm.color,
  isHidden: 'Viditeľnosť',
  meaningId: 'Význam',
  meaningInstanceNumber: 'inštancia',
  significantDigits: 'Platné číslice',
  unitOfMeasureId: 'Jednotka',
  scaleType: 'Typ osi',
};

const MetadataUpdate: Partial<TComponentKeys<'MetadataUpdate'>> = {
  ...Metadata,
  significantDigitsOverwrite: Metadata.significantDigits,
};

const AlarmDefinition: Partial<TComponentKeys<'AlarmDefinition'>> = {
  ...Metadata,
  severity: AlarmCollection.severity,
};

const PartAttribute: Partial<TComponentKeys<'PartAttribute'>> & {
  value: string,
} = {
  displayName: 'Atribúty',
  unitOfMeasureId: 'Jednotka',
  value: 'Hodnota',
  valueType: 'Typ hodnoty',
  isBuiltIn: 'Vstavaný',
  isRequired: 'Povinný',
  isVisible: Metadata.isHidden!,
};

const Sample: Partial<TComponentKeys<'Sample'>> = {
  displayName: 'Vzorka',
};

const TableAssessmentTest: Partial<TComponentKeys<'TableAssessmentTest'>> = {
  displayName: generalTerm.name,
  isOverwritten: generalTerm.overwrite,
};

const TableAssessmentTestItem: Partial<Record<keyof components['schemas']['TableAssessmentTest']['items'][number], string>> = {
  displayName: generalTerm.name,
  requirement: generalTerm.requirement,
  result: generalTerm.result,
};

const AssessmentTestResultType = 'Výsledok testu';

const ChartAssessmentTest: Partial<TComponentKeys<'ChartAssessmentTest'>> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  xUnitId: 'Jednotka X',
  yUnitId: 'Jednotka Y',
  requirement: 'Skontrolovať',
  isOverwritten: generalTerm.overwrite,
};

const FileAssessmentTest: Partial<TComponentKeys<'FileAssessmentTest'>> = {
  displayName: generalTerm.name,
  result: 'Výsledok testu',
  resultComment: 'Komentár',
  isOverwritten: generalTerm.overwrite,
};

const ReportOverview: Partial<TComponentKeys<'ReportOverview'>> = {
  reportId: generalTerm.reportId,
  reporterDisplayName: generalTerm.reporter,
  jobId: generalTerm.jobId,
  jobDisplayName: generalTerm.job,
  assetId: generalTerm.assetId,
  assetDisplayName: generalTerm.asset,
  assetLocation: generalTerm.location,
  recipeDisplayName: generalTerm.recipe,
  partId: generalTerm.partId,
  partDisplayName: generalTerm.part,
  partCustomerDisplayName: generalTerm.customer,
  partMaterialDisplayName: generalTerm.material,
  partDrawingReference: generalTerm.drawingReference,
  orderNumbers: 'Objednávky',
  isAutogenerated: 'Importované z cudzieho systému',
  orderCreatedAtUtc: 'Dátum objednávky',
  isDeleted: generalTerm.showDeleted,
};

const ReportBlockAlarmsTemplateVariable: Record<TComponentsSchema['ReportBlockAlarmsTemplateVariable'], string> = {
  alarms: 'Alarmy',
};

const ReportBlockAuditEnvelopesTemplateVariable: Record<TComponentsSchema['ReportBlockAuditEnvelopesTemplateVariable'], string> = {
  envelopes: 'Obálky',
};

const ReportBlockAuditReportHeaderTemplateVariable: Record<TComponentsSchema['ReportBlockAuditReportHeaderTemplateVariable'], string> = {
  asset_name: 'Zariadenie',
  audit_specification_display_name: 'Špecifikácia',
  audited_at: 'Dátum auditu',
  auditor_display_name: 'Audítor',
  auditor_remark: 'Poznámky auditóra',
  description: 'Popis',
  duration_seconds: 'Trvanie',
  finished_at: 'Čas ukončenia',
  load_number: 'Číslo vsázky',
  location_name: 'Lokalita',
  recipe_description: 'Popis receptu',
  recipe_name: 'Recept',
  report_number: 'Číslo správy',
  started_at: 'Čas začatia',
};

const ReportBlockJobDetailsHeaderTemplateVariable: Record<TComponentsSchema['ReportBlockJobDetailsHeaderTemplateVariable'], string> = {
  asset_name: 'Zariadenie',
  description: 'Popis',
  duration_seconds: 'Trvanie',
  finished_at: 'Čas ukončenia',
  load_number: 'Číslo vsázky',
  location_name: 'Lokalita',
  recipe_description: 'Popis receptu',
  recipe_name: 'Recept',
  started_at: 'Čas začatia',
};

const ReportBlockJobPartsField: Record<TComponentsSchema['ReportBlockJobPartsField'], string> = {
  customer: generalTerm.customer,
  description: generalTerm.description,
  displayName: generalTerm.part,
  drawingReference: generalTerm.drawingReference,
  material: generalTerm.material,
  order: 'Objednávka',
  partCount: 'Množstvo',
  treatmentRemarks: 'Poznámky k spracovaniu',
};

const ReportBlockJobPartsTemplateVariable: Record<TComponentsSchema['ReportBlockJobPartsTemplateVariable'], string> = {
  attribute_totals: 'Súčet každého atribútu',
  attribute_unit_symbols: 'Jednotka každého atribútu',
  columns: 'Stĺpce tabuľky',
  has_part_read_permission: 'Oprávnenie na čítanie dielov',
  part_attributes: 'Atribúty dielov',
  part_count_total: 'Celkové množstvo',
  parts: 'Diely',
};

const ReportBlockJobStagesTemplateVariable: Record<TComponentsSchema['ReportBlockJobStagesTemplateVariable'], string> = {
  has_stage_read_permission: 'Oprávnenie na čítanie fáz',
  stages: 'Fázy',
};

const ReportBlockQualityReportHeaderTemplateVariable: Record<TComponentsSchema['ReportBlockQualityReportHeaderTemplateVariable'], string> = {
  asset_name: 'Zariadenie',
  customer_display_name: generalTerm.customer,
  description: 'Popis',
  duration_seconds: 'Trvanie',
  finished_at: 'Čas ukončenia',
  load_number: 'Číslo vsázky',
  location_name: 'Lokalita',
  material_display_name: generalTerm.material,
  part_display_name: generalTerm.part,
  part_drawing_reference: generalTerm.drawingReference,
  recipe_description: 'Popis receptu',
  recipe_name: 'Recept',
  report_number: 'Číslo správy',
  reporter_display_name: generalTerm.reporter,
  started_at: 'Čas začatia',
};

const ReportBlockQualitySamplesTemplateVariable: Record<TComponentsSchema['ReportBlockQualitySamplesTemplateVariable'], string> = {
  samples: 'Vzorky',
};

const ReportBlockRecipeTemplateVariable: Record<TComponentsSchema['ReportBlockRecipeTemplateVariable'], string> = {
  does_include_setpoints: 'Každá požadovaná hodnota vo vlastnom riadku',
  does_include_signal_legend: 'Legenda signálov',
  specification: 'Špecifikácia spracovania',
};

const ReportBlockSignaturesTemplateVariable: Record<TComponentsSchema['ReportBlockSignaturesTemplateVariable'], string> = {
  titles: 'Názvy polí na podpis',
};

const ReportBlockId: Record<TReportBlockId, string> = {
  alarms: 'Alarmy',
  auditEnvelopes: 'Obálky auditu',
  auditReportHeader: 'Hlavička správy auditu',
  jobDetailsHeader: 'Podrobnosti vsázky',
  jobImages: 'Obrázky vsázky',
  jobParts: 'Diely',
  jobStages: 'Fázy vsádzky',
  qualityReportHeader: 'Hlavička správy o kvalite',
  qualitySamples: 'Vzorky a skúšky',
  recipe: 'Recept',
  signatures: 'Podpisy',
  timeseriesChart: 'Graf časových radov',
};

const ReportComponentIdType: Record<TComponentsSchema['ReportComponentIdType'], string> = {
  auditEnvelope: 'Audit: Obálka',
  auditEnvelopeSections: 'Audit: Úseky obálky',
  auditResult: 'Audit: Výsledok',
  formatQuantity: 'Hodnota s jednotkou',
  formatValue: 'Hodnota',
  keyValueGrid: 'Mriežka hodnôt',
  keyValuePair: 'Hodnota s popisom',
  prothermBatchRecipe: 'Protherm: Vsádzkový recept',
  prothermBeltRecipe: 'Protherm: Pásový recept',
  qualityChartTest: 'Kvalita: Skúška s grafom',
  qualityFileTest: 'Kvalita: Skúška so súborom',
  qualityImageTest: 'Kvalita: Skúška s obrázkom',
  qualitySample: 'Kvalita: Vzorka',
  qualityTableTest: 'Kvalita: Tabuľková skúška',
  qualityTest: 'Kvalita: Skúška',
  qualityTestResult: 'Kvalita: Výsledok skúšky',
  table: 'Tabuľka',
  warning: 'Upozornenie',
};

const ReportComponentIdTypeDescription: Record<TComponentsSchema['ReportComponentIdType'], string> = {
  auditEnvelope: 'Zobrazuje obálku auditu s jej snímačmi, pásmom a trvaním, grafom snímačov, úsekmi a hodnotením.',
  auditEnvelopeSections: 'Uvádza úseky obálky, v ktorých hodnoty zostali v pásme, s ich časmi, hodnotami a výsledkom.',
  auditResult: 'Zobrazuje, či obálka vyhovela, nevyhovela alebo je nejednoznačná.',
  formatQuantity: 'Formátuje číslo s jeho jednotkou, napr. 1575 °C. Jednotky ako °C, %, bar alebo m³/h sa sádzajú ako v tlači; ostatné jednotky nasledujú za číslom ako text. Číslo sa zaokrúhli na zadaný počet desatinných miest, predvolene na dve.',
  formatValue: 'Formátuje jednu hodnotu: dátum a čas, čísla, áno alebo nie a text. Tabuľky a hodnoty s popisom ním formátujú každú hodnotu.',
  keyValueGrid: 'Usporiada hodnoty s popisom do riadkov, napr. podrobnosti vsázky. Každý riadok obsahuje dvojice kľúča a hodnoty.',
  keyValuePair: 'Zobrazí hodnotu pod jej popisom. Hodnotu formátuje komponent pre hodnoty.',
  prothermBatchRecipe: 'Zobrazí kroky vsádzkového receptu Protherm s požadovanými hodnotami, digitálnymi výstupmi, podmienkami ukončenia a trvaním, a voliteľne názvy digitálnych výstupov a podmienok.',
  prothermBeltRecipe: 'Zobrazí zóny pásového receptu Protherm s ich polohou v peci, a voliteľne ich požadované hodnoty s toleranciami a časom alarmu.',
  qualityChartTest: 'Zobrazí graf požadovaných a nameraných hodnôt skúšky s grafom a tabuľku ich hodnôt.',
  qualityFileTest: 'Zobrazí názov súboru skúšky so súborom.',
  qualityImageTest: 'Zobrazí obrázok skúšky s obrázkom.',
  qualitySample: 'Zobrazí vzorku správy o kvalite s jej vlastnými poľami a skúškami.',
  qualityTableTest: 'Zobrazí znaky tabuľkovej skúšky s ich požiadavkou a nameraným výsledkom.',
  qualityTest: 'Zobrazí skúšku s jej názvom, výsledkom a komentárom a podrobnosti jej druhu.',
  qualityTestResult: 'Zobrazí, či skúška vyhovela alebo nevyhovela.',
  table: 'Zobrazí tabuľku, ktorá pokračuje na ďalších stranách. Každý stĺpec má hlavičku, voliteľne jednotku, podiel na šírke, svoje bunky a voliteľne súčet.',
  warning: 'Zvýrazní správu, napr. že údaje sa nepodarilo načítať.',
};

const ReportComponentAuditEnvelopeParameter: Record<TComponentsSchema['ReportComponentAuditEnvelopeParameter'], string> = {
  envelope: 'Obálka',
};

const ReportComponentAuditEnvelopeSectionsParameter: Record<TComponentsSchema['ReportComponentAuditEnvelopeSectionsParameter'], string> = {
  envelope: 'Obálka',
};

const ReportComponentAuditResultParameter: Record<TComponentsSchema['ReportComponentAuditResultParameter'], string> = {
  result: 'Výsledok',
};

const ReportComponentFormatQuantityParameter: Record<TComponentsSchema['ReportComponentFormatQuantityParameter'], string> = {
  decimal_places: 'Desatinné miesta',
  unit: 'Jednotka',
  value: 'Hodnota',
};

const ReportComponentFormatValueParameter: Record<TComponentsSchema['ReportComponentFormatValueParameter'], string> = {
  value: 'Hodnota',
};

const ReportComponentKeyValueGridParameter: Record<TComponentsSchema['ReportComponentKeyValueGridParameter'], string> = {
  rows: 'Riadky',
};

const ReportComponentKeyValuePairParameter: Record<TComponentsSchema['ReportComponentKeyValuePairParameter'], string> = {
  key: 'Popis',
  value: 'Hodnota',
};

const ReportComponentProthermBatchRecipeParameter: Record<TComponentsSchema['ReportComponentProthermBatchRecipeParameter'], string> = {
  does_include_setpoints: 'Každá požadovaná hodnota vo vlastnom riadku',
  does_include_signal_legend: 'Legenda signálov',
  specification: 'Recept',
};

const ReportComponentProthermBeltRecipeParameter: Record<TComponentsSchema['ReportComponentProthermBeltRecipeParameter'], string> = {
  does_include_setpoints: 'Každá požadovaná hodnota vo vlastnom riadku',
  does_include_signal_legend: 'Legenda signálov',
  specification: 'Recept',
};

const ReportComponentQualityChartTestParameter: Record<TComponentsSchema['ReportComponentQualityChartTestParameter'], string> = {
  test: 'Skúška',
};

const ReportComponentQualityFileTestParameter: Record<TComponentsSchema['ReportComponentQualityFileTestParameter'], string> = {
  test: 'Skúška',
};

const ReportComponentQualityImageTestParameter: Record<TComponentsSchema['ReportComponentQualityImageTestParameter'], string> = {
  test: 'Skúška',
};

const ReportComponentQualitySampleParameter: Record<TComponentsSchema['ReportComponentQualitySampleParameter'], string> = {
  sample: 'Vzorka',
};

const ReportComponentQualityTableTestParameter: Record<TComponentsSchema['ReportComponentQualityTableTestParameter'], string> = {
  test: 'Skúška',
};

const ReportComponentQualityTestParameter: Record<TComponentsSchema['ReportComponentQualityTestParameter'], string> = {
  test: 'Skúška',
};

const ReportComponentQualityTestResultParameter: Record<TComponentsSchema['ReportComponentQualityTestResultParameter'], string> = {
  result: 'Výsledok',
};

const ReportComponentTableParameter: Record<TComponentsSchema['ReportComponentTableParameter'], string> = {
  columns: 'Stĺpce',
};

const ReportComponentWarningParameter: Record<TComponentsSchema['ReportComponentWarningParameter'], string> = {
  message: 'Správa',
};

const ReportScopeType: Record<TComponentsSchema['ReportScopeType'], string> = {
  assetTimeRange: 'Časový rozsah zariadenia',
  auditResultReference: 'Audit',
  jobReference: 'Vsázka',
  qualityReportReference: 'Správa o kvalite',
};

const ReportTemplateIdType: Record<TComponentsSchema['ReportTemplateIdType'], string> = {
  '__base__': 'Základné rozloženie',
  'qmulus.audit': 'Správa auditu',
  'qmulus.quality': 'Správa o kvalite',
  'qmulus.report_builder.heat_treatment_report': 'Správa o tepelnom spracovaní',
};

const ReportTemplateKind: Record<TComponentsSchema['ReportTemplateKind'], string> = {
  blocks: 'Bloky',
  template: 'Šablóna',
};

const ReportTemplateUpsert: Partial<TComponentKeys<'ReportTemplateUpsert'>> = {
  header: 'Hlavička',
  footer: 'Päta',
  content: 'Obsah',
  blocks: 'Bloky',
};

const ReportTemplateHeader: Partial<TComponentKeys<'ReportTemplateHeader-Input'>> = {
  height: 'Výška',
  contentLeft: 'Ľavý obsah',
  contentCenter: 'Stredný obsah',
  contentRight: 'Pravý obsah',
};

const ReportTemplateFooter: Partial<TComponentKeys<'ReportTemplateFooter-Input'>> = {
  contentLeft: 'Ľavý obsah',
  contentCenter: 'Stredný obsah',
  contentRight: 'Pravý obsah',
};

const Job: TComponentKeys<'Job'> = {
  assetId: generalTerm.asset,
  displayName: generalTerm.jobDisplayName,
  durationSeconds: 'Trvanie',
  description: generalTerm.description,
  startedAtUtc: generalTerm.jobStartedAtUtc,
  finishedAtUtc: generalTerm.jobFinishedAtUtc,
  finishedOverflowAtUtc: 'Skutočný čas ukončenia',
  treatmentSpecificationDisplayName: generalTerm.recipe,
  hasAlarms: 'Alarmy',
  jobVerdict: 'Hodnotenie',
  parentJobId: 'Tok procesu',
  isProductive: 'Označiť spracovanie ako produktívne',
  status: 'Stav',
  plannedStartAtUtc: 'Plánovaný čas spustenia',
  plannedFinishAtUtc: 'Plánovaný čas ukončenia',
  plannedDurationSeconds: 'Plánované trvanie',
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  startedBy: 'Spustil',
  treatmentSpecificationId: generalTerm.recipe,
  treatmentSpecificationRevision: generalTerm.recipe,
  jobId: generalTerm.jobId,
  assetHierarchyLevel: generalTerm.asset,
  previousJobId: 'Predchádzajúca zákazka',
  nextJobId: 'Nasledujúca zákazka',
  parentAssetId: generalTerm.asset,
  startedOrPlannedTimeAtUtc: generalTerm.jobStartedAtUtc,
  finishedOrPlannedTimeAtUtc: generalTerm.jobFinishedAtUtc,
  plannedOrActualDurationSeconds: 'Trvanie',
  isActive: 'Aktívny',
};

const MaterialCalculationModelOutput: Partial<TComponentKeys<'MaterialCalculationModel'>> = {
  displayName: 'Nazwa',
  description: 'Opis',
};

const JobStage: Partial<TComponentKeys<'JobStage'>> | Record<'state', string> = {
  assetId: generalTerm.asset,
  displayName: generalTerm.recipeStep,
  state: 'Status',
  jobDisplayName: generalTerm.jobDisplayName,
  startedAtUtc: generalTerm.jobStartedAtUtc,
  finishedAtUtc: generalTerm.jobFinishedAtUtc,
  durationSeconds: 'Trvanie',
  stageNo: 'Nr',
};

const JobStatus: Record<TJobStatus, string> = {
  PLANNED: 'Plánované',
  STARTED: 'Spustené',
  FINISHED: 'Dokončené',
};

const OrderStatus: Record<TOrderStatus, string> = {
  CREATED: 'Vytvorené',
  RELEASED: 'Uvoľnené',
  STARTED: 'Spustené',
  PAUSED: 'Pozastavené',
  FULFILLED: 'Splnené',
  CANCELLED: 'Zrušené',
};

const AlarmSeverity: Record<components['schemas']['AlarmSeverity'], string> = {
  0: 'Info',
  1: 'Upozornenie',
  2: 'Alarm',
  3: 'Nodná udalosť',
};

const AlarmEventSeverity: Record<components['schemas']['AlarmEventSeverity'], string> = {
  warning: 'Upozornenie',
  alarm: 'Alarm',
  emergency: 'Nútná udalosť',
};

const HandlingState: Record<components['schemas']['HandlingState'], string> = {
  [-99]: 'Poškodené',
  [-10]: 'Zlyhalo',
  [-5]: 'Zlyhalo (očakávané)',
  [0]: 'Zaregistrované',
  [10]: 'Spracúva sa',
  [20]: 'Dokončené',
};

const LogFileState: Partial<TComponentKeys<'LogFileState'>> = {
  filePathId: 'Cesta k súboru',
  state: 'Stav',
  jobId: generalTerm.job,
  discoveredAtUtc: 'Objavené dňa',
  lastModifiedAtUtc: generalTerm.lastModifiedAt,
};

const RootCauseGroup: Partial<TComponentKeys<'RootCauseGroup'>> = {
  displayName: 'Názov skupiny primarných príčin',
  description: generalTerm.description,
};

const EquipmentProfile: Partial<TComponentKeys<'ProfileTreeNode'>> = {
  label: 'Názov profilu',
  combinator: 'Typ kombinácie',
};

const TimeBucket_AssetUtilization_: Partial<TComponentKeys<'TimeBucket_AssetUtilization_'>> = {
  earliestDateUtc: 'Dátum začiatku',
  latestDateUtc: 'Dátum konca',
};

const EquipmentProfileMeaning: Partial<TComponentKeys<'Meaning'>> = {
  label: 'Názov významu',
  description: 'Popis',
  physicalDimensionId: 'Fyzikálna veličina',
  preferredUnitId: 'Preferovaná jednotka',
};

const EquipmentTreeNavigation: Partial<TComponentKeys<'AssetTreeNode'>> = {
  displayName: 'Názov',
};

const MaterialGroup: Partial<TComponentKeys<'MaterialGroup'>> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  materialIds: 'Materiály',
};

const MaterialCalculationModelElementCoefficient: Partial<TComponentKeys<'MaterialCalculationModelElementCoefficient'>> = {
  alloyingElementId: 'Element',
  description: generalTerm.description,
  eJcAustenite: 'eJC (Austenit)',
  eJcFerrite: 'eJC (Ferrit)',
  eJnAustenite: 'eJN (Austenit)',
  eJnFerrite: 'eJN (Ferrit)',
  rJjcAustenite: 'rJJC (Austenit)',
  rJjcFerrite: 'rJJC (Ferrit)',
  rJjnAustenite: 'rJJN (Austenit)',
  rJjnFerrite: 'rJJN (Ferrit)',
};

const MaintenanceState: Record<EMaintenanceState, string> = {
  [EMaintenanceState.PENDING]: 'Čaká na vybavenie',
  [EMaintenanceState.ACCEPTED]: 'Prijaté',
  [EMaintenanceState.IN_PROGRESS]: 'Prebieha',
  [EMaintenanceState.DONE]: 'Hotové',
  [EMaintenanceState.VALIDATED]: 'Overené',
};

const MaintenancePriority: Record<EMaintenancePriority, string> = {
  [EMaintenancePriority.LOW]: 'Nízka',
  [EMaintenancePriority.NORMAL]: 'Normálna',
  [EMaintenancePriority.HIGH]: 'Vysoká',
};

const MaintenancePeriod: Record<ECalendarPeriod, string> = {
  [ECalendarPeriod.WEEK]: 'Týždeň',
  [ECalendarPeriod.MONTH]: 'Mesiac',
  [ECalendarPeriod.YEAR]: 'Rok',
};

const MaintenanceRecurrenceMonthDayValue: Record<EMaintenanceRecurrenceMonthDayValue, string> = {
  [EMaintenanceRecurrenceMonthDayValue.FIRST]: 'Prvý',
  [EMaintenanceRecurrenceMonthDayValue.SECOND]: 'Druhý',
  [EMaintenanceRecurrenceMonthDayValue.THIRD]: 'Tretí',
  [EMaintenanceRecurrenceMonthDayValue.FOURTH]: 'Štvrtý',
  [EMaintenanceRecurrenceMonthDayValue.LAST]: 'Posledný',
};

const MaintenanceRecurrenceMonthDayDimension: Record<EMaintenanceRecurrenceMonthDayDimension, string> = {
  [EMaintenanceRecurrenceMonthDayDimension.WEEKDAY]: 'Pracovný deň',
  [EMaintenanceRecurrenceMonthDayDimension.WEEKEND_DAY]: 'Víkendový deň',
  [EMaintenanceRecurrenceMonthDayDimension.DAY]: 'Kalenárny deň | Kalenárne dni',
};

const MaintenanceRecurrenceEndCondition: Record<EMaintenanceRecurrenceEndCondition, string> = {
  [EMaintenanceRecurrenceEndCondition.AFTER]: 'po',
  [EMaintenanceRecurrenceEndCondition.ON_THIS_DAY]: 'v tento deň',
};

const MetadataWithChartConfigInput: Partial<TComponentKeys<'MetadataWithChartConfig'>> = {
  ...Metadata,
};

const RootCause: Partial<TComponentKeys<'RootCause'> & {
  count: string,
}> = {
  displayName: 'Primárna príčina',
  description: generalTerm.description,
  count: 'Počet',
};

const JobValueData: Partial<TComponentKeys<'JobValueData'>> = {
  jobId: 'JobId',
  jobDisplayName: generalTerm.jobDisplayName,
  jobStartUtc: generalTerm.jobStartedAtUtc,
  jobEndUtc: generalTerm.jobFinishedAtUtc,
  value: 'Hodnota',
  recipeId: 'RecipeId',
  recipeRevision: 'RecipeRevision',
  recipeDisplayName: generalTerm.recipe,
};

const QualityPreset: Partial<TComponentKeys<'QualityPreset'>> = {
  displayName: generalTerm.name,
};

const Observation: Partial<TComponentKeys<'Observation'>> = {
  assetDisplayName: generalTerm.asset,
  displayName: 'Názov pozorovania',
  observationTimeAtUtc: 'Pozorovanie nastalo',
  jobFeatureDisplayName: 'Vlastnosť spracovania',
  observationType: 'Typ pozorovania',
  jobValue: 'Hodnota',
  jobDisplayName: generalTerm.jobDisplayName,
  jobStartedAtUtc: generalTerm.jobStartedAtUtc,
  jobFinishedAtUtc: generalTerm.jobFinishedAtUtc,
};

const QuenchingMedia: Partial<TComponentKeys<'QuenchingMedia'>> = {
  displayName: generalTerm.name,
  maxIntensity: 'Maximálna intenzita',
  minIntensity: 'Minimálna intenzita',
  quenchingMediaId: 'ID',
};

const expressionFunctionCategories: Record<TFunctionCategory, string> & {
  all: string,
} = {
  all: 'Všetky',
  arithmetic: 'Aritmetické funkcie',
  custom: 'Vlastné funkcie',
  cumulative: 'Kumulatívne funkcie',
  exponential: 'Exponenciálne funkcie',
  generative: 'Generatívne funkcie',
  imputation: 'Imputačné funkcie',
  logical: 'Logické funkcie',
  numerical: 'Numerické funkcie',
  operator: 'Operátor',
  other: 'Ostatné funkcie',
  sections: 'Funkcie sekcií',
  statistical: 'Štatistické funkcie',
  trigonometric: 'Trigonometrické funkcie',
};

const expressionFunctionsExplanations: {
  functions: Record<TFunctionDefinition['functionType'], string>,
  operators: Record<TOperatorDefinition['operatorType'], string>,
} = {
  functions: {
    abs: 'Táto funkcia vypočíta absolútne hodnoty signálu po prvkoch.',
    acos: 'Táto funkcia vypočíta arkuskozínus každého prvku signálu.',
    add: 'Sčítanie. Sčíta dva operandy.',
    asin: 'Táto funkcia vypočíta arkussínus každého prvku signálu.',
    atan2: 'Táto funkcia vypočíta arkustangens a/b pre každý zodpovedajúci pár prvkov.',
    atan: 'Táto funkcia vypočíta arkustangens každého prvku signálu.',
    avg: 'Ak je zadaných viac argumentov, táto funkcia vypočíta priemer každého signálu. Inak vypočíta priemer zo všetkých vzoriek.',
    avg_windowed: 'Táto funkcia vypočíta priemer hodnôt signálu pre zadanú dĺžku okna v sekundách. Záporná dĺžka okna bude konvertovaná na dĺžku okna 0, čo dáva hodnotu NaN.',
    bfill: 'Vyplní hodnoty NaN pomocou nasledujúcej platnej hodnoty.',
    ceil: 'Táto funkcia zaokrúhli každý prvok signálu nahor na najbližšie celé číslo.',
    clamp: 'Táto funkcia obmedzí hodnoty signálu v zadanom rozsahu.',
    cos: 'Táto funkcia vypočíta kosínus každého prvku signálu.',
    count_samples: 'Akumuluje počet vzoriek, kde vstupný signál vyhodnotí ako pravdivý.',
    count_seconds: 'Akumuluje sekundy, kde vstupný signál vyhodnotí ako pravdivý.',
    counter: 'Táto funkcia zvýši počítadlo o 1 pre každý časový krok.',
    cumulative_avg: 'Vráti kumulatívny priemer signálu.',
    cumulative_integrate: 'Vráti kumulatívny integrál (pomocou metódy trapézov v sekundách).',
    cumulative_max: 'Vráti kumulatívne maximum signálu.',
    cumulative_mean: 'Vráti kumulatívny priemer signálu.',
    cumulative_median: 'Vráti kumulatívny medián signálu.',
    cumulative_min: 'Vráti kumulatívne minimum signálu.',
    cumulative_range: 'Vráti kumulatívny rozsah signálu.',
    cumulative_std: 'Vráti kumulatívnu štandardnú odchýlku signálu.',
    cumulative_sum: 'Vráti kumulatívny súčet signálu, pričom hodnoty NaN sú považované za nulu. Kumulatívny súčet sa nemení pri výskyte NaN a úvodné NaN sú nahradené nulami. Nuly sú vrátené pre signály, ktoré sú celé NaN alebo prázdne.',
    cumulative_var: 'Vráti kumulatívny rozptyl signálu.',
    degrees: 'Táto funkcia konvertuje každý prvok signálu z radiánov na stupne.',
    denormalize: 'Táto funkcia denormalizuje hodnoty signálu z rozsahu [0, 1] do zadaného rozsahu.',
    diff: 'Táto funkcia vypočíta rozdiel medzi po sebe nasledujúcimi prvkami signálu.',
    divide: 'Delenie. Vydelí prvý operand druhým.',
    do_nothing: 'Táto funkcia vráti vstupný signál.',
    elapsed_seconds: 'Táto funkcia vráti počet sekúnd, ktoré ubehli od začiatku časového rámca.',
    equals: 'Rovnosť. Vráti true, ak sú operandy rovnaké, inak false.',
    exp: 'Táto funkcia vypočíta exponenciálu každého prvku signálu.',
    fill: 'Vyplní hodnoty NaN v signáli a hodnotami signálu b.',
    ffill: 'Vyplní hodnoty NaN propagáciou poslednej platnej hodnoty na nasledujúcu.',
    floor: 'Táto funkcia zaokrúhli každý prvok signálu nadol na najbližšie celé číslo.',
    floor_divide: 'Celočíselné delenie. Vráti podiel delenia bez zvyšku.',
    gradient: 'Numerický gradient vstupného signálu ako 1/sekunda.',
    gradient_windowed: 'Numerický gradient pre dané dx (sekundy). Záporné alebo nulové dx spôsobí hodnoty NaN. Vstupné dx sa predpokladá ako konštantné.',
    greater_than: 'Väčšie ako. Vráti true, ak je prvý operand väčší ako druhý.',
    greater_than_or_equal: 'Väčšie alebo rovné. Vráti true, ak je prvý operand väčší alebo rovný druhému.',
    if: 'Táto funkcia vykoná podmienkovú operáciu po prvkoch na základe podmienky.',
    integrate: 'Táto funkcia integruje vstupný signál pomocou zloženého trapézového pravidla v sekundách.',
    interpfill: 'Vyplní hodnoty NaN lineárnou interpoláciou medzi poslednou a nasledujúcou platnou hodnotou.',
    is_finite: 'Táto funkcia skontroluje, či sú prvky signálu konečné po prvkoch.',
    less_than: 'Menšie ako. Vráti true, ak je prvý operand menší ako druhý.',
    less_than_or_equal: 'Menšie alebo rovné. Vráti true, ak je prvý operand menší alebo rovný druhému.',
    ln: 'Táto funkcia vypočíta prirodzený logaritmus každého prvku signálu.',
    log10: 'Táto funkcia vypočíta logaritmus so základom 10 každého prvku signálu.',
    log2: 'Táto funkcia vypočíta logaritmus so základom 2 každého prvku signálu.',
    log: 'Táto funkcia vypočíta logaritmus každého prvku signálu so základom b.',
    logical_and: 'Logické AND. Vráti true, ak sú oba operandy pravdivé, inak false.',
    logical_not: 'Unárne logické NOT. Vráti true, ak je operand nepravdivý, a false, ak je operand pravdivý.',
    logical_or: 'Logické OR. Vráti true, ak je aspoň jeden operand pravdivý, inak false.',
    logical_xor: 'Logické XOR. Vráti false, ak majú oba operandy rovnaký logický stav, inak true.',
    max: 'Ak je zadaných viac argumentov, táto funkcia nájde maximálnu hodnotu medzi signálmi. Inak nájde maximálnu hodnotu medzi všetkými vzorkami.',
    max_windowed: 'Táto funkcia vypočíta maximum hodnôt signálu pre zadanú dĺžku okna v sekundách. Záporná dĺžka okna bude konvertovaná na dĺžku okna 0, čo dáva hodnotu NaN.',
    mean: 'Ak je zadaných viac argumentov, táto funkcia vypočíta priemer každého signálu. Inak vypočíta priemer zo všetkých vzoriek.',
    mean_windowed: 'Táto funkcia vypočíta priemer hodnôt signálu pre zadanú dĺžku okna v sekundách. Záporná dĺžka okna bude konvertovaná na dĺžku okna 0, čo dáva hodnotu NaN.',
    median: 'Ak je zadaných viac argumentov, táto funkcia nájde medián medzi signálmi. Inak nájde medián medzi všetkými vzorkami.',
    median_windowed: 'Táto funkcia vypočíta medián hodnôt signálu pre zadanú dĺžku okna v sekundách. Záporná dĺžka okna bude konvertovaná na dĺžku okna 0, čo dáva hodnotu NaN.',
    min: 'Ak je zadaných viac argumentov, táto funkcia nájde minimálnu hodnotu medzi signálmi. Inak nájde minimálnu hodnotu medzi všetkými vzorkami.',
    min_windowed: 'Táto funkcia vypočíta minimum hodnôt signálu pre zadanú dĺžku okna v sekundách. Záporná dĺžka okna bude konvertovaná na dĺžku okna 0, čo dáva hodnotu NaN.',
    modulo: 'Modulo. Vráti zvyšok po delení prvého operandu druhým.',
    multiply: 'Násobenie. Vynásobí dva operandy.',
    negate: 'Táto funkcia neguje každý prvok signálu.',
    normalize: 'Táto funkcia normalizuje hodnoty signálu do rozsahu [0, 1].',
    not_equals: 'Nerovnosť. Vráti true, ak operandy nie sú rovnaké, inak false.',
    percentile: 'Táto funkcia vypočíta percentil hodnôt signálu.',
    power: 'Umocňovanie. Umocní prvý operand na mocninu druhého operandu.',
    radians: 'Táto funkcia konvertuje každý prvok signálu zo stupňov na radiány.',
    ramp: 'Táto funkcia vráti lineárnu rampu od 0 do 1.',
    random: 'Táto funkcia vráti náhodné číslo medzi 0 a 1 pre každý časový krok.',
    range: 'Ak je zadaných viac argumentov, táto funkcia vypočíta rozsah (max - min) každého signálu. Inak vypočíta rozsah všetkých vzoriek.',
    range_windowed: 'Táto funkcia vypočíta rozsah hodnôt signálu pre zadanú dĺžku okna v sekundách. Záporná dĺžka okna bude konvertovaná na dĺžku okna 0, čo dáva hodnotu NaN.',
    relative_error: 'Vráti relatívnu chybu `((skutočná / relatívna) - 1) * 100`, kde relatívna je != 0 a hodnoty sú konečné.',
    round: 'Táto funkcia zaokrúhli každý prvok signálu na najbližšie celé číslo.',
    sections_join: 'Odstráni všetky medzery medzi sekciami kratšie ako zadaná hranica spojením obklopujúcich sekcií.',
    sections_first: 'Vráti prvú dostupnú sekciu. Môže nevrátiť žiadnu sekciu, ak žiadna neexistuje.',
    sections_last: 'Vráti poslednú dostupnú sekciu. Môže nevrátiť žiadnu sekciu, ak žiadna neexistuje.',
    sections_nth: 'Vráti najkratšie sekcie. Môže vrátiť viac sekcií, ak existuje viac sekcií s minimálnym trvaním.',
    sections_shortest: 'Vráti najkratšie sekcie. Môže vrátiť viac sekcií, ak existuje viac sekcií s minimálnou dĺžkou.',
    sections_median_duration: 'Vráti sekcie s trvaním zodpovedajúcim mediánu trvaní všetkých detekovaných sekcií.',
    sections_longest: 'Vráti najdlhšie sekcie. Môže vrátiť viac sekcií, ak existuje viac sekcií s maximálnym trvaním.',
    sections_max_value: 'Vráti sekcie obsahujúce celkovú najväčšiu hodnotu. Môže vrátiť viac sekcií, ak viaceré sekcie obsahujú maximum.',
    sections_median_value: 'Vráti sekcie obsahujúce mediánovú hodnotu. Môže vrátiť viac sekcií, ak viaceré sekcie obsahujú medián.',
    sections_min_value: 'Vráti sekcie obsahujúce najmenšiu hodnotu. Môže vrátiť viac sekcií, ak viaceré sekcie obsahujú minimum.',
    sign: 'Táto funkcia vráti znamienko každého prvku signálu.',
    sin: 'Táto funkcia vypočíta sínus každého prvku signálu.',
    sqrt: 'Táto funkcia vypočíta druhú odmocninu každého prvku signálu.',
    stage_number: 'Vráti číslo kroku receptu v danom čase, ak je aktívne spracovanie. Inak vráti NaN.',
    std: 'Ak je zadaných viac argumentov, táto funkcia vypočíta štandardnú odchýlku každého signálu. Inak vypočíta štandardnú odchýlku všetkých vzoriek.',
    std_windowed: 'Táto funkcia vypočíta štandardnú odchýlku hodnôt signálu pre zadanú dĺžku okna v sekundách. Záporná dĺžka okna bude konvertovaná na dĺžku okna 0, čo dáva hodnotu NaN.',
    steady_state_detection: 'Táto funkcia umožňuje nájsť oblasti s takmer konštantnými hodnotami. Dĺžka okna umožňuje vyhladzovanie vstupného signálu a limitná hodnota umožňuje definovať maximálny gradient, keď sa signál ešte považuje za konštantný.',
    subtract: 'Odčítanie. Odčíta druhý operand od prvého.',
    sum: 'Ak je zadaných viac argumentov, táto funkcia vypočíta súčet každého signálu. Inak vypočíta súčet všetkých vzoriek.',
    sum_windowed: 'Táto funkcia vypočíta súčet hodnôt signálu pre zadanú dĺžku okna v sekundách. Záporná dĺžka okna bude konvertovaná na dĺžku okna 0, čo dáva hodnotu NaN.',
    tan: 'Táto funkcia vypočíta tangens každého prvku signálu.',
    timestamp: 'Táto funkcia vráti milisekundy od epochy (1970-01-01 00:00:00 UTC).',
    total_seconds: 'Vráti celkový počet sekúnd aktuálne viditeľného časového rámca ako konštantu.',
    trunc: 'Táto funkcia skráti každý prvok signálu smerom k nule.',
    var: 'Ak je zadaných viac argumentov, táto funkcia vypočíta rozptyl každého signálu. Inak vypočíta rozptyl všetkých vzoriek.',
    var_windowed: 'Táto funkcia vypočíta rozptyl hodnôt signálu pre zadanú dĺžku okna v sekundách. Záporná dĺžka okna bude konvertovaná na dĺžku okna 0, čo dáva hodnotu NaN.',
    first: 'Táto funkcia vráti konštantu prvej vzorky.',
    last: 'Táto funkcia vráti konštantu poslednej vzorky.',
    shift: 'Posunie signál o zadaný počet vzoriek. Kladné hodnoty posunú doprava, záporné doľava. Nové hodnoty sú vyplnené hodnotami NaN.',
  },
  operators: {
    '!': 'Unárne logické NOT. Vráti true, ak je operand nepravdivý, a false, ak je operand pravdivý.',
    '-': 'Odčítanie. Odčíta druhý operand od prvého.',
    '+': 'Sčítanie. Sčíta dva operandy.',
    '**': 'Umocňovanie. Umocní prvý operand na mocninu druhého operandu.',
    '*': 'Násobenie. Vynásobí dva operandy.',
    '//': 'Celočíselné delenie. Vráti podiel delenia bez zvyšku.',
    '/': 'Delenie. Vydelí prvý operand druhým.',
    '%': 'Modulo. Vráti zvyšok po delení prvého operandu druhým.',
    '==': 'Rovnosť. Vráti true, ak sú operandy rovnaké, inak false.',
    '!=': 'Nerovnosť. Vráti true, ak operandy nie sú rovnaké, inak false.',
    '<': 'Menšie ako. Vráti true, ak je prvý operand menší ako druhý.',
    '<=': 'Menšie alebo rovné. Vráti true, ak je prvý operand menší alebo rovný druhému.',
    '>': 'Väčšie ako. Vráti true, ak je prvý operand väčší ako druhý.',
    '>=': 'Väčšie alebo rovné. Vráti true, ak je prvý operand väčší alebo rovný druhému.',
    '&&': 'Logické AND. Vráti true, ak sú oba operandy pravdivé, inak false.',
    '||': 'Logické OR. Vráti true, ak je aspoň jeden operand pravdivý, inak false.',
    '^': 'Logické XOR. Vráti false, ak majú oba operandy rovnaký logický stav, inak true.',
  },
};

const DataMigrationState: TComponentKeys<'DataMigrationState'> = {
  assetId: generalTerm.assetId,
  displayName: generalTerm.displayName,
  assetDisplayName: generalTerm.asset,
  lastTimestampAtUtc: 'Najnovšie dáta',
  lastEtlRunAtUtc: 'Posledná migrácia',
  etlLagSeconds: 'Oneskorenie migrácie',
  dataId: 'Identifikátor',
  dataFormat: 'Typ',
  dataSourceReference: generalTerm.dataSourceReference,
};

const JobMigrationState: TComponentKeys<'JobMigrationState'> = {
  assetId: generalTerm.assetId,
  assetDisplayName: generalTerm.asset,
  lastTimestampAtUtc: 'Najnovšie dáta',
  lastEtlRunAtUtc: 'Posledná migrácia',
  etlLagSeconds: 'Oneskorenie migrácie',
};

const QmulusDriver = {
  PT9800: {
    DataMigrationState,
    JobMigrationState,
  },
};

const TableTestData: Partial<TComponentKeys<'TableTestData'>> = {
  jobDisplayName: generalTerm.jobDisplayName,
  jobStartUtc: generalTerm.jobStartedAtUtc,
  jobEndUtc: generalTerm.jobFinishedAtUtc,
  value: 'Hodnota',
  recipeDisplayName: generalTerm.recipe,
  partDisplayName: generalTerm.part,
  sampleDisplayName: 'Vzorka',
};

const ModelBuilderModelOverview: Partial<TComponentKeys<'ModelBuilderModelOverview'>> = {
  accuracy: 'Presnosť',
  assetId: generalTerm.asset,
  createdAtUtc: generalTerm.createdAt,
  createdByDisplayName: 'Autor',
  displayName: 'Model',
  inputDisplayNames: 'Vstupné signály',
  lifecycleState: 'Stav',
  modelRevision: 'Verzia',
  modelType: 'Typ modelu',
  outputDisplayName: 'Výstupný signál',
  revisionCount: 'Verzie',
};

const ModelLifecycleState: Record<EModelLifecycleState, string> = {
  [EModelLifecycleState.REGISTERED]: 'Registrovaný',
  [EModelLifecycleState.LOADING_DATA]: 'Načítavanie tréningových dát',
  [EModelLifecycleState.PREPARING_DATA]: 'Spracovanie tréningových dát',
  [EModelLifecycleState.TRAINING]: 'Tréning prebieha',
  [EModelLifecycleState.SAVING_MODEL]: 'Ukladanie modelu',
  [EModelLifecycleState.CREATING_METADATA]: 'Registrovanie signálov',
  [EModelLifecycleState.READY]: 'Pripravený',
};

const ModelBuilderModelType: Record<components['schemas']['ModelBuilderModelType'], string> = {
  LINEAR_REGRESSION: 'Lineárna regresia',
  SMART_REGRESSION: 'Inteligentná regresia',
};

const ModelBuilderTrainingRequest: Partial<TComponentKeys<'ModelBuilderTrainingRequest'>> = {
  displayName: 'Názov modelu',
  testSize: 'Testovacia sada',
};

const TimeBucketType: Record<TComponentValue<'TimeBucketType'>, string> = {
  day: 'Dni',
  week: 'Týždne',
  month: 'Mesiace',
  quarter: 'Kvartály',
  year: 'Roky',
  full: 'Celý časový rozsah',
  auto: 'Automaticky',
};

const AlarmStatistics: Partial<TComponentKeys<'AlarmStatistics'>> = {
  displayName: generalTerm.displayName,
  severity: AlarmDefinition.severity,
};

const AssetBlobIndex: Partial<TComponentKeys<'AssetBlobIndex'>> = {
  displayName: generalTerm.displayName,
  description: generalTerm.description,
  semanticMeaningId: 'Kategória',
  ownerDisplayName: 'Nahral',
};

const BlobMeaning: Record<TComponentValue<'BlobMeaning'>, string> = {
  [EBlobMeaning.MANUAL]: 'Príručka',
  [EBlobMeaning.DRAWING]: 'Výkres',
  [EBlobMeaning.CONFIGURATION]: 'Konfigurácia',
  [EBlobMeaning.BILL_OF_MATERIALS]: 'Kusovník',
  [EBlobMeaning.TENANT_LOGO]: 'Logo',
  [EBlobMeaning.PART_IMAGE]: 'Obrázok dielu',
};

const Meaning: Partial<TComponentKeys<'Meaning'>> = {
  meaningId: 'Význam',
  userDefinedDisplayName: 'Prepísanie predvoleného názvu',
  userDefinedDescription: generalTerm.description,
  physicalDimensionId: 'Fyzikálna dimenzia',
  preferredUnitId: 'Uprednostňovaná jednotka',
  defaultColor: generalTerm.color,
};

const MaintenanceEventOverview: TComponentKeys<'MaintenanceEventOverview'> = {
  createdByDisplayName: generalTerm.createdBy,
  createdById: generalTerm.createdBy,
  assetDisplayName: generalTerm.asset,
  assetId: generalTerm.asset,
  assetLocation: generalTerm.location,
  assigneeDisplayName: 'Assignee',
  assigneeId: 'Assignee',
  calendarEventType: 'Event type',
  color: generalTerm.color,
  displayName: generalTerm.displayName,
  endAtUtc: generalTerm.jobFinishedAtUtc,
  maintenanceSeriesId: 'Series',
  occurrenceId: 'Occurrence',
  priority: 'Priority',
  startAtUtc: generalTerm.jobStartedAtUtc,
  state: 'State',
};

const MaintenanceEventDetails: Partial<TComponentKeys<'MaintenanceEventDetails'>> = {
  ...MaintenanceEventOverview,
  description: generalTerm.description,
  seriesRecurrence: 'Opakovanie',
  seriesAttachments: 'Prílohy',
  overheadSeconds: 'Réžia trvania úlohy',
  taskInstances: 'Úlohy údržby',
  assetBlobs: 'Dokumentácia aktíva',
};

const MaintenanceSeriesCreate: Partial<TComponentKeys<'MaintenanceSeriesCreate'>> = {
  ...MaintenanceEventOverview,
  description: generalTerm.description,
  recurrence: MaintenanceEventDetails.seriesRecurrence,
  overheadSeconds: MaintenanceEventDetails.overheadSeconds,
  taskTemplates: MaintenanceEventDetails.taskInstances,
  attachments: MaintenanceEventDetails.seriesAttachments,
};

const CalendarEventTag: TComponentKeys<'CalendarEventTag'> = {
  calendarEventTagId: 'Id',
  calendarEventType: MaintenanceEventOverview.calendarEventType,
  color: generalTerm.color,
  createdAtUtc: generalTerm.createdAt,
  createdByDisplayName: generalTerm.createdBy,
  createdByUserId: generalTerm.createdBy,
  deletedAtUtc: generalTerm.deletedAt,
  deletedBy: generalTerm.deletedBy,
  deletedByDisplayName: generalTerm.deletedBy,
  description: generalTerm.description,
  displayName: generalTerm.displayName,
  isDeleted: generalTerm.deleted,
};

const OperationEventOverview: TComponentKeys<'OperationEventOverview'> = {
  assetLocation: generalTerm.location,
  calendarEventId: 'id',
  createdByDisplayName: generalTerm.createdBy,
  createdById: generalTerm.createdBy,
  displayName: generalTerm.displayName,
  startAtUtc: generalTerm.jobStartedAtUtc,
  endAtUtc: generalTerm.jobFinishedAtUtc,
  assetId: generalTerm.asset,
  assetDisplayName: generalTerm.asset,
  calendarEventType: MaintenanceEventOverview.calendarEventType,
  calendarEventTag: 'Event tag',
};

const OperationEventDetails: TComponentKeys<'OperationEventDetails'> = {
  ...OperationEventOverview,
  description: generalTerm.description,
};

const MaintenanceTaskResultType: Record<TComponentValue<'MaintenanceTaskResultType'>, string> = {
  WORK_DONE: 'Hotovo',
  OMITTED: 'Vynechané',
};

const MaintenanceTaskReviewType: Record<TComponentValue<'MaintenanceTaskReviewType'>, string> = {
  APPROVED: 'Schválené',
  REJECTED: 'Zamietnuté',
};

const TaskInstance: Partial<TComponentKeys<'TaskInstance'>> = {
  description: generalTerm.description,
  durationSeconds: 'Trvanie',
  attachments: 'Prílohy',
  workResult: 'Stav',
  reviewResult: 'Hodnotenie',
};

const Role: Partial<TComponentKeys<'Role'>> = {
  displayName: generalTerm.displayName,
  description: generalTerm.description,
  isDefault: 'predvolená rola',
};

const RoleWithPermissions: Partial<TComponentKeys<'RoleWithPermissions'>> = {
  ...Role,
};

const SimulatorAMS275910CompoundLayerClass: Record<ESimulatorAMS275910CompoundLayerClass, string> = {
  [ESimulatorAMS275910CompoundLayerClass.CLASS_0]: 'Žiadna zložená vrstva',
  [ESimulatorAMS275910CompoundLayerClass.CLASS_1]: '0 - 13 µm',
  [ESimulatorAMS275910CompoundLayerClass.CLASS_2]: '13 - 25 µm',
};

const SimulatorAMS275910SteelClass: Record<ESimulatorAMS275910SteelClass, string> = {
  [ESimulatorAMS275910SteelClass.NITRALLOY_135M]: 'Nitralloy 135M',
  [ESimulatorAMS275910SteelClass.NITRALLOY_EZ]: 'Nitralloy EZ',
  [ESimulatorAMS275910SteelClass.NITRALLOY_N]: 'Nitralloy N',
  [ESimulatorAMS275910SteelClass._4140_4340]: '4140 / 4340',
  [ESimulatorAMS275910SteelClass.DC_D6AC]: 'DC / D6AC',
  [ESimulatorAMS275910SteelClass.H11]: 'H11',
  [ESimulatorAMS275910SteelClass.STAINLESS]: 'Nerezová',
  [ESimulatorAMS275910SteelClass.CARBON]: 'Uhlík',
};

const SimulatorAMS275912PorosityClass: Record<ESimulatorAMS275912PorosityClass, string> = {
  [ESimulatorAMS275912PorosityClass.CLASS_1]: 'Trieda 1',
  [ESimulatorAMS275912PorosityClass.CLASS_2]: 'Trieda 2',
};

const SimulatorAMS275912SteelClass: Record<ESimulatorAMS275912SteelClass, string> = {
  [ESimulatorAMS275912SteelClass.GROUP_0]: 'Skupina 0',
  [ESimulatorAMS275912SteelClass.GROUP_1]: 'Skupina 1',
  [ESimulatorAMS275912SteelClass.GROUP_2]: 'Skupina 2',
};

const SimulatorPreTreatmentType: Record<ESimulatorPreTreatmentType, string> = {
  [ESimulatorPreTreatmentType.NORMALIZED]: 'Normalizovaný',
  [ESimulatorPreTreatmentType.QUENCHED]: 'Kalený',
  [ESimulatorPreTreatmentType.TEMPERED]: 'Popustený',
};

const SimulatorSimulationPart: Partial<TComponentKeys<'SimulatorSimulationPart'>> = {
  setpointMinSurfaceHardness: 'Min. tvrdosť povrchu',
  setpointCaseDepth: 'Efektívna hĺbka povrchovej vrstvy',
  setpointCaseDepthHardness: generalTerm.requiredHardness,
  grainSize: generalTerm.grainSize,
  doesUseShape: 'Typ simulácie tvaru',
  shape: 'Všeobecný tvar dielu (DIN 17211)',
  quenchingMediaId: 'Kaliace médium',
  quenchingIntensity: generalTerm.quenchingIntensity,
  endTemperature: generalTerm.endTemperature,
  nitridingMinSurfaceHardness: 'Min. tvrdosť povrchu',
  nitridingHardnessDepth: generalTerm.nitridingDepth,
  nitridingHardness: 'Tvrdosť po nitridácii',
  nitridingSteelClass: 'Skupina ocele (AMS 2759-10)',
  nitridingCompoundLayerClass: 'Trieda zloženej vrstvy (AMS 2759-10)',
  nitrocarburizingPorosityClass: 'Pórovitosť (AMS 2759-12)',
  nitrocarburizingSteelClass: 'Skupina ocele (AMS 2759-12)',
};

const AssetStatusJobInfo: Partial<TComponentKeys<'AssetStatusJobInfo'>> = {
  jobDisplayName: generalTerm.jobDisplayName,
  treatmentSpecificationDisplayName: generalTerm.recipe,
  stageDisplayName: generalTerm.recipeStep,
  stageNo: 'Stage number',
};

const AssetStatusOverview: Partial<TComponentKeys<'AssetStatusOverview'>> = {
  activeAlarmCount: 'Aktívne alarmy',
  state: 'Stav',
};

const AssetState: Record<TComponentValue<'AssetState'>, string> = {
  RUNNING: 'Bežiaci',
  OFFLINE: 'Offline',
  IDLE: 'Nečinný',
  PLANNED_UNIT_DOWNTIME: CalendarEventType.PLANNED_UNIT_DOWNTIME,
  ACTUAL_UNIT_DOWNTIME: CalendarEventType.ACTUAL_UNIT_DOWNTIME,
  OPERATION_HOLD: CalendarEventType.OPERATION_HOLD,
};

const QmulusLicenseViolationReason: Record<TComponentValue<'QmulusLicenseViolationReason'>, string> = {
  NO_LICENSE_KEY: 'Žiadna licencia',
  ASSET_LIMIT_VIOLATED: 'Nakonfigurovaných príliš veľa aktív',
  EXPIRED: 'Platnosť licencie vypršala',
  EXPIRED_GRACE_PERIOD: 'Platnosť licencie vypršala',
  INVALID: 'Neplatná licencia',
  USER_LIMIT_VIOLATED: 'Vytvorených príliš veľa používateľov',
  OTHER: 'Porušenie licencie',
};

const AuditEnvelope: Partial<TComponentKeys<'AuditEnvelope'>> = {
  valueSensorMeanings: 'Senzory',
  durationMeanings: 'Čas. senzory',
  stabilizationCombinationType: 'Stabilizácia',
  stabilizationStagesCnt: 'Kroky receptu',
  stabilizationPeriodSeconds: 'Trvanie',
};

const EnvelopeThreshold: TComponentKeys<'EnvelopeThreshold_int_'> = {
  lowerOffset: 'Limit -',
  setpoint: 'Nastavená hodnota',
  upperOffset: 'Limit +',
};

const AuditEnvelopStabilizationCombinationType: Record<TComponentValue<'AuditEnvelopStabilizationCombinationType'>, string> = {
  0: 'Pridaj',
  1: 'Najkratší',
  2: 'Najdlší',
};

const ScaleType: Record<TComponentValue<'ScaleType'>, string> = {
  linear: 'Lineárny',
  logarithmic: 'Logaritmický',
};

const ChemicalElementInSteel: Record<TComponentValue<'ChemicalElementInSteel'>, string> = {
  Fe: 'Železe',
  C: 'Uhlík',
  Mn: 'Mangán',
  P: 'Fosfor',
  S: 'Síra',
  Si: 'Kremík',
  Cr: 'Chróm',
  V: 'Vanád',
  W: 'Volfrám',
  Mo: 'Molybdén',
  Co: 'Kobalt',
  Ni: 'Nikel',
  Cu: 'Meď',
  Al: 'Hliník',
  Ti: 'Titán',
  Nb: 'Niób',
  B: 'Bór',
  Pb: 'Olovo',
  Zr: 'Zirkón',
  Ta: 'Tantal',
  N: 'Dusík',
  Se: 'Selén',
};

const EquipmentPhysicalType: Record<TComponentValue<'EquipmentPhysicalType'>, string> = {
  1: 'Šachtová pec',
  2: 'Dierová retortová pec',
  10: 'Zvonová pec',
  20: 'Vodorovná pec',
  21: 'Vodorovná retortová pec',
  30: 'Skriňová pec',
  40: 'Viacnásobná komora pec',
  50: 'Rotačná pec',
  51: 'Prechodá pec',
  52: 'Prstencová pec',
  100: 'Pásová pec',
  110: 'Rotačná retortová pec',
  120: 'Pec Shaker Hearth',
  200: 'Sklad',
  201: 'Miesto nakládky',
  202: 'Miesto vykládky',
  203: 'Miesto nakládky/vykládky',
};
const DataSourceType: Record<TComponentValue<'DataSourceType'>, string> = {
  ...NonTranslatable.dataSourceName,
  production_statistics: AppId[EAppId.PRODUCTION_STATISTICS],
  model_builder: AppId[EAppId.MODEL_BUILDER],
  ml_model: `${AppId[EAppId.MODEL_BUILDER]} (Legacy)`,
  generic: 'Všeobecný zdroj dát / Node-RED',
};

const NpmSystemIdentifier: TComponentKeys<'NpmSystemIdentifier'> = {
  customerCode: 'customerCode',
  location: 'location',
  systemId: 'systemId',
  subSystemId: 'subSystemId',
};

const PtDatabaseIdentifier: TComponentKeys<'PtDatabaseIdentifier'> = {
  type: 'Typ',
  ptDbId: 'Numer PE',
};

const WorkplaceIdentifier: TComponentKeys<'WorkplaceIdentifier'> = {
  ...PtDatabaseIdentifier,
  workplaceId: 'workplaceId',
  chamberId: 'chamberId',
};

const AutomatIdentifier: TComponentKeys<'AutomatIdentifier'> = {
  ...WorkplaceIdentifier,
  automatNo: 'automatNo',
};

const AssetParameter: Partial<TComponentKeys<'AssetParameter'>> = {
  namespace: 'Menný priestor',
  category: 'Kategória',
  parameterName: 'Parameter',
  sourceAssetId: 'Zdroj',
  value: 'Hodnota',
};

const AssetParameterDefinition: Partial<TComponentKeys<'AssetParameterDefinition'>> = {
  namespace: AssetParameter.namespace,
  category: AssetParameter.category,
  parameterName: AssetParameter.parameterName,
  physicalDimensionId: Meaning.physicalDimensionId,
  description: generalTerm.description,
  defaultValue: 'Predvolená hodnota',
  allowedMin: 'Minimálna hodnota',
  allowedMax: 'Maximálna hodnota',
};

const ExpressionType: Record<Exclude<TValidateExpressionRouteResponse['expression']['type'], undefined> | typeof sinkExpression | typeof sinkVariable, string> = {
  ExpressionConstant: 'Konštanta',
  ExpressionOperator: 'Operátor',
  ExpressionUnaryOperator: 'Operátor',
  ExpressionDataIdentifier: 'Signál',
  ExpressionMeaningIdentifier: 'Význam',
  ExpressionFunction: 'ExpressionFunction',
  ExpressionGenerativeFunction: 'Generatívne funkcie',
  ExpressionVariableIdentifier: 'Premenná',
  ExpressionAssetParameterIdentifier: 'Parameter aktíva',
  SinkExpression: 'Výraz',
  SinkVariable: 'Definícia premennej',
};

const ExpressionNamedConstant: Record<TComponentValue<'ExpressionNamedConstant'>, string> = {
  pi: 'Pi π',
  e: 'Eulerovo číslo',
  inf: 'Nekonečno',
  nan: 'Nie je číslo',
  true: 'PRAVDA',
  false: 'NEPRAVDA',
  null: 'null',
  millisecond: 'Milisekunda',
  minute: 'Minúta',
  hour: 'Hodina',
};

const ExpressionOperatorType: Record<TComponentValue<'ExpressionOperatorType'>, string> = {
  '**': 'umocňovanie',
  '*': 'násobenie',
  '//': 'celočíselné delenie',
  '/': 'delenie',
  '%': 'modulo',
  '+': 'sčítanie',
  '-': 'odčítanie',
  '==': 'rovný',
  '!=': 'nerovný',
  '<': 'menej ako',
  '<=': 'menej alebo rovný',
  '>': 'viac ako',
  '>=': 'viac alebo rovný',
  '^': 'bitové XOR',
  '&&': 'logické AND',
  '||': 'logické ALEBO',
};

const ExpressionUnaryOperatorType: Record<TComponentValue<'ExpressionUnaryOperatorType'>, string> = {
  '!': 'logické NIE',
  '-': 'negácia',
  '+': 'identita',
};

const ExpressionFunctionTypeAggregation: Record<TComponentValue<'ExpressionFunctionTypeAggregation'>, string> = {
  min: 'Minimum',
  max: 'Maximálny',
  range: 'Rozsah',
  sum: 'Súčet',
  mean: 'Priemer',
  avg: 'Priemerny',
  std: 'Smerodajná odchýlka',
  var: 'Rozptyl',
  median: 'Medián',
};

const QmulusDashboardWidget: Record<keyof TQmulusDashboardWidgetSharedProps, string> = {
  displayName: generalTerm.displayName,
  widgetType: 'Typ',
  size: 'Veľkosť',
};

const QmulusDashboardWidgetType: Record<TQmulusDashboardWidgetType, string> = {
  anomaly_detection_observations: `${AppId[EAppId.ANOMALY_DETECTION]}: Pozorovania`,
  camera: 'Kamera',
  gauge: 'Miernik',
  job_features: DataFormat.job_aggregate,
  timeseries: DataFormat.time_series,
};

const QmulusDashboardWidgetObservations: Pick<TComponentKeys<'QmulusDashboardWidgetObservations'>, 'variant'> = {
  variant: 'Variant',
};

const QmulusDashboardWidgetObservationsVariant: Record<components['schemas']['QmulusDashboardWidgetObservations']['variant'], string> = {
  details: 'Podrobnosti',
  frequency: 'Frekvencia',
};

const QmulusDashboardMixinData: Pick<TComponentKeys<'QmulusDashboardWidgetGauge'>, 'assetId' | 'dataFormat' | 'data' | 'lookback'> = {
  assetId: generalTerm.assetId,
  dataFormat: Metadata.dataFormat!,
  data: 'Dáta',
  lookback: 'Časový rozsah',
};

const QmulusDashboardMixinLimits: Pick<TComponentKeys<'QmulusDashboardWidgetGauge'>, 'limitMin' | 'limitMax'> = {
  limitMin: 'Minimum',
  limitMax: 'Maximálny',
};

const QmulusDashboardWidgetGauge: Partial<TComponentKeys<'QmulusDashboardWidgetGauge'>> = {
  ...QmulusDashboardWidget,
  ...QmulusDashboardMixinData,
  ...QmulusDashboardMixinLimits,
  appearance: 'Vzhľad',
  thresholds: 'Prahy',
};

const QmulusDashboardWidgetGaugeAppearance: Record<TComponentValue<'QmulusDashboardWidgetGauge'>['appearance'], string> = {
  180: 'Oblúk - 180°',
  270: 'Oblúk - 270°',
  horizontal: 'Lineárne – Vodorovne',
  vertical: 'Lineárny – Zvisle',
};

const QmulusDashboardNamedLimit: Record<TComponentValue<'QmulusDashboardNamedLimit'>, string> = {
  standard_deviation: 'Średnia + b * odchylenie standardowe',
  lower_whisker: 'Q1 - 1,5 * IQR',
  upper_whisker: 'Q3 + 1,5 * IQR',
  q1: 'Kwartyl 1',
  q3: 'Kwartyl 3',
};

const QmulusDashboardThreshold: TComponentKeys<'QmulusDashboardThreshold'> = {
  value: 'Hodnota',
  color: generalTerm.color,
};

const QmulusDashboard: Partial<TComponentKeys<'QmulusDashboard'>> = {
  displayName: generalTerm.displayName,
};

const SimulatorRecipeSetpointType: Record<TComponentValue<'SimulatorRecipeSetpoint'>['simulatorSetpointMeaningId'], string> = {
  RUN_TIME: 'Čas behu',
  TEMPERATURE: 'Teplota',
  CARBURIZING_DEPTH_RELATIVE: 'Hĺbka napúhlenia (relatívna)',
  CARBURIZING_DEPTH_ABSOLUTE: 'Hĺbka napúhlenia (absolútna)',
  CARBON_POTENTIAL_CP: 'Uhlíkový potenciál CP',
  SURFACE_CARBON: PartMaterialProperties.surfaceCarbon,
  AUTO_BOOST_C: 'Auto C (100-x)',
  SOOT_LIMIT: 'Medzná hodnota sadí',
  NITROGEN_POTENTIAL_NP: 'Dusíkový potenciál NP',
  NITRIDING_DEPTH_RELATIVE: 'Hĺbka nitridácie (relatívna)',
  NITRIDING_DEPTH_ABSOLUTE: 'Hĺbka nitridácie (absolútna)',
  NITRIDING_POTENTIAL_KN: 'Nitridačný potenciál kN',
  SURFACE_NITROGEN: 'Povrchový dusík',
  AUTO_BOOST_N: 'Auto N (100-x)',
  CARBURIZING_POTENTIAL_KC_B: 'Napúhleni potenciál kC (Bodouard)',
  CARBURIZING_POTENTIAL_KC_W: 'Napúhleni potenciál kC(H₂O)',
  TOTAL_GAS_FLOW: 'Objemový prietok (konštantný)',
  TOTAL_GAS_FLOW_AUTO: 'Objemový prietok (auto-pulz)',
  CO_CONTENT: generalTerm.carbonMonoxide,
  H2_CONTENT: generalTerm.hydrogen,
  N2_CONTENT: 'Dusík N₂',
  NH3_CONTENT: 'Amoniak NH₃',
  COX_CONTENT: 'COₓ',
  C2H2_CONTENT: 'Acetylén C₂H₂',
  C3H8_CONTENT: 'Propán C₃H₈',
  WAIT_RELATIVE: 'Čakaj',
};

const SimulatorRecipeStep: TComponentKeys<'SimulatorRecipeStepMutation'> = {
  stageId: generalTerm.recipeStage,
  displayName: generalTerm.displayName,
  setpoints: generalTerm.setpoint,
};

const SimulatorRecipeSetpoint: TComponentKeys<'SimulatorRecipeSetpoint'> = {
  simulatorSetpointMeaningId: generalTerm.setpoint,
  unitOfMeasurementId: generalTerm.unit,
  control: 'Riadenie nastavenou hodnotou',
  endControl: 'Koncová podmienka',
};

const PT9800RecipeSimulationInput: TComponentKeys<'PT9800RecipeSimulationInput'> = {
  recipe: generalTerm.recipe,
  recipeMetadata: '',
  intervalSeconds: '',
  dataSourceId: generalTerm.dataSourceId,
  startTemperature: 'Počiatočná teplota',
  heatingGradient: 'Gradient ohrevu',
  coolingGradient: 'Gradient chladenia',
  assetFactor: 'Faktor pece',
  coContent: 'CO',
  h2Content: 'H2',
};

const CCTPhaseBoundaryType: Record<TComponentValue<'CCTBoundaryType'>, string> = {
  PEARLITE: 'Perlit',
  FERRITE: 'Ferit',
  BAINITE: 'Bainit',
  CARBIDE: 'Karbid',
};

const JobConsumption: TComponentKeys<'JobConsumption'> = {
  dataId: 'Spotrebný materiál',
  meaningId: 'Význam',
  value: 'Množstvo',
  unitOfMeasureId: generalTerm.unit,
};

const JobValueComparison: TComponentKeys<'JobValueComparison'> = {
  dataId: DataFormat.job_aggregate,
  jobId: generalTerm.job,
  value: 'Hodnota',
  comparison: 'Porovnanie',
};

const JobValueComparisonPeriod: TComponentKeys<'JobValueComparisonPeriod'> = {
  previous30D: 'Porovnanie s predáchadzajúcimi 30 dňami',
  previous90D: 'Porovnanie s predáchadzajúcimi 90 dňami',
  previousYear: 'Porovnanie s predáchadzajúcim rokom',
};

const JobValueBaselineMetrics: TComponentKeys<'JobValueBaselineMetrics'> = {
  isOutlier: 'Štatistická odľahlá hodnota',
  delta: 'Absolútna zmena',
  deltaPercent: 'Relatívna zmena',
  standardDeviation: 'Smerodajná odchýlka',
  baseline: 'Priemer za obdobie',
  earliestDateUtc: 'Začiatok obdobia',
  latestDateUtc: 'Koniec obdobia',
};

const AuditTrailSettings: TComponentKeys<'AuditTrailSettings'> = {
  isEnabled: 'Zapnuté',
  retentionPeriodDays: 'Obdobie uchovávania',
};

const AuditTrailEvent: TComponentKeys<'AuditTrailEvent'> = {
  timestampUtc: 'Časová pečiatka',
  userId: 'Používateľ',
  userDisplayName: 'Používateľ',
  endpointMethod: 'Metóda',
  endpoint: 'Endpoint',
  params: 'Parametre',
  appKey: 'API',
  returnStatus: 'Stav',
};

const AuditSpecificationResultDetail: Partial<TComponentKeys<'AuditSpecificationResultDetail'>> = {
  auditorRemark: 'Poznámky auditóra',
};

const Order: TComponentKeys<'Order'> = {
  orderId: 'Objednávka',
  createdAtUtc: generalTerm.createdAt,
  createdBy: generalTerm.createdBy,
  customerDisplayName: Customer.displayName,
  customerId: Customer.customerId,
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  partDisplayName: Part.displayName,
  partId: Part.partId,
  revisedAtUtc: generalTerm.lastModifiedAt,
  revisedBy: generalTerm.lastModifiedBy,
  revisionId: generalTerm.revision,
  partRevisionId: 'Revízia dielu',
  attributes: 'Atribúty objednávky',
  status: 'Stav',
  target: 'Cieľ',
};

const OperationOrder: Partial<TComponentKeys<'OperationOrder'>> = {
  displayName: Order.orderId,
  customerId: generalTerm.customer,
};

const PartInstance: TComponentKeys<'PartInstance'> = {
  partInstanceId: Part.displayName,
  displayName: Part.displayName,
  partId: Part.partId,
  jobId: Job.jobId,
  jobDisplayName: Job.displayName,
  orderId: Order.orderId,
  orderDisplayName: Order.orderId,
  partCount: 'Množstvo',
  partRevisionId: Part.partRevisionId,
};

const OrderAttribute: TComponentKeys<'OrderAttribute'> = {
  createdAtUtc: generalTerm.createdAt,
  createdBy: generalTerm.createdBy,
  displayName: generalTerm.displayName,
  isBuiltIn: 'Vstavaný',
  isRequired: 'Povinný',
  isVisible: Metadata.isHidden!,
  orderAttributeId: 'ID atribútu',
  physicalDimension: Meaning.physicalDimensionId!,
  revisedAtUtc: generalTerm.lastModifiedAt,
  revisedBy: generalTerm.lastModifiedBy,
  revisionId: generalTerm.revision,
  unitOfMeasureId: generalTerm.unit,
  valueType: 'Typ',
};

const OrderAttributeNumber: TComponentKeys<'OrderAttributeNumber'> = {
  ...OrderAttribute,
  minValue: 'Minimálna hodnota',
  maxValue: 'Maximálna hodnota',
  isMaxExclusive: 'Exkluzívne maximum',
  isMinExclusive: 'Exkluzívne minimum',
};

const OrderAttributeString: TComponentKeys<'OrderAttributeString'> = {
  ...OrderAttribute,
  minLength: 'Minimálna dĺžka',
  maxLength: 'Maximálna dĺžka',
  pattern: 'Vzor',
};

const OrderAttributeValueType: Record<NonNullable<TComponentValue<'OrderAttribute'>['valueType']>, string> = {
  'string': OpenAPIPrimitiveType.string,
  'integer': OpenAPIPrimitiveType.integer,
  'number': OpenAPIPrimitiveType.number,
  'boolean': OpenAPIPrimitiveType.boolean,
  'date-time': 'Časová pečiatka',
};

const BuiltInOrderAttribute: Record<TComponentValue<'BuiltInOrderAttribute'>, string> = {
  DUE_DATE: 'Termín',
  PRIORITY: 'Priorita',
  REMARK: 'Poznámka',
  START_NOT_BEFORE: 'Najskorší povolený začiatok',
  WORK_ORDER_NUMBER: 'Číslo pracovného príkazu',
};

const OperationCalendarStateStart: TComponentKeys<'OperationCalendarStateStart'> = {
  displayName: generalTerm.displayName,
  description: generalTerm.description,
  calendarEventType: OperationEventDetails.calendarEventType,
  startAtUtc: OperationEventDetails.startAtUtc,
  calendarEventTagId: CalendarEventTag.displayName,
};

const OperationCalendarStateFinish: TComponentKeys<'OperationCalendarStateFinish'> = {
  displayName: generalTerm.displayName,
  description: generalTerm.description,
  finishedAtUtc: OperationEventDetails.endAtUtc,
};

const ScaleOptions: Record<EScaleOption, string> = {
  [EScaleOption.FIT_SCREEN]: 'Prispôsobiť obrazovke',
  [EScaleOption.FULL_SIZE]: '100%',
};

export default {
  AlarmCollection,
  AlarmDefinition,
  AlarmEvent,
  AlarmEventSeverity,
  AlarmSeverity,
  AlarmStatistics,
  AppGroupCommercial: NonTranslatable.AppGroupCommercial,
  AppGroupSemanticDepartment,
  AppGroupSemanticTopic,
  AppId,
  AssessmentTestResultType,
  Asset,
  AssetBlobIndex,
  AssetCreate,
  AssetHierarchyLevel,
  Weekday,
  WeekdayShort,
  AssetParameter,
  AssetParameterDefinition,
  AssetProfileCombinator,
  AssetProfileStatus,
  AssetState,
  AssetStatusJobInfo,
  AssetStatusOverview,
  AssetUtilization,
  AuditEnvelopStabilizationCombinationType,
  AuditEnvelope,
  AuditEnvelopeNonPassedReasons,
  AuditResultType,
  AuditSpecification,
  AuditSpecificationResultDetail,
  AuditSpecificationResultOverview,
  AuditTrailEvent,
  AuditTrailSettings,
  AutomatIdentifier,
  BlobMeaning,
  BuiltInOrderAttribute,
  CCTPhaseBoundaryType,
  CalendarEventType,
  CameraRegistryEntry,
  CameraRegistryEntryCameraType,
  CarburizingRecipeSimulationValuesType,
  CarburizingSimulationPartShape,
  CarburizingSimulationPartShapeSize,
  CarburizingSimulatorRecipeType,
  Chamber,
  ChamberCreate,
  ChamberType,
  ChartAssessmentTest,
  ChartAssessmentTestRequirementType,
  ChemicalElementInSteel,
  CompanyCreate,
  CounterAction,
  CsvDatetimeFormat,
  CsvLineTerminator,
  CsvQuoteCharacter,
  CsvQuoteStyle,
  CsvQuoteStyleHint,
  CsvSeperator,
  Customer,
  DataFormat,
  DataSource,
  DataSourceAssociation,
  DataSourceType,
  EnvelopeThreshold,
  EquipmentCreate,
  EquipmentPhysicalType,
  EquipmentProfile,
  EquipmentProfileMeaning,
  EquipmentTreeNavigation,
  ExpressionFunctionTypeAggregation,
  ExpressionNamedConstant,
  ExpressionOperatorType,
  ExpressionType,
  ExpressionUnaryOperatorType,
  FileAssessmentTest,
  HandlingState,
  HeatTreatmentCapability,
  HeatTreatmentProcessType,
  Job,
  JobStage,
  JobStatus,
  JobValueBaselineMetrics,
  JobConsumption,
  JobValueComparison,
  JobValueComparisonPeriod,
  JobValueData,
  ShiftHoursWindow,
  LocationShift,
  LocationCreate,
  LogFileState,
  MaintenanceEventDetails,
  MaintenanceEventOverview,
  MaintenanceEventType,
  MaintenancePeriod,
  MaintenancePriority,
  MaintenanceRecurrenceEndCondition,
  MaintenanceRecurrenceMonthDayDimension,
  MaintenanceRecurrenceMonthDayValue,
  MaintenanceSeriesCreate,
  MaintenanceState,
  MaintenanceStateType,
  MaintenanceTaskResultType,
  MaintenanceTaskReviewType,
  Material,
  MaterialCalculationModelElementCoefficient,
  MaterialCalculationModelOutput,
  MaterialGroup,
  MaterialNamingSystem,
  MaterialPropertySpectrum,
  Meaning,
  Metadata,
  MetadataUpdate,
  MetadataWithChartConfigInput,
  ModelBuilderModelOverview,
  ModelBuilderModelType,
  ModelBuilderTrainingRequest,
  ModelLifecycleState,
  NotificationChannelType,
  NotificationTable,
  NpmSystemIdentifier,
  Observation,
  ObservationType,
  ObservationTypeHeader,
  OpenAPIPrimitiveType,
  OperationCalendarStateFinish,
  OperationCalendarStateStart,
  CalendarEventTag,
  OperationEventDetails,
  OperationEventOverview,
  Order,
  OrderAttribute,
  OrderAttributeNumber,
  OrderAttributeString,
  OrderAttributeValueType,
  OrderStatus,
  PT9800RecipeSimulationInput,
  Part,
  PartAssetRecipeQualification,
  PartAttribute,
  PartDetails,
  PartInstance,
  OperationOrder,
  PartMaterialProperties,
  PhysicalDimension,
  ProductionStatistics,
  PtDatabaseIdentifier,
  QmulusDashboard,
  QmulusDashboardMixinData,
  QmulusDashboardMixinLimits,
  QmulusDashboardNamedLimit,
  QmulusDashboardThreshold,
  QmulusDashboardWidget,
  QmulusDashboardWidgetGauge,
  QmulusDashboardWidgetGaugeAppearance,
  QmulusDashboardWidgetObservations,
  QmulusDashboardWidgetObservationsVariant,
  QmulusDashboardWidgetType,
  QmulusDriver,
  QmulusLicenseViolationReason,
  QualityPreset,
  QuenchingMedia,
  ReportBlockAlarmsTemplateVariable,
  ReportBlockAuditEnvelopesTemplateVariable,
  ReportBlockAuditReportHeaderTemplateVariable,
  ReportBlockJobDetailsHeaderTemplateVariable,
  ReportBlockId,
  ReportBlockJobPartsField,
  ReportBlockJobPartsTemplateVariable,
  ReportBlockJobStagesTemplateVariable,
  ReportBlockQualityReportHeaderTemplateVariable,
  ReportBlockQualitySamplesTemplateVariable,
  ReportBlockRecipeTemplateVariable,
  ReportBlockSignaturesTemplateVariable,
  ReportComponentAuditEnvelopeParameter,
  ReportComponentAuditEnvelopeSectionsParameter,
  ReportComponentAuditResultParameter,
  ReportComponentFormatQuantityParameter,
  ReportComponentFormatValueParameter,
  ReportComponentIdType,
  ReportComponentIdTypeDescription,
  ReportComponentKeyValueGridParameter,
  ReportComponentKeyValuePairParameter,
  ReportComponentProthermBatchRecipeParameter,
  ReportComponentProthermBeltRecipeParameter,
  ReportComponentQualityChartTestParameter,
  ReportComponentQualityFileTestParameter,
  ReportComponentQualityImageTestParameter,
  ReportComponentQualitySampleParameter,
  ReportComponentQualityTableTestParameter,
  ReportComponentQualityTestParameter,
  ReportComponentQualityTestResultParameter,
  ReportComponentTableParameter,
  ReportComponentWarningParameter,
  ReportOverview,
  ReportScopeType,
  ReportTemplateIdType,
  ReportTemplateKind,
  ReportTemplateFooter,
  ReportTemplateHeader,
  ReportTemplateUpsert,
  Role,
  RoleWithPermissions,
  RootCause,
  RootCauseGroup,
  Sample,
  ScaleType,
  ScaleOptions,
  SimulationStep,
  SimulatorAMS275910CompoundLayerClass,
  SimulatorAMS275910SteelClass,
  SimulatorAMS275912PorosityClass,
  SimulatorAMS275912SteelClass,
  SimulatorPreTreatmentType,
  SimulatorRecipe,
  SimulatorRecipeSetpoint,
  SimulatorRecipeSetpointType,
  SimulatorRecipeStep,
  SimulatorSimulationPart,
  SparePart,
  SubscriptionTreeNode,
  TableAssessmentTest,
  TableAssessmentTestItem,
  TableTestData,
  TaskInstance,
  TimeBucketType,
  TimeBucket_AssetUtilization_,
  UnitOfMeasurementDisplayName,
  UnitOfMeasurementSymbol,
  User,
  WorkplaceIdentifier,
  expressionFunctionCategories,
  expressionFunctionsExplanations,

  util: {
    timeBucketName: 'Okres czasu',
  },

  meaningId: {
    ...TimeseriesMeaning,
    ...ConsumptionMeaning,
    ...JobFeatureMeaning,
    ...PhysicalPropertyMeaning,
  },
  npm: npmApiTranslations,
  pt9800: pt9800ApiTranslations,
};
